import { useState, useEffect, useCallback, useRef, createElement, isValidElement } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import "./UserProfile.css";
import { requestSessionExpiry, useAuth } from "../../AuthProvider/AuthContext.jsx";
import {
  AlertTriangle, Armchair, Backpack, BadgeCheck, Bell, CalendarDays,
  Download, FileText, Heart, IdCard, LogOut, Luggage, MapPinned,
  MessageCircle, Plane, Settings, Ticket, User, Users, Camera,
  TrendingUp, CheckCircle2, Clock, Wallet, ChevronRight, ChevronDown,
  ShieldCheck, Lightbulb, X, Train, CreditCard, ArrowDownLeft,
  ArrowUpRight, Star, LayoutDashboard, Crown, Headphones, Phone,
  MessageSquare, Zap, Globe, Award,
  Moon, Sun, PanelLeftClose, PanelLeftOpen, Search, Menu, FileCheck,
  Eye, Printer, Mail, Utensils,
} from "lucide-react";
import { SeatMap, normalizeSeatMap } from "../Features/Flights/Flights";
import { initiateHotelEasebuzzPayment, verifyHotelEasebuzzPayment } from "../../services/hotelSearchApi";
import { ENABLE_TRAINS } from "../../config/features";
/* ─────────────────────────────────────────────────────────────
   API CONFIG
───────────────────────────────────────────────────────────── */
const API_BASE =
  (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";
const authHeaders = (t) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${t || localStorage.getItem("mbt_token") || ""}`,
});
const API_ORIGIN = API_BASE.replace(/\/api$/, "");
const formatPrice = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });

function resolveAvatarUrl(avatar) {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  return `${API_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
}

/* ─────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────── */
const formatMoneyAmount = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
};

const normalizeHotelPaymentDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const raw = value.trim();
    const isoDate = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDate) return isoDate[1];
  }
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const parseHoldExpiryTime = (booking = {}) => {
  const raw = booking.hold_expiry_at || booking.holdExpiryAt || booking.hold_expiry || booking.holdExpiry;
  if (!raw) return null;
  const time = new Date(raw).getTime();
  return Number.isFinite(time) ? time : null;
};

const formatHoldRemaining = (expiryTime) => {
  if (!expiryTime) return "";
  const remaining = Math.max(0, expiryTime - Date.now());
  if (remaining <= 0) return "Hold Expired";
  const totalSeconds = Math.floor(remaining / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

function safeJsonParse(value, fallback) {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return fallback; }
}

const parseHotelJson = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];

  try {
    const parsed =
      typeof value === "string"
        ? JSON.parse(value)
        : value;

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const TITLE_LABELS = {
  MR: "Mr",
  MRS: "Mrs",
  MS: "Ms",
  MSTR: "Master",
  MISS: "Miss",
};

function formatPassengerName(p) {
  const rawTitle = String(p?.title || "").trim();
  const displayTitle =
    TITLE_LABELS[rawTitle.toUpperCase()] || rawTitle;

  const parts = [
    displayTitle,
    p?.firstName || p?.first_name,
    p?.lastName || p?.last_name,
  ].filter(Boolean);

  return parts.join(" ").trim() || "Passenger";
}

const AIRLINE_NAME_TO_CODE = {
  "indigo": "6E", "spicejet": "SG", "air india": "AI",
  "air india express": "IX", "vistara": "UK", "akasa air": "QP",
};

function deriveAirlineCode(flightNumbers, airlineName) {
  const first = String(flightNumbers || "").split(",")[0].trim();
  const m = first.match(/^([A-Z0-9]{2,3})\s?\d+/i);
  if (m) return m[1].toUpperCase();
  return AIRLINE_NAME_TO_CODE[String(airlineName || "").trim().toLowerCase()] || null;
}

const airlineLogoUrl = (code) =>
  code ? `https://images.kiwi.com/airlines/64/${code}.png` : null;

const cityLabel = (code, cache) => {
  const rec = cache[code];
  return rec?.city ? `${rec.city} (${code})` : (code || "—");
};
const isEmpty = (v) => !v || v === "—" || String(v).trim() === "";

function canCancelBooking(booking) {
  if (!booking) return false;
  return !["cancelled", "completed", "released", "cancel_initiated"].includes(String(booking.status || "").toLowerCase());
}

const passengerTypeLabel = (value) => {
  const labels = { 0: "Adult", 1: "Child", 2: "Infant" };
  return labels[Number(value)] || "Passenger";
};

const durationUnitLabel = (value) => Number(value) === 0 ? "hour" : "day";

const formatChargeValue = (rule) => {
  const value = String(rule?.Value ?? rule?.value ?? "").trim();
  if (!value) return "As per airline";
  return Number(rule?.ValueType ?? rule?.valueType) === 1 ? `${value}%` : `₹${formatPrice(value)}`;
};

const formatServiceFee = (value) => {
  const amount = Number(value || 0);
  return amount > 0 ? `₹${formatPrice(amount)}` : "None";
};

const normalizeChargeRules = (value) => {
  const parsed = safeJsonParse(value, []);
  const list = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
  return list.filter((rule) => rule && typeof rule === "object");
};
const applicabilityLabel = (rule) =>
  Number(rule?.Applicablility ?? rule?.applicablility) === 1 ? "Per Passenger" : "Per Segment";

const returnFlightLabel = (rule) =>
  (rule?.Return_Flight ?? rule?.return_flight) ? "Return flight" : "Onward flight";
const chargeWindowLabel = (rule) => {
  const from = rule.DurationFrom ?? rule.durationFrom;
  const to = rule.DurationTo ?? rule.durationTo;
  const fromUnit = durationUnitLabel(rule.DurationTypeFrom ?? rule.durationTypeFrom);
  const toUnit = durationUnitLabel(rule.DurationTypeTo ?? rule.durationTypeTo);
  if (from === undefined && to === undefined) return "Any time";
  if (from !== undefined && to !== undefined) return `${from} ${fromUnit}${Number(from) === 1 ? "" : "s"} to ${to} ${toUnit}${Number(to) === 1 ? "" : "s"}`;
  if (from !== undefined) return `From ${from} ${fromUnit}${Number(from) === 1 ? "" : "s"}`;
  return `Up to ${to} ${toUnit}${Number(to) === 1 ? "" : "s"}`;
};

const renderIconNode = (icon, props = {}) => {
  if (!icon) return null;
  if (isValidElement(icon)) return icon;
  if (typeof icon === "function" || typeof icon === "object") return createElement(icon, props);
  return icon;
};

const toDateInputValue = (d) => {
  if (!d) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(d))) return String(d);
  const parsed = new Date(d);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
};

const STATUS_CONFIG = {
  upcoming: { label: "Upcoming", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  completed: { label: "Completed", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  cancelled: { label: "Cancelled", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  partially_cancelled: {
    label: "Partially Cancelled",
    color: "#92400e",
    bg: "#fffbeb",
    border: "#fde68a",
  },
  draft: { label: "Draft", color: "#7c3aed", bg: "#faf5ff", border: "#ddd6fe" },
  in_progress: { label: "In Progress", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  submitted: { label: "Submitted", color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
  approved: { label: "Approved", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  rejected: { label: "Rejected", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  pending_payment: { label: "Pay Now", color: "#7c3aed", bg: "#faf5ff", border: "#ddd6fe" },
  on_hold: { label: "On Hold", color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  released: { label: "Released", color: "#64748b", bg: "#f8fafc", border: "#cbd5e1" },
  cancel_initiated: { label: "Cancellation Requested", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  held: { label: "Held · payment due", color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  pending_ticket: { label: "Issuing Ticket…", color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
};

/* ─────────────────────────────────────────────────────────────
   API FUNCTIONS
───────────────────────────────────────────────────────────── */
const hasAuthHeader = (headers = {}) =>
  Boolean(headers.Authorization || headers.authorization);

async function apiFetch(url, options = {}) {
  const res = await fetch(url, options);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (
      hasAuthHeader(options.headers) &&
      (res.status === 401 || /invalid token/i.test(String(body.error || body.errorDesc || body.message || "")))
    ) {
      requestSessionExpiry();
    }
    const message = [
      `HTTP ${res.status}`,
      body.code,
      body.error || body.errorDesc || body.message,
    ].filter(Boolean).join(" · ");
    const err = new Error(message || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.json();
}

const buildInactiveHoldMessage = (hold = {}) => {
  const legIndex = Number(hold.legIndex);
  const legPrefix = Number.isFinite(legIndex) ? `Flight ${legIndex + 1}: ` : "";
  return `${legPrefix}This airline hold has expired or is no longer active. Please search again to continue.`;
};

async function verifyHeldFlightPaymentEligibility(bookingId, token) {
  const res = await fetch(`${API_BASE}/flight-ticketing/hold-status`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ bookingId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.eligible !== true) {
    const err = new Error(buildInactiveHoldMessage(body));
    err.code = "HELD_HOLD_INACTIVE";
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const fetchUser = (id, t) => apiFetch(`${API_BASE}/users/${id}`, { headers: authHeaders(t) });
const patchUser = (id, d, t) => apiFetch(`${API_BASE}/users/${id}`, { method: "PATCH", headers: authHeaders(t), body: JSON.stringify(d) });
const fetchBookings = (uid, t) => apiFetch(`${API_BASE}/bookings?userId=${uid}`, { headers: authHeaders(t) });
const deleteBooking = (id, t) => apiFetch(`${API_BASE}/bookings/${id}`, { method: "DELETE", headers: authHeaders(t) });
const patchBookingStatus = (id, s, t) => apiFetch(`${API_BASE}/bookings/${id}`, { method: "PATCH", headers: authHeaders(t), body: JSON.stringify({ status: s }) });
const fetchHotelBookings = (uid, t) => apiFetch(`${API_BASE}/hotel-bookings?userId=${uid}`, { headers: authHeaders(t) });
const deleteHotelBooking = (id, t) => apiFetch(`${API_BASE}/hotel-bookings/${id}`, { method: "DELETE", headers: authHeaders(t) });
const patchHotelBookingStatus = (id, s, t) => apiFetch(`${API_BASE}/hotel-bookings/${id}`, { method: "PATCH", headers: authHeaders(t), body: JSON.stringify({ status: s }) });
const fetchVisa = (uid, t) => apiFetch(`${API_BASE}/visa-applications?userId=${uid}`, { headers: authHeaders(t) });
const deleteVisa = (id, t) => apiFetch(`${API_BASE}/visa-applications/${id}`, { method: "DELETE", headers: authHeaders(t) });
const fetchWishlist = (uid, t) => apiFetch(`${API_BASE}/wishlist?userId=${uid}`, { headers: authHeaders(t) });
const deleteWishlistItem = (id, t) => apiFetch(`${API_BASE}/wishlist/${id}`, { method: "DELETE", headers: authHeaders(t) });
const fetchFlightBookings = (uid, t) =>
  apiFetch(`${API_BASE}/flight-bookings?userId=${uid}`, {
    headers: authHeaders(t),
    cache: "no-store",
  });
const lookupAirports = (codes, t) =>
  apiFetch(`${API_BASE}/flight-search/airports/lookup`, {
    method: "POST",
    headers: authHeaders(t),
    body: JSON.stringify({ codes }),
  });
const deleteFlightBooking = (id, t) => apiFetch(`${API_BASE}/flight-bookings/${id}`, { method: "DELETE", headers: authHeaders(t) });
const patchFlightBookingStatus = (id, s, t) => apiFetch(`${API_BASE}/flight-bookings/${id}`, { method: "PATCH", headers: authHeaders(t), body: JSON.stringify({ status: s }) });
const fetchHolidayBookingsForUser = (uid, t) => apiFetch(`${API_BASE}/holiday-bookings?userId=${uid}`, { headers: authHeaders(t) });
const deleteHolidayBooking = (id, t) => apiFetch(`${API_BASE}/holiday-bookings/${id}`, { method: "DELETE", headers: authHeaders(t) });
const patchHolidayBookingStatus = (id, s, t) => apiFetch(`${API_BASE}/holiday-bookings/${id}`, { method: "PATCH", headers: authHeaders(t), body: JSON.stringify({ status: s }) });
const fetchBusBookings = (uid, t) => apiFetch(`${API_BASE}/bus-bookings?userId=${uid}`, { headers: authHeaders(t) });
const fetchBusCancellationHistory = (t) =>
  apiFetch(
    `${API_BASE}/bus-bookings/cancellations/history`,
    { headers: authHeaders(t) }
  );
const deleteBusBooking = (id, t) => apiFetch(`${API_BASE}/bus-bookings/${id}`, { method: "DELETE", headers: authHeaders(t) });
const patchBusBookingStatus = (id, s, t) => apiFetch(`${API_BASE}/bus-bookings/${id}`, { method: "PATCH", headers: authHeaders(t), body: JSON.stringify({ status: s }) });
const fetchBusCancellationData = (id, t) => apiFetch(`${API_BASE}/bus-bookings/${id}/cancellation-data`, { method: "POST", headers: authHeaders(t), body: JSON.stringify({}) });
const cancelBusBooking = (id, t) => apiFetch(`${API_BASE}/bus-bookings/${id}/cancel`, { method: "POST", headers: authHeaders(t), body: JSON.stringify({}) });
const fetchTrainBookings = (uid, t) => apiFetch(`${API_BASE}/train-bookings?userId=${uid}`, { headers: authHeaders(t) });
const deleteTrainBooking = (id, t) => apiFetch(`${API_BASE}/train-bookings/${id}`, { method: "DELETE", headers: authHeaders(t) });
const patchTrainBookingStatus = (id, s, t) => apiFetch(`${API_BASE}/train-bookings/${id}`, { method: "PATCH", headers: authHeaders(t), body: JSON.stringify({ status: s }) });
// const fetchActivityBookings  = (uid, t)  => apiFetch(`${API_BASE}/activity-bookings?userId=${uid}`, { headers: authHeaders(t) });
const deleteActivityBooking = (id, t) => apiFetch(`${API_BASE}/activity-bookings/${id}`, { method: "DELETE", headers: authHeaders(t) });
const patchActivityBookingStatus = (id, s, t) => apiFetch(`${API_BASE}/activity-bookings/${id}`, { method: "PATCH", headers: authHeaders(t), body: JSON.stringify({ status: s }) });

async function downloadFile(url, token, filename) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || "Download failed");
  }
  const blob = await res.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl; link.download = filename;
  document.body.appendChild(link); link.click(); link.remove();
  window.URL.revokeObjectURL(objectUrl);
}

function loadEasebuzzCheckoutScript() {
  return new Promise((resolve, reject) => {
    if (window.EasebuzzCheckout) return resolve();
    const existing = document.querySelector("script[data-easebuzz-checkout]");
    if (existing) { existing.addEventListener("load", resolve, { once: true }); return; }
    const script = document.createElement("script");
    script.src = "https://ebz-static.s3.ap-south-1.amazonaws.com/easecheckout/easebuzz-checkout.js";
    script.dataset.easebuzzCheckout = "true";
    script.onload = resolve;
    script.onerror = () => reject(new Error("Could not load payment gateway."));
    document.body.appendChild(script);
  });
}

const downloadHotelReceipt = (id, t, ref) => downloadFile(`${API_BASE}/hotel-bookings/${id}/receipt`, t, `${ref || "hotel-receipt"}.pdf`);
const downloadBusTicket = (id, t, ref) => downloadFile(`${API_BASE}/bus-bookings/${id}/receipt`, t, `${ref || "bus-ticket"}.pdf`);
const downloadFlightTicket = (id, t, ref) => downloadFile(`${API_BASE}/flight-bookings/${id}/eticket`, t, `${ref || "flight-eticket"}.pdf`);
const downloadTrainTicket = (id, t, ref) => downloadFile(`${API_BASE}/train-bookings/${id}/receipt`, t, `${ref || "train-ticket"}.pdf`);
const downloadActivityVoucher = (id, t, ref) => downloadFile(`${API_BASE}/activity-bookings/${id}/receipt`, t, `${ref || "activity-voucher"}.pdf`);

async function uploadAvatar(userId, file, token) {
  const form = new FormData(); form.append("avatar", file);
  const res = await fetch(`${API_BASE}/users/${userId}/avatar`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form });
  if (!res.ok) throw new Error("Avatar upload failed");
  return res.json();
}

async function removeAvatar(userId, token) {
  const res = await fetch(`${API_BASE}/users/${userId}/avatar`, { method: "DELETE", headers: authHeaders(token) });
  if (!res.ok) throw new Error("Avatar removal failed");
  return res.json();
}

const toSnakeCase = (obj) => {
  const map = { passportNumber: "passport_number", passportExpiry: "passport_expiry", emergencyName: "emergency_name", emergencyRelation: "emergency_relation", emergencyPhone: "emergency_phone" };
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [map[k] || k, v]));
};

/* ─────────────────────────────────────────────────────────────
   NORMALIZERS
───────────────────────────────────────────────────────────── */
const normalizeHotelBooking = (b) => ({
  ...b, id: b.id || b.booking_id || b._id, source: "hotel",
  destination: b.hotel_name, country: b.hotel_location, image: b.hotel_image,
  departure_date: b.check_in, return_date: b.check_out,
  num_travellers:
    Number(b.adults || 0) + Number(b.children || 0), total_price: b.grand_total, ticket_url: b.ticket_url || null,
});

const normalizeBusBooking = (b) => ({
  ...b, id: b.id || b.booking_id || b._id, source: "bus",
  destination: `${b.from_city || "Bus"}${b.to_city ? ` to ${b.to_city}` : ""}`,
  country: b.operator || b.bus_type || "", image: null,
  departure_date: b.travel_date, return_date: null,
  num_travellers: b.num_passengers, total_price: b.total_price,
  booking_reference: b.booking_reference || b.supplier_booking_id, ticket_url: b.ticket_url || null,
});

const normalizeBusCancellation = (c) => ({
  ...c,

  id: `bus-cancel-${c.id}`,

  cancellation_id: c.id,
  original_booking_id: c.booking_id,

  source: "bus-cancellation",
  isCancellationRecord: true,

  status: "cancelled",
  booking_status: "CANCELLED",

  destination:
    `${c.from_city || "Bus"}${c.to_city ? ` to ${c.to_city}` : ""
    }`,

  country:
    c.operator ||
    c.bus_type ||
    "",

  departure_date:
    c.travel_date,

  return_date: null,

  num_travellers:
    Number(c.cancelled_count || 0),

  booking_reference:
    c.booking_reference,

  seat_numbers:
    c.cancelled_seats,

  passenger_details:
    Array.isArray(c.cancelled_passengers)
      ? c.cancelled_passengers
      : safeJsonParse(
        c.cancelled_passengers,
        []
      ),

  total_price:
    Number(c.refund_amount || 0),

  refund_amount:
    Number(c.refund_amount || 0),
});

const getFlightPassengerId = (
  passenger,
  index
) =>
  Number(
    passenger?.passengerId ??
    passenger?.paxId ??
    index + 1
  );

const getPassengerCancellationStatusForLeg = (
  passenger,
  legIndex,
  leg = {}
) => {
  const byLeg =
    passenger?.cancellationByLeg &&
    typeof passenger.cancellationByLeg === "object"
      ? passenger.cancellationByLeg
      : {};

  const entry =
    byLeg[String(legIndex)] ??
    byLeg[legIndex];

  if (entry) {
    return String(
      typeof entry === "string"
        ? entry
        : entry.status || ""
    ).toLowerCase();
  }

  /*
   * Compatibility with bookings cancelled before
   * passenger + leg tracking was introduced.
   */
  const passengerStatus =
    String(
      passenger?.cancellationStatus || ""
    ).toLowerCase();

  const legStatus =
    String(
      leg?.cancellationStatus ||
      leg?.cancellation_status ||
      ""
    ).toLowerCase();

  if (
    (
      passengerStatus === "requested" ||
      passengerStatus === "cancelled"
    ) &&
    (
      legStatus === "requested" ||
      legStatus === "cancelled"
    )
  ) {
    return passengerStatus;
  }

  return "";
};

const isPassengerCancelledOnFlightLeg = (
  passenger,
  legIndex,
  leg
) => {
  const status =
    getPassengerCancellationStatusForLeg(
      passenger,
      legIndex,
      leg
    );

  return (
    status === "requested" ||
    status === "cancelled"
  );
};

const normalizeFlightBooking = (b) => {
  const passengers =
    safeJsonParse(
      b.passenger_details,
      []
    );

  const itineraryLegs =
    safeJsonParse(
      b.itinerary_legs,
      []
    );

  const legs =
    Array.isArray(itineraryLegs) &&
    itineraryLegs.length
      ? itineraryLegs
      : [
          {
            departCode: b.depart_code,
            arriveCode: b.arrive_code,
            airlinePnr: b.airline_pnr,
          },
        ];

  const legPassengerCounts =
    legs.map((leg, legIndex) => {
      const cancelledPassengers =
        passengers.filter(
          (passenger) =>
            isPassengerCancelledOnFlightLeg(
              passenger,
              legIndex,
              leg
            )
        );

      return {
        legIndex,

        departCode:
          leg?.departCode ||
          leg?.depart_code ||
          b.depart_code ||
          "",

        arriveCode:
          leg?.arriveCode ||
          leg?.arrive_code ||
          b.arrive_code ||
          "",

        airlinePnr:
          leg?.airlinePnr ||
          leg?.airline_pnr ||
          b.airline_pnr ||
          null,

        total: passengers.length,

        cancelled:
          cancelledPassengers.length,

        active:
          Math.max(
            0,
            passengers.length -
              cancelledPassengers.length
          ),
      };
    });

  const hasCancellationHistory =
    legPassengerCounts.some(
      (leg) =>
        leg.cancelled > 0
    );

  const allLegsFullyCancelled =
    legPassengerCounts.length > 0 &&
    legPassengerCounts.every(
      (leg) =>
        leg.total > 0 &&
        leg.active === 0
    );

  return {
    ...b,

    id:
      b.id ||
      b.booking_id ||
      b._id,

    source: "flight",

    destination:
      `${b.depart_code || "Flight"}${
        b.arrive_code
          ? ` to ${b.arrive_code}`
          : ""
      }`,

    country:
      b.airline ||
      b.flight_numbers ||
      "",

    image: null,

    departure_date:
      b.travel_date,

    return_date:
      b.return_date,

    /*
     * Keep original journey traveller count.
     * A passenger cancelled only on Return can
     * still be active on Outbound.
     */
    num_travellers:
      passengers.length > 0
        ? passengers.length
        : b.num_passengers,

    legPassengerCounts,

    hasCancellationHistory,

    hasPartialCancellation:
      hasCancellationHistory &&
      !allLegsFullyCancelled,

    total_price:
      b.total_price,

    post_booking_addon_total:
      b.post_booking_addon_total,

    booking_reference:
      b.booking_reference ||
      b.bookingReference ||
      null,

    ticket_url:
      b.ticket_url ||
      null,
  };
};

const buildFlightCancellationRecords = (
  booking
) => {
  const passengers =
    safeJsonParse(
      booking.passenger_details,
      []
    );

  const itineraryLegs =
    safeJsonParse(
      booking.itinerary_legs,
      []
    );

  const legs =
    Array.isArray(itineraryLegs) &&
    itineraryLegs.length
      ? itineraryLegs
      : [
          {
            departCode:
              booking.depart_code,

            arriveCode:
              booking.arrive_code,

            airlinePnr:
              booking.airline_pnr,
          },
        ];

  return legs
    .map((leg, legIndex) => {
      const cancelledPassengers =
        passengers
          .map(
            (
              passenger,
              passengerIndex
            ) => {
              const status =
                getPassengerCancellationStatusForLeg(
                  passenger,
                  legIndex,
                  leg
                );

              if (
                status !== "requested" &&
                status !== "cancelled"
              ) {
                return null;
              }

              return {
                ...passenger,

                passengerId:
                  getFlightPassengerId(
                    passenger,
                    passengerIndex
                  ),

                cancellationDisplayStatus:
                  status,
              };
            }
          )
          .filter(Boolean);

      if (
        cancelledPassengers.length === 0
      ) {
        return null;
      }

      const allConfirmedCancelled =
        cancelledPassengers.every(
          (passenger) =>
            passenger
              .cancellationDisplayStatus ===
            "cancelled"
        );

      return {
        ...booking,

        /*
         * UI-only ID.
         * This is NOT a new DB booking.
         */
        id:
          `flight-cancel-${booking.id}-${legIndex}`,

        original_booking_id:
          booking.id,

        source:
          "flight-cancellation",

        isFlightCancellationRecord:
          true,

        isCancellationRecord:
          true,

        /*
         * Generic Cancelled filter already checks
         * booking.status === "cancelled".
         */
        status: "cancelled",

        cancellationDisplayStatus:
          allConfirmedCancelled
            ? "cancelled"
            : "cancel_initiated",

        cancellationLegIndex:
          legIndex,

        cancellationLeg:
          leg,

        passenger_details:
          cancelledPassengers,

        itinerary_legs:
          [leg],

        num_travellers:
          cancelledPassengers.length,

        departure_date:
          legIndex === 0
            ? booking.travel_date
            : booking.return_date ||
              booking.travel_date,

        booking_reference:
          booking.booking_reference,

        airline_pnr:
          leg?.airlinePnr ||
          leg?.airline_pnr ||
          booking.airline_pnr ||
          null,
      };
    })
    .filter(Boolean);
};

const normalizeHolidayBooking = (b) => ({
  ...b, id: b.id || b.booking_id || b._id, source: "holiday",
  destination: b.destination || b.package_title || b.title || "Holiday Package",
  country: b.country || b.package_location || b.location || "",
  image: b.image || b.image_url || null,
  departure_date: b.travel_date, return_date: null,
  num_travellers: b.num_travellers, total_price: b.total_price, booking_reference: b.booking_reference,
});

const normalizeTrainBooking = (b) => ({
  ...b, id: b.id || b.booking_id || b._id, source: "train",
  destination: `${b.from_station || b.from_city || "Train"}${b.to_station || b.to_city ? ` to ${b.to_station || b.to_city}` : ""}`,
  country: b.train_name || b.train_number || "", image: null,
  departure_date: b.travel_date, return_date: null,
  num_travellers: b.num_passengers, total_price: b.total_price,
  booking_reference: b.booking_reference || b.pnr, ticket_url: b.ticket_url || null,
});

const normalizeActivityBooking = (b) => ({
  ...b, id: b.id || b.booking_id || b._id, source: "activity",
  destination: b.activity_name || b.title || "Activity",
  country: b.location || b.city || "", image: b.image || b.image_url || null,
  departure_date: b.activity_date || b.travel_date, return_date: null,
  num_travellers: b.num_participants || b.num_travellers, total_price: b.total_price,
  booking_reference: b.booking_reference, ticket_url: b.ticket_url || null,
});


function flattenUser(rawUser) {
  const u =
    rawUser?.user ??
    rawUser?.data?.user ??
    rawUser?.data ??
    rawUser ??
    {};

  return {
    name: u.name || "",
    email: u.email || "",
    phone: u.phone || "",

    avatar: u.avatar || null,
    tier: u.tier || "Member",

    memberSince:
      u.memberSince ??
      u.member_since ??
      new Date().getFullYear(),

    dob: toDateInputValue(u.dob),
    nationality: u.nationality || "",
    address: u.address || "",

    passportNumber:
      u.passportNumber ??
      u.passport_number ??
      "",

    passportExpiry: toDateInputValue(
      u.passportExpiry ??
      u.passport_expiry
    ),

    emergencyName:
      u.emergencyName ??
      u.emergency_name ??
      u.emergencyContact?.name ??
      "",

    emergencyRelation:
      u.emergencyRelation ??
      u.emergency_relation ??
      u.emergencyContact?.relation ??
      "",

    emergencyPhone:
      u.emergencyPhone ??
      u.emergency_phone ??
      u.emergencyContact?.phone ??
      "",

    walletBalance:
      u.wallet_balance ??
      u.walletBalance ??
      0,

    rewardPoints:
      u.reward_points ??
      u.rewardPoints ??
      0,

    totalSavings:
      u.total_savings ??
      u.totalSavings ??
      0,
  };
}


function extractUserId(authUser) {
  if (!authUser) return null;
  const rawId = authUser.id ?? authUser.userId ?? authUser.user_id ?? authUser._id ?? null;
  if (rawId == null) return null;
  const id = String(rawId).trim();
  return (id && id.toLowerCase() !== "null" && id.toLowerCase() !== "undefined") ? id : null;
}

function extractUserIdFromToken(token) {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1] || ""));
    return extractUserId(payload);
  } catch { return null; }
}

/* ─────────────────────────────────────────────────────────────
   TIER CONFIG
───────────────────────────────────────────────────────────── */
const TIER_CONFIG = {
  Gold: { cls: "gold", icon: "👑", next: "Premium", ptsNeeded: 10000, currentFloor: 5000 },
  Silver: { cls: "silver", icon: "⭐", next: "Gold", ptsNeeded: 5000, currentFloor: 2000 },
  Bronze: { cls: "bronze", icon: "🥉", next: "Silver", ptsNeeded: 2000, currentFloor: 500 },
  Member: { cls: "default", icon: "✦", next: "Bronze", ptsNeeded: 500, currentFloor: 0 },
  Premium: { cls: "premium", icon: "💎", next: null, ptsNeeded: 10000, currentFloor: 10000 },
};
const getTierConfig = (tier) => TIER_CONFIG[tier] || TIER_CONFIG.Member;

/* ─────────────────────────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────────────────────────── */
function MbtBadge({ status }) {
  const norm = String(status || "").toLowerCase();
  const cfg = STATUS_CONFIG[norm] || { label: status, color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
  return (
    <span className="mbt-badge" style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      {cfg.label}
    </span>
  );
}

function MbtSpinner() {
  return <div className="mbt-loading"><div className="mbt-spinner" /><p>Syncing dashboard architecture...</p></div>;
}

function MbtError({ message, onRetry }) {
  return (
    <div className="mbt-empty">
      <div className="mbt-empty-icon" style={{ color: "#fca5a5" }}><AlertTriangle size={40} /></div>
      <h3 className="mbt-empty-title">Database Synced Error</h3>
      <p className="mbt-empty-text">{message}</p>
      {onRetry && <button className="mbt-retry-btn" onClick={onRetry}>Re-authenticate Link</button>}
    </div>
  );
}

function FilterBtn({ label, count, active, onClick }) {
  return (
    <button onClick={onClick} className={`mbt-filter-tab ${active ? "active" : ""}`}>
      {label}
      {count !== undefined && <span className="mbt-filter-count">{count}</span>}
    </button>
  );
}

function SourceIcon({ source, size = 20 }) {
  if (source === "hotel") return <Luggage size={size} />;
  if (source === "bus") return <Armchair size={size} />;
  if (source === "holiday") return <MapPinned size={size} />;
  if (source === "flight") return <Plane size={size} />;
  if (source === "train") return <Train size={size} />;
  if (source === "activity") return <Backpack size={size} />;
  return <Ticket size={size} />;
}

function bookingBarClass(source) {
  return `mbt-booking-bar mbt-booking-bar--${source || "trip"}`;
}

/* BookingCard */
function BookingCard({ booking, onDelete, onStatusChange, onDownloadReceipt, onPayFlight, downloadingId, payingFlightId }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const isDownloading = downloadingId === booking.id;
  const isPaying = payingFlightId === booking.id;


  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    if (menuOpen) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menuOpen]);

  const canDownload =
    ["hotel", "bus", "train", "activity"].includes(booking.source) ||
    (booking.source === "flight" && booking.ticketing_status === "success");

  const bookingStatus = String(booking.status || "").toLowerCase();
  const ticketingStatus = String(booking.ticketing_status || "").toLowerCase();
  const paymentStatus = String(booking.payment_status || "").toLowerCase();
  const hasGatewayPayment =
    paymentStatus === "success" ||
    Boolean(booking.payment_id || booking.paymentId);

  const canPayFlight =
    booking.source === "flight" &&
    !hasGatewayPayment &&
    (
      bookingStatus === "held" ||
      (
        bookingStatus === "pending_payment" &&
        !["success", "inprocess"].includes(ticketingStatus)
      )
    );
  const opensCancelPage = ["flight", "hotel", "bus", "train", "activity"].includes(booking.source);

  const handleCancel = () => {
    if (opensCancelPage) { navigate(`/cancel-booking/${booking.source}/${booking.id}`); return; }
    onStatusChange(booking, "cancelled");
  };

  return (
    <div className="mbt-booking-card">
      <div className={bookingBarClass(booking.source)} />
      <div className="mbt-booking-inner">
        {booking.image
          ? <img src={booking.image} alt={booking.destination} className="mbt-booking-thumb" />
          : <div className="mbt-booking-thumb-placeholder"><SourceIcon source={booking.source} size={22} /></div>
        }
        <div className="mbt-booking-info">
          <div className="mbt-booking-title">{booking.destination || "—"}</div>
          <div className="mbt-booking-meta">
            <span>
              <CalendarDays size={12} />
              {formatDate(booking.travel_date)}
            </span>

            <span>
              <Plane size={12} />
              {booking.airline} · {booking.flight_numbers}
            </span>

            {booking.fare_name && (
              <span>
                {booking.fare_name}
                {booking.class_type ? ` · ${booking.class_type}` : ""}
              </span>
            )}

            {booking.fare_type &&
              String(booking.fare_type).toLowerCase() !== "regular" && (
                <span>
                  <Ticket size={12} />
                  {booking.fare_type} Fare
                </span>
              )}
          </div>
          {booking.booking_reference && <div className="mbt-booking-ref">REF&nbsp;<strong>{booking.booking_reference}</strong></div>}
        </div>
        <div className="mbt-booking-right">
          <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
            <MbtBadge
              status={
                String(
                  booking.booking_status || ""
                ).toUpperCase() ===
                  "PARTIALLY_CANCELLED"
                  ? "partially_cancelled"
                  : booking.status
              }
            />
            {canCancelBooking(booking) && (
              <div className="mbt-menu-wrap" ref={menuRef}>
                <button className="mbt-menu-trigger" onClick={() => setMenuOpen(p => !p)}>⋯</button>
                {menuOpen && (
                  <div className="mbt-context-menu">
                    <button className="mbt-menu-item" style={{ color: "#d97706" }} onClick={() => { handleCancel(); setMenuOpen(false); }}>
                      <X size={13} /> Cancel Booking
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="mbt-booking-price">₹{formatPrice(booking.total_price)}</div>
          <div className="mbt-booking-actions">
            {canDownload && (
              <button type="button" className="mbt-booking-dl" disabled={isDownloading} onClick={() => onDownloadReceipt?.(booking)}>
                <Download size={12} />{isDownloading ? "Fetching..." : "Get Receipt"}
              </button>
            )}
            {canPayFlight && (
              <button type="button" className="mbt-booking-pay-now" disabled={isPaying} onClick={() => onPayFlight?.(booking)}>
                <CreditCard size={12} />{isPaying ? "Processing..." : "Continue Payment"}
              </button>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

/* BookingCategorySection */
function BookingCategorySection({ title, icon, bookings, filter, setFilter, loading, error, onRetry, onDelete, onStatusChange, onDownloadReceipt, onPayFlight, downloadingId, payingFlightId, emptyTitle, emptyText, renderCard }) {
  const matchesCategoryFilter = (booking, category) => {
    const status = String(booking.status || "").toLowerCase();
    if (category === "all") return true;
    if (category === "cancelled") return status === "cancelled" || status === "released";
    if (category === "held" && booking.source === "hotel") {
      const supplierStatus = String(booking.supplier_status || booking.booking_status || "").toLowerCase();
      return status === "on_hold" || status === "blocked" || supplierStatus === "blocked";
    }
    return status === category;
  };

  const filtered = bookings.filter(b => {
    return matchesCategoryFilter(b, filter);
  });

  return (
    <div className="mbt-section-fade">
      <div className="mbt-cat-head">
        <div style={{ flex: 1 }}>
          <h2 className="mbt-section-heading">{title} <span className="mbt-heading-line" /></h2>
          {/* <p className="mbt-cat-sub">Production endpoints active for automated query filters.</p> */}
        </div>
        <div className="mbt-cat-count">{filtered.length} logs</div>
      </div>

      <div className="mbt-filter-row">
        {["all", "upcoming", "held", "pending_payment", "completed", "cancelled"].map(f => {
          const cnt = bookings.filter(b => matchesCategoryFilter(b, f)).length;
          return <FilterBtn key={f} label={f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)} count={cnt} active={filter === f} onClick={() => setFilter(f)} />;
        })}
      </div>

      {loading ? <MbtSpinner /> : error ? <MbtError message={error} onRetry={onRetry} /> : filtered.length === 0 ? (
        <div className="mbt-empty-glass">
          <div className="mbt-empty-icon">{renderIconNode(icon, { size: 22, strokeWidth: 2 })}</div>
          <h3 className="mbt-empty-title">{emptyTitle}</h3>
          <p className="mbt-empty-text">{emptyText}</p>
        </div>
      ) : (
        <div className="mbt-bookings-list">
          {filtered.map(b => renderCard
            ? renderCard(b)
            : <BookingCard key={`${b.source}-${b.id}`} booking={b} onDelete={onDelete} onStatusChange={onStatusChange} onDownloadReceipt={onDownloadReceipt} onPayFlight={onPayFlight} downloadingId={downloadingId} payingFlightId={payingFlightId} />
          )}
        </div>
      )}
    </div>
  );
}

/* VisaCard */
function VisaCard({ visa, onDelete }) {
  const appId = visa.application_id || visa.id;
  return (
    <div className="mbt-visa-card">
      <div className="mbt-visa-top">
        <span className="mbt-visa-flag">🌍</span>
        <div className="mbt-visa-info">
          <div className="mbt-visa-country">{visa.country_name || visa.country || "—"}</div>
          <div className="mbt-visa-type">{visa.visa_type || "Tourist"} Variant</div>
        </div>
        <MbtBadge status={visa.status || "submitted"} />
      </div>
      <div className="mbt-visa-rows">
        <div className="mbt-visa-row"><span>Reference ID</span><strong>{visa.reference_number || "—"}</strong></div>
        <div className="mbt-visa-row"><span>Est. Processing</span><span>{visa.processing_time || "4-7 Business Days"}</span></div>
        <div className="mbt-visa-row"><span>Passport Reference</span><span>{visa.passport_number ? `******${String(visa.passport_number).slice(-4)}` : "—"}</span></div>
        <div className="mbt-visa-row"><span>Consular Fee</span><strong>₹{formatPrice(visa.total_fee || visa.fee_paid)}</strong></div>
      </div>
      <div style={{ padding: "0 16px 14px" }}>
        <button className="mbt-wishlist-remove" onClick={() => { if (window.confirm("Purge application history?")) onDelete(appId); }}>
          Purge Record
        </button>
      </div>
    </div>
  );
}

/* WishlistCard */
function WishlistCard({ item, onRemove, onBookNow, removingId }) {
  return (
    <div className="mbt-wishlist-card">
      <div className="mbt-wishlist-img-wrap">
        {item.image ? <img src={item.image} alt={item.destination} className="mbt-wishlist-img" /> : <div className="mbt-wishlist-placeholder"><MapPinned size={32} /></div>}
      </div>
      <div className="mbt-wishlist-info">
        <div className="mbt-wishlist-name">{item.destination || "—"}</div>
        <div className="mbt-wishlist-country">📍 {item.country || "Global Route"}</div>
        {item.price && <div className="mbt-wishlist-price">₹{formatPrice(item.price)}<span style={{ fontSize: 10, color: "#9ca3af" }}> base</span></div>}
        <div style={{ display: "flex", marginTop: 8 }}>
          <button className="mbt-wishlist-book-now" onClick={() => onBookNow(item)}>
            Book Now
          </button>
          <button className="mbt-wishlist-remove" disabled={removingId === item.id} onClick={() => onRemove(item.id)}>
            {removingId === item.id ? "Dropping..." : "✕ Drop Item"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ProfileRow Completion */

function ProfileRow({ label, value, fieldKey, inputType = "text", onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const handleSave = async () => {
    if (saving) return;

    setSaving(true);

    try {
      const saved = await onSave(fieldKey, draft.trim());

      // Keep the editor open when the API update fails.
      if (saved !== false) {
        setEditing(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (saving) return;
    setDraft(value || "");
    setEditing(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSave();
    }

    if (e.key === "Escape") {
      handleCancel();
    }
  };

  if (editing) {
    return (
      <div className="mbt-profile-row mbt-profile-row--editing">
        <span className="mbt-profile-row-label">{label}</span>

        <input
          ref={inputRef}
          type={inputType}
          className="mbt-profile-row-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          disabled={saving}
        />

        <div
          className="mbt-profile-row-actions"
          style={{ opacity: 1 }}
        >
          <button
            type="button"
            className="mbt-profile-row-btn mbt-profile-row-btn--save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>

          <button
            type="button"
            className="mbt-profile-row-btn mbt-profile-row-btn--cancel"
            onClick={handleCancel}
            disabled={saving}
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  if (isEmpty(value)) {
    return (
      <div
        className="mbt-profile-add-row"
        onClick={() => {
          setDraft("");
          setEditing(true);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            setDraft("");
            setEditing(true);
          }
        }}
      >
        <span className="mbt-profile-add-label">{label}</span>
        <span className="mbt-profile-add-cta">
          + Add {label}
        </span>
      </div>
    );
  }

  return (
    <div
      className="mbt-profile-row"
      onClick={() => setEditing(true)}
    >
      <span className="mbt-profile-row-label">{label}</span>

      <span className="mbt-profile-row-value">
        {inputType === "date" ? formatDate(value) : value}
      </span>

      <div className="mbt-profile-row-actions">
        <button
          type="button"
          className="mbt-profile-row-edit-trigger"
          onClick={(e) => {
            e.stopPropagation();
            setEditing(true);
          }}
        >
          Edit
        </button>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────
   PRIMARY CONTAINER COMPONENT
───────────────────────────────────────────────────────────── */
export default function UserProfileDashboard() {
  const { authUser, token, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get("tab") || "overview";
  const [airportCache, setAirportCache] = useState({});
  // Framework UI State
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Global Sync Architecture State
  const [userProfile, setUserProfile] = useState(null);
  const [allBookings, setAllBookings] = useState([]);
  const [hotelBookings, setHotelBookings] = useState([]);
  const [flightBookings, setFlightBookings] = useState([]);
  const [busBookings, setBusBookings] = useState([]);
  const [
    busCancellationHistory,
    setBusCancellationHistory
  ] = useState([]);
  const [holidayBookings, setHolidayBookings] = useState([]);
  const [trainBookings, setTrainBookings] = useState([]);
  const [activityBookings, setActivityBookings] = useState([]);
  const [visaApplications, setVisaApplications] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);

  // Category Level Filtering
  const [hotelFilter, setHotelFilter] = useState("all");
  const [flightFilter, setFlightFilter] = useState(
    searchParams.get("filter") || "all"
  );
  const [busFilter, setBusFilter] = useState("all");
  const [holidayFilter, setHolidayFilter] = useState("all");
  const [trainFilter, setTrainFilter] = useState("all");
  const [activityFilter, setActivityFilter] = useState("all");
  useEffect(() => {
    if (currentTab === "flights") {
      setFlightFilter(searchParams.get("filter") || "all");
    }
  }, [currentTab, searchParams]);

  // Operational Loading Flags
  const [globalLoading, setGlobalLoading] = useState(true);
  const [globalError, setGlobalError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [payingFlightId, setPayingFlightId] = useState(null);
  const [payingHotelId, setPayingHotelId] = useState(null);
  const [hotelHoldActionId, setHotelHoldActionId] = useState(null);
  const [flightPaymentMessage, setFlightPaymentMessage] = useState("");
  const [removingWishlistId, setRemovingWishlistId] = useState(null);
  const pollIntervalRef = useRef(null);
  const [flightSearchText, setFlightSearchText] = useState("");
  const [flightAirlineFilter, setFlightAirlineFilter] = useState("all");
  const [flightDateFrom, setFlightDateFrom] = useState("");
  const [flightDateTo, setFlightDateTo] = useState("");
  useEffect(() => {
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, []);
  const activeUserId = extractUserId(authUser) || extractUserIdFromToken(token);
  const resolveAirportCodes = useCallback(async (codes) => {
    const unique = [...new Set(codes.filter(Boolean))].filter(c => !airportCache[c]);
    if (!unique.length) return;
    try {
      const res = await lookupAirports(unique, token);
      setAirportCache(prev => ({ ...prev, ...(res.airports || {}) }));
    } catch (_) { /* ignore — falls back to showing the code */ }
  }, [airportCache, token]);
  const synchronizePipeline = useCallback(async () => {
    if (!activeUserId) {
      setGlobalError("Active session payload missing. Re-routing initialization sequence.");
      setGlobalLoading(false);
      return;
    }
    try {
      setGlobalError(null);
      const [
        uRes,
        bRes,
        hRes,
        fRes,
        busRes,
        busCancelRes,
        holRes,
        trainRes,
        vRes,
        wRes
      ] = await Promise.all([
        fetchUser(activeUserId, token)
          .catch(() => ({})),

        fetchBookings(activeUserId, token)
          .catch(() => []),

        fetchHotelBookings(activeUserId, token)
          .catch(() => []),

        fetchFlightBookings(activeUserId, token)
          .catch(() => []),

        fetchBusBookings(activeUserId, token)
          .catch(() => []),

        fetchBusCancellationHistory(token)
          .catch(() => []),

        fetchHolidayBookingsForUser(activeUserId, token)
          .catch(() => []),

        fetchTrainBookings(activeUserId, token)
          .catch(() => []),

        fetchVisa(activeUserId, token)
          .catch(() => []),

        fetchWishlist(activeUserId, token)
          .catch(() => []),
      ]);
      setUserProfile(flattenUser(uRes));

      const normalizedHotels = (Array.isArray(hRes) ? hRes : hRes.data || []).map(normalizeHotelBooking);
      const normalizedFlights = (Array.isArray(fRes) ? fRes : fRes.data || []).map(normalizeFlightBooking);
      const normalizedFlightCancellations =
  normalizedFlights.flatMap(
    buildFlightCancellationRecords
  );

const flightBookingsWithCancellationHistory =
  new Set(
    normalizedFlightCancellations.map(
      (record) =>
        String(
          record.original_booking_id
        )
    )
  );

const visibleNormalFlightBookings =
  normalizedFlights.filter(
    (booking) => {
      const fullyCancelled =
        String(
          booking.status || ""
        ).toLowerCase() ===
        "cancelled";

      const hasCancellationHistory =
        flightBookingsWithCancellationHistory.has(
          String(booking.id)
        );

      /*
       * If the whole original booking is already
       * cancelled and derived cancellation cards
       * exist, don't show a duplicate original card.
       */
      return !(
        fullyCancelled &&
        hasCancellationHistory
      );
    }
  );
      const normalizedBuses = (Array.isArray(busRes) ? busRes : busRes.data || []).map(normalizeBusBooking);
      const normalizedBusCancellations =
        (
          Array.isArray(busCancelRes)
            ? busCancelRes
            : busCancelRes?.data || []
        ).map(normalizeBusCancellation);
      const bookingsWithCancellationHistory =
        new Set(
          normalizedBusCancellations.map(
            (c) => String(c.original_booking_id)
          )
        );
      const visibleNormalBusBookings =
        normalizedBuses.filter((booking) => {
          const fullyCancelled =
            String(
              booking.status || ""
            ).toLowerCase() === "cancelled";

          const hasCancellationHistory =
            bookingsWithCancellationHistory.has(
              String(booking.id)
            );

          /*
           * When cancellation history exists,
           * don't show a duplicate empty/fully-cancelled
           * original booking in Cancelled tab.
           */
          return !(
            fullyCancelled &&
            hasCancellationHistory
          );
        });
      const normalizedHolidays = (Array.isArray(holRes) ? holRes : holRes.data || []).map(normalizeHolidayBooking);
      const normalizedTrains = (Array.isArray(trainRes) ? trainRes : trainRes.data || []).map(normalizeTrainBooking);

      setHotelBookings(normalizedHotels);
      setFlightBookings([
  ...visibleNormalFlightBookings,
  ...normalizedFlightCancellations,
]);
      const codes = normalizedFlights.flatMap(b => [b.depart_code, b.arrive_code]);
      resolveAirportCodes(codes);
      setBusCancellationHistory(
        normalizedBusCancellations
      );

      setBusBookings([
        ...visibleNormalBusBookings,
        ...normalizedBusCancellations,
      ]);
      setHolidayBookings(normalizedHolidays);
      setTrainBookings(normalizedTrains);
      setActivityBookings([]); // no backend support yet
      setVisaApplications(Array.isArray(vRes) ? vRes : vRes.data || []);
      setWishlistItems(Array.isArray(wRes) ? wRes : wRes.data || []);

      const unified = [...normalizedHotels, ...normalizedFlights, ...normalizedBuses, ...normalizedHolidays, ...normalizedTrains];
      unified.sort((a, b) => new Date(b.departure_date || 0) - new Date(a.departure_date || 0));
      setAllBookings(unified);
      return { flights: normalizedFlights };

    } catch (err) {
      setGlobalError(err.message || "Failed to complete unified API pipeline synchronization.");
      return { flights: [] };
    } finally {
      setGlobalLoading(false);
    }
  }, [activeUserId, token]);

  useEffect(() => {
    synchronizePipeline();
  }, [synchronizePipeline]);
  useEffect(() => {
    if (currentTab === "flights") {
      synchronizePipeline().then(({ flights = [] } = {}) => {
        // Self-heal: any flight still "inprocess"/unresolved gets re-checked
        // against the airline right now, instead of waiting on a cron job
        // or an in-memory poller that may no longer exist.
        resolveStuckFlightTickets(flights);
      });
    }
  }, [currentTab]);

  const resolveStuckFlightTickets = async (bookings = flightBookings) => {
    const stuck = bookings.filter(b =>
      ["pending_ticket", "completed"].includes(String(b.status || "").toLowerCase()) &&
      !["success", "cancelled", "blocked"].includes(String(b.ticketing_status || "").toLowerCase())
    );
    if (!stuck.length) return;
    await Promise.allSettled(
      stuck.map(b =>
        apiFetch(`${API_BASE}/flight-ticketing/status/${b.id}`, { headers: authHeaders(token) })
      )
    );
    synchronizePipeline();
  };
  
const handleUpdateProfileField = async (key, val) => {
  if (!activeUserId) {
    alert("Cannot update profile: user session is unavailable.");
    return false;
  }

  try {
    const apiPayload = toSnakeCase({ [key]: val });

    const updated = await patchUser(
      activeUserId,
      apiPayload,
      token
    );

    const userData =
      updated?.user ??
      updated?.data?.user ??
      updated?.data ??
      updated;

    // Apply the saved value even when the API returns only a
    // partial response, without replacing other profile fields.
    setUserProfile((prev) =>
      flattenUser({
        ...(prev || {}),
        ...(userData && typeof userData === "object"
          ? userData
          : {}),
        [key]: val,
      })
    );

    return true;
  } catch (err) {
    alert(
      `Profile update failed: ${
        err.message || "Please try again."
      }`
    );

    return false;
  }
};


  const handleAvatarMutation = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadAvatar(activeUserId, file, token);
      const newAvatar = res?.avatar || res?.user?.avatar || null;
      if (newAvatar) {
        setUserProfile(prev => ({ ...prev, avatar: newAvatar }));
        updateUser({ avatar: newAvatar });   // ← keeps Navbar in sync
      } else {
        synchronizePipeline();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAvatarRemoval = async () => {
    if (!window.confirm("Verify profile avatar removal request?")) return;
    try {
      await removeAvatar(activeUserId, token);
      setUserProfile(prev => ({ ...prev, avatar: null }));
      updateUser({ avatar: null });   // ← keeps Navbar in sync
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDownloadTicketReceipt = async (booking) => {
    setDownloadingId(booking.id);
    try {
      if (booking.source === "hotel") await downloadHotelReceipt(booking.id, token, booking.booking_reference);
      if (booking.source === "bus") await downloadBusTicket(booking.id, token, booking.booking_reference);
      if (booking.source === "flight") await downloadFlightTicket(booking.id, token, booking.booking_reference);
      if (booking.source === "train") await downloadTrainTicket(booking.id, token, booking.booking_reference);
      if (booking.source === "activity") await downloadActivityVoucher(booking.id, token, booking.booking_reference);
    } catch (err) {
      alert(`Receipt routing compilation faulted: ${err.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleReleaseHotelHold = async (booking) => {
    if (hotelHoldActionId) return;
    if (!booking.booking_reference) {
      alert("Hotel booking reference is missing.");
      return;
    }

    setHotelHoldActionId(booking.id);
    try {
      await apiFetch(`${API_BASE}/hotel-search/hold-confirm`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({
          bookingId: booking.booking_reference,
          requestType: "RELEASE",
          requestId: `hotel_hold_release_${Date.now()}`,
        }),
      });
      await synchronizePipeline();
    } catch (err) {
      alert(err.message || "Could not release this hotel hold.");
    } finally {
      setHotelHoldActionId(null);
    }
  };

  const handleContinueHotelHoldPayment = async (booking) => {
    if (payingHotelId) return;
    const expiryTime = parseHoldExpiryTime(booking);
    if (expiryTime && expiryTime <= Date.now()) {
      alert("Hold Expired");
      await synchronizePipeline();
      return;
    }
    if (!booking.booking_reference) {
      alert("Hotel booking reference is missing.");
      return;
    }

    const payableAmount = Number(booking.grand_total || booking.total_price || 0);
    const payerEmail = booking.guest_email || userProfile?.email || "";
    const payerPhone = String(booking.guest_phone || userProfile?.phone || "").replace(/\D/g, "").slice(-10) || "9999999999";
    const checkIn = normalizeHotelPaymentDate(booking.checkIn || booking.check_in);
    const checkOut = normalizeHotelPaymentDate(booking.checkOut || booking.check_out);
    if (!Number.isFinite(payableAmount) || payableAmount <= 0) {
      alert("This hotel booking has no payable amount. Please contact support.");
      return;
    }
    if (!payerEmail) {
      alert("Email is required before payment.");
      return;
    }
    if (!checkIn || !checkOut) {
      alert("Hotel check-in/check-out dates are missing. Please contact support.");
      return;
    }

    setPayingHotelId(booking.id);
    let checkoutOpened = false;
    try {
      await loadEasebuzzCheckoutScript();
      const payload = await initiateHotelEasebuzzPayment({
        booking: {
          ...booking,
          bookingReference: booking.requestId || booking.request_id || booking.id,
          id: booking.hotel_id || booking.id,
          selectedRoom: { name: String(booking.room_type || "").split("×")[0].trim() },
          guestEmail: payerEmail,
          guestPhone: payerPhone,
          checkIn,
          checkOut,
        },
        amount: payableAmount,
        nights: booking.nights || 1,
        token,
        onAuthExpired: requestSessionExpiry,
        // Server prices this payment from the booking row (grand_total),
        // never from the amount above.
        paymentContext: { bookingReference: booking.booking_reference },
      });

      const merchantKey = String(payload.key || "").trim();
      const accessKey = String(payload.access_key || "").trim();
      if (!merchantKey || !accessKey || accessKey.length < 20) {
        throw new Error(payload.error || "Payment gateway could not be initialized.");
      }

      const easebuzzCheckout = new window.EasebuzzCheckout(merchantKey, payload.env || "test");
      easebuzzCheckout.initiatePayment({
        access_key: accessKey,
        onResponse: async (response) => {            try {
              if (response.status !== "success") {
                throw new Error(response.error_Message || "Payment was not completed.");
              }
              // Verify with the backend (signature / Easebuzz API) BEFORE the
              // hold is confirmed — a client redirect alone is never trusted.
              
const verification = await verifyHotelEasebuzzPayment(
  {
    ...response,
    txnid: response.txnid || payload.txnid,
  },
  token
);

if (!verification.verified) {
  throw new Error(
    "Payment could not be verified yet. Please wait a moment and try again."
  );
}

              await apiFetch(`${API_BASE}/hotel-search/hold-confirm`, {
              method: "POST",
              headers: authHeaders(token),
              body: JSON.stringify({
                bookingId: booking.booking_reference,
                requestType: "CONFIRM",
                requestId: `hotel_hold_confirm_${Date.now()}`,
                paymentMethod: "Easebuzz",
                paymentTxnId: response.txnid || payload.txnid || null,
                easepayId: response.easepayid || null,
              }),
            });
            setPayingHotelId(null);
            await synchronizePipeline();
            setHotelFilter("upcoming");
            setSearchParams({ tab: "hotels" });
          } catch (err) {
            alert(err.message || "Payment completed, but hotel hold confirmation failed. Please contact support.");
            await synchronizePipeline();
          } finally {
            setPayingHotelId(null);
          }
        },
      });
      checkoutOpened = true;
    } catch (err) {
      alert(err.message || "Payment gateway could not be initialized.");
    } finally {
      if (!checkoutOpened) setPayingHotelId(null);
    }
  };

  const handleInitiateFlightPaymentGateway = async (booking) => {
    if (payingFlightId) return;

    setFlightPaymentMessage("");
    setPayingFlightId(booking.id);
    let checkoutOpened = false;
    try {
      let itineraryLegs = booking.itinerary_legs || [];
      if (typeof itineraryLegs === "string") {
        try {
          itineraryLegs = JSON.parse(itineraryLegs);
        } catch {
          itineraryLegs = [];
        }
      }
      itineraryLegs = Array.isArray(itineraryLegs) ? itineraryLegs : [];
      const hasItinerarySupplierRefs =
        itineraryLegs.length > 1 &&
        itineraryLegs.every((leg) => leg?.supplierBookingRef);
      const missingItineraryLegIndex =
        itineraryLegs.length > 1
          ? itineraryLegs.findIndex((leg) => !leg?.supplierBookingRef)
          : -1;

      if (!booking.supplier_booking_ref && !hasItinerarySupplierRefs) {
        if (missingItineraryLegIndex !== -1) {
          throw new Error(`Flight ${missingItineraryLegIndex + 1} is missing a supplier booking reference. Please search for the flight again.`);
        }
        throw new Error("This booking session has expired. Please search for the flight again.");
      }
      const payableAmount = Number(booking.total_price || 0);
      const payerEmail = booking.passenger_email || userProfile?.email || "";
      const rawPhone = booking.passenger_phone || userProfile?.phone || "";
      const payerPhone = String(rawPhone).replace(/\D/g, "").slice(-10) || "9999999999";
      if (!Number.isFinite(payableAmount) || payableAmount <= 0) {
        throw new Error("This booking has no payable amount. Please contact support.");
      }
      if (!payerEmail) {
        throw new Error("Email is required before payment.");
      }
      const bookingStatus =
        String(
          booking.status || ""
        ).toLowerCase();

      const shouldCheckResumeState =
        bookingStatus === "held";

      if (shouldCheckResumeState) {
        const resumeState =
          await verifyHeldFlightPaymentEligibility(
            booking.id,
            token
          );

        console.log(
          "[RESUME PAYMENT STATE]",
          {
            bookingId: booking.id,
            localStatus: bookingStatus,
            supplierAction:
              resumeState?.action,
            legs:
              resumeState?.legResults,
          }
        );

        /*
         * Supplier already confirms booking.
         * Do NOT open Easebuzz.
         */
        if (
          resumeState?.action ===
          "confirmed"
        ) {
          await synchronizePipeline();

          alert(
            "This booking is already confirmed with the airline."
          );

          return;
        }

        if (
          resumeState?.action === "processing"
        ) {
          await synchronizePipeline();

          alert(
            "Payment is complete. Your ticket is being processed by the airline."
          );

          return;
        }

        /*
         * Supplier already received payment.
         * Do NOT charge customer again.
         * Resume ticketing instead.
         */
        if (
          resumeState?.action === "ticket"
        ) {
          const ticketEndpoint =
            hasItinerarySupplierRefs
              ? "/flight-ticketing/ticket-itinerary"
              : "/flight-ticketing/ticket";

          const ticket =
            await apiFetch(
              `${API_BASE}${ticketEndpoint}`,
              {
                method: "POST",
                headers:
                  authHeaders(token),

                body: JSON.stringify({
                  bookingId:
                    booking.id,

                  ticketingType:
                    bookingStatus === "held"
                      ? "2"
                      : "1",
                }),
              }
            );

          console.log(
            "[RESUME PAYMENT SKIP GATEWAY]",
            {
              bookingId:
                booking.id,
              ticketStatus:
                ticket?.status,
            }
          );

          await synchronizePipeline();

          return;
        }
      }

      await loadEasebuzzCheckoutScript();
      const payload = await apiFetch(`${API_BASE}/payment/easebuzz/initiate`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({
          // server-priced: amount is derived from the booking's saved pricing
          // snapshot (client value above is ignored by the flight branch)
          service: "flight",
          amount: payableAmount.toFixed(2),
          productinfo: `Flight ${booking.depart_code || ""} to ${booking.arrive_code || ""}`.trim(),
          firstname: booking.passenger_name || userProfile?.name || "Traveller",
          email: payerEmail,
          phone: payerPhone,
          bookingReference: booking.booking_reference || booking.supplier_booking_ref,
          udf1: booking.travel_date ? String(booking.travel_date).slice(0, 10) : "",
          udf2: booking.airline || "",
          udf3: booking.fare_name || "",
        })
      });
      const merchantKey = String(payload.key || "").trim();
      const accessKey = String(payload.access_key || "").trim();
      if (!merchantKey || !accessKey || accessKey.length < 20) {
        throw new Error(payload.error || "Payment gateway could not be initialized.");
      }

      const easebuzzCheckout = new window.EasebuzzCheckout(merchantKey, payload.env || "test");
      easebuzzCheckout.initiatePayment({
        access_key: accessKey,
        onResponse: async (response) => {
          try {
            if (response.status !== "success") {
              throw new Error(response.error_Message || "Payment was not completed.");
            }
            const paymentId = response.easepayid || response.txnid || "";
            const addPaymentEndpoint = hasItinerarySupplierRefs
              ? "/flight-ticketing/add-payment-itinerary"
              : "/flight-ticketing/add-payment";
            const ticketEndpoint = hasItinerarySupplierRefs
              ? "/flight-ticketing/ticket-itinerary"
              : "/flight-ticketing/ticket";

            const addPayment = await apiFetch(`${API_BASE}${addPaymentEndpoint}`, {
              method: "POST",
              headers: authHeaders(token),
              body: JSON.stringify({
                bookingId: booking.id,
                transactionType: 0,
                clientRefNo: paymentId,
                gatewayResponse: response,
              }),
            });
            if (!addPayment.success) {
              throw new Error(
                addPayment.errorDesc || "Airline payment confirmation failed."
              );
            }

            const ticket = await apiFetch(`${API_BASE}${ticketEndpoint}`, {
              method: "POST",
              headers: authHeaders(token),
              body: JSON.stringify({
                bookingId: booking.id,

                ticketingType:
                  String(
                    booking.status || ""
                  ).toLowerCase() === "held"
                    ? "2"
                    : "1",
              }),
            });

            // A 200 response can still be a logical failure (success:false) —
            // e.g. status "failed". Only "success"/"blocked"/"inprocess" are OK to proceed.
            const ticketStatus = String(ticket.status || ticket.ticketing_status || "").toLowerCase();
            if (!ticket.success && !["success", "blocked", "inprocess"].includes(ticketStatus)) {
              throw new Error(ticket.error || ticket.pnr?.failureRemark || "Airline ticketing could not be completed. Our team will follow up.");
            }
            // Safe to include status here: this PATCH runs only after /ticket has
            // already resolved (sequential await, not concurrent), so ticket.status
            // is the final value — no race with saveTicketResult().
            await apiFetch(`${API_BASE}/flight-bookings/${booking.id}`, {
              method: "PATCH",
              headers: authHeaders(token),
              body: JSON.stringify({
                status:
  ticketStatus === "success" || ticketStatus === "blocked"
    ? "upcoming"
    : "pending_ticket",
                paymentId,
                paymentMethod: response.mode || "easebuzz",
                paymentStatus: "success",
                gatewayResponse: response,
              }),
            });

            await synchronizePipeline();

            if (ticketStatus === "success") {
              setFlightFilter("upcoming");
              setSearchParams({
                tab: "flights",
                filter: "upcoming",
              });
            }
            if (ticket.status === "inprocess") {
              if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = setInterval(async () => {
                try {
                  const statusRes = await fetch(`${API_BASE}/flight-ticketing/status/${booking.id}`, {
                    headers: authHeaders(token),
                  });
                  const statusData = await statusRes.json();
                  if (["success", "failed", "cancelled", "blocked"].includes(statusData.ticketing_status)) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                    synchronizePipeline(); // refresh the booking list so the badge/status updates
                  }
                } catch (_) {
                  // Supplier can be briefly unavailable — keep polling.
                }
              }, 5 * 60 * 1000);
            }
          } catch (statusErr) {
            console.error(
              "[FLIGHT TICKETING AFTER PAYMENT]",
              statusErr
            );

            await synchronizePipeline();

            setFlightPaymentMessage(
              "Payment completed successfully, but airline ticket confirmation could not be completed. Our team will follow up."
            );
          } finally {
            setPayingFlightId(null);
          }
        }
      });
      checkoutOpened = true;
    } catch (err) {
      if (err.code === "HELD_HOLD_INACTIVE") {
        alert(err.message);
      } else {
        alert(`Payment subsystem handshake failure: ${err.message}`);
      }
    } finally {
      if (!checkoutOpened) setPayingFlightId(null);
    }
  };

  const handlePurgeBookingLog = async (booking) => {
    try {
      if (booking.source === "hotel") await deleteHotelBooking(booking.id, token);
      else if (booking.source === "flight") await deleteFlightBooking(booking.id, token);
      else if (booking.source === "holiday") await deleteHolidayBooking(booking.id, token);
      else if (booking.source === "bus") await deleteBusBooking(booking.id, token);
      else if (booking.source === "train") await deleteTrainBooking(booking.id, token);
      else if (booking.source === "activity") await deleteActivityBooking(booking.id, token);
      else await deleteBooking(booking.id, token);
      synchronizePipeline();
    } catch (err) {
      alert(`Purge execution failed: ${err.message}`);
    }
  };

  const handleBookingStatusMutation = async (booking, nextStatus) => {
    try {
      if (booking.source === "hotel") await patchHotelBookingStatus(booking.id, nextStatus, token);
      else if (booking.source === "flight") await patchFlightBookingStatus(booking.id, nextStatus, token);
      else if (booking.source === "holiday") await patchHolidayBookingStatus(booking.id, nextStatus, token);
      else if (booking.source === "bus") await patchBusBookingStatus(booking.id, nextStatus, token);
      else if (booking.source === "train") await patchTrainBookingStatus(booking.id, nextStatus, token);
      else if (booking.source === "activity") await patchActivityBookingStatus(booking.id, nextStatus, token);
      else await patchBookingStatus(booking.id, nextStatus, token);
      synchronizePipeline();
    } catch (err) {
      alert(`Status state transition failure: ${err.message}`);
    }
  };

  const handleDropWishlistElement = async (itemId) => {
    setRemovingWishlistId(itemId);
    try {
      await deleteWishlistItem(itemId, token);
      setWishlistItems(prev => prev.filter(i => i.id !== itemId));
    } catch (err) {
      alert(`Wishlist transaction faulted: ${err.message}`);
    } finally {
      setRemovingWishlistId(null);
    }
  };
  const handleBookWishlistItem = (item) => {
    navigate(`/hotels?destination=${encodeURIComponent(item.destination)}`);
  };
  const handleDropVisaRecord = async (visaId) => {
    try {
      await deleteVisa(visaId, token);
      setVisaApplications(prev => prev.filter(v => (v.application_id || v.id) !== visaId));
    } catch (err) {
      alert(`Visa mutation layer dropped: ${err.message}`);
    }
  };

  const switchTab = (tabName) => {
    setSearchParams({ tab: tabName });
    setMobileDrawerOpen(false);
  };

  if (globalLoading) return <div className="mbt-frame-placeholder"><MbtSpinner /></div>;
  if (globalError) return <div className="mbt-frame-placeholder"><MbtError message={globalError} onRetry={synchronizePipeline} /></div>;

  const profile = userProfile || flattenUser({});
  const tierCfg = getTierConfig(profile.tier);
  const tierProgressPct = Math.min(100, Math.max(0, ((profile.rewardPoints - tierCfg.currentFloor) / (tierCfg.ptsNeeded - tierCfg.currentFloor)) * 100)) || 0;

  // Derive Analytical Intelligence layers
  const upcomingCount = allBookings.filter(b => String(b.status).toLowerCase() === "upcoming").length;
  const completedCount = allBookings.filter(b => String(b.status).toLowerCase() === "completed").length;
  const cancelledCount = allBookings.filter(b => String(b.status).toLowerCase() === "cancelled").length;
  const aggregateSpend = allBookings.reduce((sum, b) => sum + Number(b.total_price || 0), 0);
  const flightAirlineOptions = [...new Set(flightBookings.map(b => b.airline).filter(Boolean))];

  const filteredFlightBookings = flightBookings.filter(b => {
    if (flightAirlineFilter !== "all" && b.airline !== flightAirlineFilter) return false;

    if (flightDateFrom && (!b.travel_date || b.travel_date < flightDateFrom)) return false;
    if (flightDateTo && (!b.travel_date || b.travel_date > flightDateTo)) return false;

    if (flightSearchText.trim()) {
      const q = flightSearchText.trim().toLowerCase();
      const haystack = [
        b.depart_code, b.arrive_code, b.airline, b.flight_numbers,
        b.booking_reference, b.airline_pnr,
        airportCache[b.depart_code]?.city, airportCache[b.arrive_code]?.city,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!haystack.includes(q)) return false;
    }

    return true;
  });
  return (
    <div className={`mbt-dashboard-wrapper ${isDarkMode ? "mbt-dark-mode" : ""}`}>

      {/* SIDEBAR NAVIGATION ENGINE */}
      <aside className={`mbt-sidebar ${sidebarCollapsed ? "collapsed" : ""} ${mobileDrawerOpen ? "mobile-open" : ""}`}>
        <div className="mbt-sidebar-brand">
          <button className="mbt-sidebar-toggle-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
            {sidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>

        <nav className="mbt-sidebar-nav">
          <button onClick={() => switchTab("overview")} className={`mbt-nav-link ${currentTab === "overview" ? "active" : ""}`}>
            <LayoutDashboard size={18} /> {!sidebarCollapsed && <span>Overview</span>}
          </button>
          <button onClick={() => switchTab("profile")} className={`mbt-nav-link ${currentTab === "profile" ? "active" : ""}`}>
            <User size={18} /> {!sidebarCollapsed && <span>Profile</span>}
          </button>

          <div className="mbt-nav-divider">{!sidebarCollapsed && "Booking Contexts"}</div>
          <button onClick={() => switchTab("hotels")} className={`mbt-nav-link ${currentTab === "hotels" ? "active" : ""}`}>
            <Luggage size={18} /> {!sidebarCollapsed && <span>Hotels</span>}
          </button>
          <button onClick={() => switchTab("flights")} className={`mbt-nav-link ${currentTab === "flights" ? "active" : ""}`}>
            <Plane size={18} /> {!sidebarCollapsed && <span>Flights</span>}
          </button>
          <button onClick={() => switchTab("bus")} className={`mbt-nav-link ${currentTab === "bus" ? "active" : ""}`}>
            <Armchair size={18} /> {!sidebarCollapsed && <span>Bus</span>}
          </button>
          {ENABLE_TRAINS && (
          <button onClick={() => switchTab("train")} className={`mbt-nav-link ${currentTab === "train" ? "active" : ""}`}>
            <Train size={18} /> {!sidebarCollapsed && <span>Trains</span>}
          </button>
          )}
          <button onClick={() => switchTab("activities")} className={`mbt-nav-link ${currentTab === "activities" ? "active" : ""}`}>
            <Backpack size={18} /> {!sidebarCollapsed && <span>Activities</span>}
          </button>
          <button onClick={() => switchTab("holiday")} className={`mbt-nav-link ${currentTab === "holiday" ? "active" : ""}`}>
            <MapPinned size={18} /> {!sidebarCollapsed && <span>Holidays</span>}
          </button>

          <div className="mbt-nav-divider">{!sidebarCollapsed && "Assets & Modules"}</div>
          <button onClick={() => switchTab("visa")} className={`mbt-nav-link ${currentTab === "visa" ? "active" : ""}`}>
            <IdCard size={18} /> {!sidebarCollapsed && <span>Visa Bureau</span>}
          </button>
          <button onClick={() => switchTab("wishlist")} className={`mbt-nav-link ${currentTab === "wishlist" ? "active" : ""}`}>
            <Heart size={18} /> {!sidebarCollapsed && <span>Wishlist</span>}
          </button>

          <button
            onClick={async () => {
              try {
                if (logout) await logout();
              } finally {
                navigate("/");
              }
            }}
            className="mbt-nav-link mbt-logout-trigger"
          >
            <LogOut size={18} /> {!sidebarCollapsed && <span>LOG OUT </span>}
          </button>
        </nav>
      </aside>

      {/* VIEWPORT CONTROLLER CONTENT AREA */}
      <div className="mbt-workspace-viewport">

        {/* PREMIUM TOP NAVBAR */}
        <header className="mbt-top-navbar">
          <div className="mbt-navbar-left">
            <button className="mbt-mobile-menu-hamburger" onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}>
              <Menu size={20} />
            </button>
            <div className="mbt-search-bar-container">
            </div>
          </div>

        </header>

        {/* CONTAINER ROUTING CONTEXT PANELS */}
        <main className="mbt-scrollable-content">

          {/* TAB: OVERVIEW */}
          {currentTab === "overview" && (
            <div className="mbt-section-fade">
              <div className="mbt-welcome-hero-card">
                <div className="mbt-hero-text-block">
                  <h1>Welcome Back, {profile.name || "Explorer"}</h1>
                </div>
              </div>

              {/* CORE PERFORMANCE ANALYTICS MATRICES */}
              <div className="mbt-analytics-grid">
                <div className="mbt-analytics-card">
                  <div className="mbt-card-metric-label">Total Documented Bookings</div>
                  <div className="mbt-card-metric-value">{allBookings.length}</div>
                  <div className="mbt-card-metric-footer">Across all integrated endpoint APIs</div>
                </div>
                <div className="mbt-analytics-card" style={{ borderColor: "#bfdbfe" }}>
                  <div className="mbt-card-metric-label">Upcoming Routes</div>
                  <div className="mbt-card-metric-value" style={{ color: "#2563eb" }}>{upcomingCount}</div>
                  <div className="mbt-card-metric-footer">Active live window logs</div>
                </div>
                <div className="mbt-analytics-card" style={{ borderColor: "#bbf7d0" }}>
                  <div className="mbt-card-metric-label">Completed Journeys</div>
                  <div className="mbt-card-metric-value" style={{ color: "#16a34a" }}>{completedCount}</div>
                  <div className="mbt-card-metric-footer">Archived pipeline logs</div>
                </div>
                <div className="mbt-analytics-card">
                  <div className="mbt-card-metric-label">Financial Microspend Aggregation</div>
                  <div className="mbt-card-metric-value">₹{formatPrice(aggregateSpend)}</div>
                  <div className="mbt-card-metric-footer">Total calculated valuation</div>
                </div>
              </div>

              {/* TWO COLUMN SUB-STRUCTURE */}
              <div className="mbt-dashboard-split-row">
                <div className="mbt-split-main-column">
                  <h3 className="mbt-content-subheading">Immediate System Activity Queue</h3>
                  {allBookings.length === 0 ? (
                    <div className="mbt-empty-glass">No recent server logs resolved.</div>
                  ) : (
                    <div className="mbt-bookings-list">
                      {allBookings.slice(0, 3).map(b => (
                        b.source === "hotel" ? (
                          <HotelBookingCard
                            key={`${b.source}-${b.id}`}
                            booking={b}
                            onDownloadReceipt={handleDownloadTicketReceipt}
                            downloadingId={downloadingId}
                            onPayHold={handleContinueHotelHoldPayment}
                            onReleaseHold={handleReleaseHotelHold}
                            payingId={payingHotelId}
                            actionId={hotelHoldActionId}
                          />
                        ) : (
                          <BookingCard key={`${b.source}-${b.id}`} booking={b} onDelete={handlePurgeBookingLog} onStatusChange={handleBookingStatusMutation} onDownloadReceipt={handleDownloadTicketReceipt} downloadingId={downloadingId} />
                        )
                      ))}
                    </div>
                  )}
                </div>

                <div className="mbt-split-side-column">
                  {/* <div className="mbt-glass-widget-card">
                    <h4>Membership Tier Lifecycle</h4>
                    <div className="mbt-progress-meta-row">
                      <span>Progression Matrix</span>
                      <span>{profile.rewardPoints} / {tierCfg.ptsNeeded || profile.rewardPoints} Pts</span>
                    </div> */}
                  {/* <div className="mbt-progress-bar-track">
                      <div className="mbt-progress-bar-fill" style={{ width: `${tierProgressPct}%` }} />
                    </div>
                    {tierCfg.next && <p className="mbt-widget-tip-text">Accumulate {tierCfg.ptsNeeded - profile.rewardPoints} additional points to unlock {tierCfg.next} perks.</p>}
                  </div> */}

                  {/* <div className="mbt-glass-widget-card" style={{ marginTop: 16 }}>
                    <h4>Financial Security Overview</h4>
                    <div className="mbt-widget-financial-row">
                      <div>
                        <span>Net Savings Metrics</span>
                        <strong>₹{formatPrice(profile.totalSavings)}</strong>
                      </div>
                      <Award size={24} style={{ color: "#16a34a", opacity: 0.8 }} />
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          )}

          {/* TAB: PROFILE MODULE */}
          {currentTab === "profile" && (() => {
            // Travel-readiness calc — grouped by what each set of fields unlocks
            const personal = { name: profile.name, email: profile.email, phone: profile.phone, dob: profile.dob, nationality: profile.nationality, address: profile.address };
            const documents = { passportNumber: profile.passportNumber, passportExpiry: profile.passportExpiry };
            const emergency = { emergencyName: profile.emergencyName, emergencyRelation: profile.emergencyRelation, emergencyPhone: profile.emergencyPhone };

            const groupStatus = (group) => {
              const values = Object.values(group);
              const filled = values.filter(Boolean).length;
              return { filled, total: values.length, complete: filled === values.length };
            };

            const personalStatus = groupStatus(personal);
            const docsStatus = groupStatus(documents);
            const emergencyStatus = groupStatus(emergency);

            const totalFilled = personalStatus.filled + docsStatus.filled + emergencyStatus.filled;
            const totalFields = personalStatus.total + docsStatus.total + emergencyStatus.total;
            const readinessPct = Math.round((totalFilled / totalFields) * 100);

            return (
              <div className="mbt-section-fade">
                <h2 className="mbt-section-heading">Your Profile <span className="mbt-heading-line" /></h2>

                <div className="mbt-profile-grid-architecture">
                  <div className="mbt-profile-avatar-mainframe-card">
                    <div className="mbt-avatar-interactive-wrapper">
                      <div className="mbt-avatar-glow-ring" />
                      {profile.avatar ? (
                        <img src={resolveAvatarUrl(profile.avatar)} alt={profile.name} className="mbt-big-avatar-img" />
                      ) : (
                        <div className="mbt-big-avatar-placeholder">{String(profile.name || "U").charAt(0).toUpperCase()}</div>
                      )}
                      <label className="mbt-avatar-upload-overlay">
                        <Camera size={18} />
                        <input type="file" accept="image/*" onChange={handleAvatarMutation} style={{ display: "none" }} />
                      </label>
                    </div>
                    <h3>{profile.name || "Anonymous User"}</h3>
                    <p>{profile.email}</p>
                    {profile.avatar && <button onClick={handleAvatarRemoval} className="mbt-avatar-purge-btn">Remove Avatar</button>}

                    <div className="mbt-profile-readiness">
                      <div className="mbt-profile-readiness-label">
                        <span>Travel Readiness</span>
                        <strong>{readinessPct}%</strong>
                      </div>
                      <div className="mbt-profile-readiness-track">
                        <div
                          className="mbt-profile-readiness-fill"
                          style={{ width: `${readinessPct}%` }}
                        />
                      </div>
                      <p className="mbt-profile-readiness-hint">
                        {readinessPct === 100
                          ? "Your profile is complete — bookings will pre-fill automatically."
                          : `${totalFields - totalFilled} field${totalFields - totalFilled === 1 ? "" : "s"} left to speed up checkout.`}
                      </p>
                    </div>
                  </div>

                  <div className="mbt-profile-fields-glass-card">
                    <div className="mbt-profile-group-head">
                      <div className="mbt-profile-group-title"><User size={15} /> Personal Details</div>
                      <span className={`mbt-profile-group-status ${personalStatus.complete ? "complete" : "incomplete"}`}>
                        {personalStatus.complete ? "Complete" : `${personalStatus.filled}/${personalStatus.total}`}
                      </span>
                    </div>
                    <div className="mbt-fields-vertical-stack">
                      <ProfileRow label="Full Name" value={profile.name} fieldKey="name" onSave={handleUpdateProfileField} />
                      <ProfileRow label="Email" value={profile.email} fieldKey="email" onSave={handleUpdateProfileField} />
                      <ProfileRow label="Phone Number" value={profile.phone} fieldKey="phone" onSave={handleUpdateProfileField} />
                      <ProfileRow label="Date of Birth" value={profile.dob} fieldKey="dob" inputType="date" onSave={handleUpdateProfileField} />
                      <ProfileRow label="Nationality" value={profile.nationality} fieldKey="nationality" onSave={handleUpdateProfileField} />
                      <ProfileRow label="Address" value={profile.address} fieldKey="address" onSave={handleUpdateProfileField} />
                    </div>

                    <div className="mbt-profile-group-head" style={{ marginTop: 26 }}>
                      <div className="mbt-profile-group-title"><FileCheck size={15} /> Travel Documents</div>
                      <span className={`mbt-profile-group-status ${docsStatus.complete ? "complete" : "incomplete"}`}>
                        {docsStatus.complete ? "Complete" : `${docsStatus.filled}/${docsStatus.total}`}
                      </span>
                    </div>
                    <p className="mbt-profile-group-sub">Used to pre-fill passenger details when you book flights or apply for a visa.</p>
                    <div className="mbt-fields-vertical-stack">
                      <ProfileRow label="Passport Number" value={profile.passportNumber} fieldKey="passportNumber" onSave={handleUpdateProfileField} />
                      <ProfileRow label="Passport Expiry" value={profile.passportExpiry} fieldKey="passportExpiry" inputType="date" onSave={handleUpdateProfileField} />
                    </div>

                    <div className="mbt-profile-group-head" style={{ marginTop: 26 }}>
                      <div className="mbt-profile-group-title"><ShieldCheck size={15} /> Emergency Contact</div>
                      <span className={`mbt-profile-group-status ${emergencyStatus.complete ? "complete" : "incomplete"}`}>
                        {emergencyStatus.complete ? "Complete" : `${emergencyStatus.filled}/${emergencyStatus.total}`}
                      </span>
                    </div>
                    <p className="mbt-profile-group-sub">Who we should reach if something goes wrong during your trip.</p>
                    <div className="mbt-fields-vertical-stack">
                      <ProfileRow label="Contact Name" value={profile.emergencyName} fieldKey="emergencyName" onSave={handleUpdateProfileField} />
                      <ProfileRow label="Relationship" value={profile.emergencyRelation} fieldKey="emergencyRelation" onSave={handleUpdateProfileField} />
                      <ProfileRow label="Phone Number" value={profile.emergencyPhone} fieldKey="emergencyPhone" onSave={handleUpdateProfileField} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* TAB: HOTELS */}
          {currentTab === "hotels" && (
            <BookingCategorySection
              title="Hotel Booking" icon={<Luggage size={36} />}
              bookings={hotelBookings} filter={hotelFilter} setFilter={setHotelFilter}
              emptyTitle="No Accommodations Synced"
              emptyText="No active hotel bookings discovered matching existing API query frames."
              renderCard={(b) => (
                <HotelBookingCard
                  key={`hotel-${b.id}`}
                  booking={b}
                  onDownloadReceipt={handleDownloadTicketReceipt}
                  downloadingId={downloadingId}
                  onPayHold={handleContinueHotelHoldPayment}
                  onReleaseHold={handleReleaseHotelHold}
                  payingId={payingHotelId}
                  actionId={hotelHoldActionId}
                />
              )}
            />
          )}

          {/* TAB: FLIGHTS */}
          {currentTab === "flights" && (
            <>
              {flightPaymentMessage && (
                <div className="mbt-empty" style={{ marginBottom: 16 }}>
                  <div
                    className="mbt-empty-icon"
                    style={{ color: "#d97706" }}
                  >
                    <AlertTriangle size={28} />
                  </div>

                  <h3 className="mbt-empty-title">
                    Payment successful
                  </h3>

                  <p className="mbt-empty-text">
                    {flightPaymentMessage}
                  </p>
                </div>
              )}

              <BookingCategorySection
                title="Flights Booking"
                icon={<Plane size={36} />}
                bookings={flightBookings}
                filter={flightFilter}
                setFilter={setFlightFilter}
                emptyTitle="No Aviation Vectors Configured"
                emptyText="No active flight vectors compiled within the user record database."
                renderCard={(b) =>
  b.isFlightCancellationRecord ? (
    <FlightCancellationCard
      key={b.id}
      booking={b}
      airportCache={airportCache}
    />
  ) : (
    <FlightBookingCard
      key={`flight-${b.id}`}
      booking={b}
      airportCache={airportCache}
      onStatusChange={
        handleBookingStatusMutation
      }
      onDownloadReceipt={
        handleDownloadTicketReceipt
      }
      onPayFlight={
        handleInitiateFlightPaymentGateway
      }
      onRefresh={
        synchronizePipeline
      }
      downloadingId={
        downloadingId
      }
      payingFlightId={
        payingFlightId
      }
    />
  )
}
              />
            </>
          )}

          {/* TAB: BUS ROUTES */}
          {currentTab === "bus" && (
            <BookingCategorySection
              title="Bus Booking" icon={<Armchair size={36} />}
              bookings={busBookings} filter={busFilter} setFilter={setBusFilter}
              emptyTitle="No Bus Logistics Resolved"
              emptyText="No ground transit bookings populated inside the database pipeline configuration."
              renderCard={(b) =>
                b.isCancellationRecord ? (
                  <BusCancellationCard
                    key={`bus-cancel-${b.cancellation_id}`}
                    booking={b}
                  />
                ) : (
                  <BusBookingCard
                    key={`bus-${b.id}`}
                    booking={b}
                    onDownloadReceipt={
                      handleDownloadTicketReceipt
                    }
                    downloadingId={downloadingId}
                  />
                )
              }
            />
          )}

          {/* TAB: TRAINS */}
          {currentTab === "train" && (
            <BookingCategorySection title="Train Booking" icon={<Train size={36} />} bookings={trainBookings} filter={trainFilter} setFilter={setTrainFilter} onDelete={handlePurgeBookingLog} onStatusChange={handleBookingStatusMutation} onDownloadReceipt={handleDownloadTicketReceipt} downloadingId={downloadingId} emptyTitle="No Rail Journeys Synced" emptyText="No active train bookings discovered matching existing API query frames." />
          )}

          {/* TAB: ACTIVITIES */}
          {currentTab === "activities" && (
            <BookingCategorySection title="Experiences & Activities" icon={<Backpack size={36} />} bookings={activityBookings} filter={activityFilter} setFilter={setActivityFilter} onDelete={handlePurgeBookingLog} onStatusChange={handleBookingStatusMutation} onDownloadReceipt={handleDownloadTicketReceipt} downloadingId={downloadingId} emptyTitle="No Activities Booked" emptyText="No tours, experiences, or activity vouchers linked to this account yet." />
          )}

          {/* TAB: HOLIDAYS */}
          {currentTab === "holiday" && (
            <BookingCategorySection title="Holiday Packages & Curations" icon={<MapPinned size={36} />} bookings={holidayBookings} filter={holidayFilter} setFilter={setHolidayFilter} onDelete={handlePurgeBookingLog} onStatusChange={handleBookingStatusMutation} emptyTitle="No Holiday Modules Initialized" emptyText="No customized vacation packages have been associated with this account structure." />
          )}

          {/* TAB: VISA BUREAU */}
          {currentTab === "visa" && (
            <div className="mbt-section-fade">
              <h2 className="mbt-section-heading">Visa Bureau <span className="mbt-heading-line" /></h2>
              {/* <p className="mbt-cat-sub">Consular clearance and e-Visa entry state application tracking.</p> */}
              {visaApplications.length === 0 ? (
                <div className="mbt-empty-glass"><IdCard size={36} /><h3 className="mbt-empty-title">No Active Visa Processes</h3></div>
              ) : (
                <div className="mbt-visa-flex-matrix">
                  {visaApplications.map(v => <VisaCard key={v.id || v.application_id} visa={v} onDelete={handleDropVisaRecord} />)}
                </div>
              )}
            </div>
          )}

          {/* TAB: WISHLIST */}
          {currentTab === "wishlist" && (
            <div className="mbt-section-fade">
              <h2 className="mbt-section-heading">Wishlist<span className="mbt-heading-line" /></h2>
              {/* <p className="mbt-cat-sub">Curated destinations linked to server-side notification hooks.</p> */}
              {wishlistItems.length === 0 ? (
                <div className="mbt-empty-glass"><Heart size={36} /><h3 className="mbt-empty-title">Wishlist Pipeline Disengaged</h3></div>
              ) : (
                <div className="mbt-wishlist-flex-matrix">
                  {wishlistItems.map(item => <WishlistCard key={item.id} item={item} onRemove={handleDropWishlistElement} onBookNow={handleBookWishlistItem} removingId={removingWishlistId} />)}
                </div>
              )}
            </div>
          )}

        </main>
      </div>
    </div>
  );
}
function FlightBookingCard({ booking, airportCache, onStatusChange, onDownloadReceipt, onPayFlight, onRefresh, downloadingId, payingFlightId }) {
  const { token } = useAuth();
  const passengers = safeJsonParse(
    booking.passenger_details,
    []
  );

  const legPassengerCounts =
  Array.isArray(
    booking.legPassengerCounts
  )
    ? booking.legPassengerCounts
    : [];

const hasPartialCancellation =
  booking.hasPartialCancellation ===
  true;

  const isInfantPassenger = (passenger = {}) => {
    const text = [
      passenger.paxType,
      passenger.pax_type,
      passenger.Pax_type,
      passenger.type,
      passenger.passengerType,
      passenger.passenger_type,
    ]
      .filter(
        (value) =>
          value !== null &&
          value !== undefined &&
          value !== ""
      )
      .join(" ")
      .toLowerCase();

    return (
      text === "2" ||
      text.includes("infant") ||
      text.includes("inf")
    );
  };

  const postSsrSelectablePassengers =
    passengers.filter(
      (passenger) =>
        !isInfantPassenger(passenger)
    );
  const [menuOpen, setMenuOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const [releaseError, setReleaseError] = useState(""); // "view" | "print" | "email" | "invoice" | null
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({ requestedDate: "", requestedTime: "", remarks: "" });
  const [cancelOpen, setCancelOpen] = useState(false);
  const [postSsrOpen, setPostSsrOpen] = useState(false);
  const [postSsrPanel, setPostSsrPanel] = useState(null);
  // null | "meal" | "baggage" | "seat"

  const [postSsrLoading, setPostSsrLoading] = useState(false);
  const [postSsrOptions, setPostSsrOptions] = useState(null);
  const [postSsrError, setPostSsrError] = useState("");
  const [postSsrSuccess, setPostSsrSuccess] = useState("");
  const [postSsrPassenger, setPostSsrPassenger] = useState("");
  const [selectedPostSsrLeg, setSelectedPostSsrLeg] = useState(0);
  const postSsrPaymentLockRef = useRef(false);

  const [passengerDropdownOpen, setPassengerDropdownOpen] = useState(false);
  const [flightLegDropdownOpen, setFlightLegDropdownOpen] = useState(false);

  const [postSsrSelectionsByContext, setPostSsrSelectionsByContext] =
    useState({});

  const postSsrContextKey =
    `${selectedPostSsrLeg}:${postSsrPassenger || "1"}`;

  const emptyPostSsrSelections = {
    Meals: [],
    Baggage: [],
    Extras: [],
    Seats: [],
  };

  const postSsrSelections =
    postSsrSelectionsByContext[postSsrContextKey] ||
    emptyPostSsrSelections;

  const getPostSsrCartRows = () => {
  const rows = Object.entries(postSsrSelectionsByContext)
    .flatMap(([key, selections]) => {
      const [legIndexRaw, paxIdRaw] = key.split(":");
      const itineraryLegIndex = Number(legIndexRaw);
      const paxId = Number(paxIdRaw || 1);

      const buildRows = (items, type) =>
        (items || []).map((item) => {
          const passenger = passengers[paxId - 1];

          return {
            item,
            type,
            paxId,
            passengerName: passenger
              ? formatPassengerName(passenger)
              : `Passenger ${paxId}`,

            passengerType:
              passenger?.paxType ??
              passenger?.pax_type ??
              passenger?.Pax_type ??
              passenger?.type ??
              passenger?.passengerType ??
              null,

            itineraryLegIndex,

            amount: Number(
              item.totalAmount ??
              item.amount ??
              0
            ),

            key: [
              booking.id,
              itineraryLegIndex,
              item.segmentId ??
              item.supplierLegIndex ??
              item.__groupKey ??
              "journey",
              paxId,
              type,
              item.ssrKey,
              item.seatCode || "",
            ].join("|"),
          };
        });

      return [
        ...buildRows(selections.Meals, "meal"),
        ...buildRows(selections.Baggage, "baggage"),
        ...buildRows(selections.Extras, "extra"),
        ...buildRows(selections.Seats, "seat"),
      ];
    })
    .filter((row) => row.item?.ssrKey);

  // One journey-wide wheelchair is one purchase.
  const seenSupplierSelections = new Set();

  return rows.filter((row) => {
    const ssrKey = String(row?.item?.ssrKey || "").trim();

    if (!ssrKey) return true;

    const isWheelchair =
      String(
        row?.item?.ssrTypeName ||
        row?.item?.ssrTypeDesc ||
        row?.item?.type ||
        row?.type ||
        ""
      )
        .trim()
        .toLowerCase()
        .includes("wheelchair");

    if (!isWheelchair) {
  return true;
}

// Wheelchair is one purchase per passenger + outer itinerary leg.
// Do not use segmentWise here because Codemagen may return
// wheelchair SSRs with Segment_Wise=true on every physical segment.
const wheelchairName = String(
  row?.item?.ssrTypeDesc ||
  row?.item?.ssrTypeName ||
  row?.item?.name ||
  ""
)
  .trim()
  .toLowerCase()
  .replace(/\s+/g, " ");

const supplierSelectionKey = [
  Number(row.itineraryLegIndex || 0),
  Number(row.paxId || 0),
  "wheelchair",
  wheelchairName || ssrKey,
].join("::");

    if (seenSupplierSelections.has(supplierSelectionKey)) {
      return false;
    }

    seenSupplierSelections.add(supplierSelectionKey);
    return true;
  });
};

  const postSsrCartRows = getPostSsrCartRows();
  const pendingPostSsrTotal = postSsrCartRows.reduce(
    (sum, row) => sum + Number(row.amount || 0),
    0
  );
  const pendingPostSsrCount = postSsrCartRows.length;

  const setPostSsrSelections = (updater) => {
    // Previous supplier/payment error belongs
    // to the previous cart state.
    setPostSsrError("");

    setPostSsrSelectionsByContext((prev) => {
      const current =
        prev[postSsrContextKey] ||
        emptyPostSsrSelections;

      const next =
        typeof updater === "function"
          ? updater(current)
          : updater;

      return {
        ...prev,
        [postSsrContextKey]: next,
      };
    });
  };
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const isDownloading = downloadingId === booking.id;
  const isPaying = payingFlightId === booking.id;
  const getPortalRoot = () => {
    if (typeof document === "undefined") return null;

    let root = document.getElementById("modal-root");

    if (!root) {
      root = document.createElement("div");
      root.id = "modal-root";
      document.body.appendChild(root);
    }

    return root;
  };
  const portalRoot = getPortalRoot();


  useEffect(() => {
    if (!postSsrSuccess) return;

    const timer = setTimeout(() => {
      setPostSsrSuccess("");
    }, 6000);

    return () => clearTimeout(timer);
  }, [postSsrSuccess]);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    if (menuOpen) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menuOpen]);

  const isTicketed =
  booking.ticketing_status === "success" ||
  Number(booking.ticket_status_id) === 4;
  // Never show "Pay Now" once ticketing already reached a resolved state —
  // even if `status` briefly glitches, ticketing_status is the safer signal.
  const bookingStatus = String(booking.status || "")
    .trim()
    .toLowerCase();

    const isReleased = bookingStatus === "released";

  const ticketingStatus = String(booking.ticketing_status || "")
    .trim()
    .toLowerCase();

  const paymentStatus = String(booking.payment_status || "")
    .trim()
    .toLowerCase();

  const canPayFlight =
  booking.source === "flight" &&
  bookingStatus !== "released" &&
  !["success", "paid"].includes(paymentStatus) &&
  ticketingStatus !== "success" &&
  ["held", "pending_payment"].includes(bookingStatus);
  const cancellationRules = normalizeChargeRules(booking.cancellation_charges);
  const canCancel = canCancelBooking(booking);
  const paymentDone = ["success", "paid"].includes(String(booking.payment_status || "").toLowerCase())
  const isUnpaidHeldFlight =
    ["held", "pending_payment"].includes(String(booking.status || "").toLowerCase()) &&
    !["success", "paid"].includes(String(booking.payment_status || "").toLowerCase()) &&
    String(booking.ticketing_status || "").toLowerCase() !== "success";
  const rescheduleRules = normalizeChargeRules(booking.reschedule_charges);
  const rescheduleStatus = String(booking.reschedule_status || "").toLowerCase();
  const rescheduleAvailableFromSupplier = rescheduleRules.length > 0;
  const canRequestReschedule =
    isTicketed &&
    paymentDone &&
    rescheduleAvailableFromSupplier &&
    String(booking.status || "").toLowerCase() !== "cancelled" &&
    !["requested", "confirming"].includes(rescheduleStatus);

  const airlineCode = deriveAirlineCode(booking.flight_numbers, booking.airline);
  const logoUrl = airlineLogoUrl(airlineCode);
  const activePassengers = passengers.filter(
    (p) =>
      p.cancellationStatus !== "requested" &&
      p.cancellationStatus !== "cancelled"
  );

  const cancelledPassengers = passengers.filter(
    (p) =>
      p.cancellationStatus === "requested" ||
      p.cancellationStatus === "cancelled"
  );

  const handleReleaseHeldBooking = async () => {
    if (actionBusy) return;

    setActionBusy("release");
    setReleaseError("");

    try {
      const response = await fetch(
        `${API_BASE}/flight-ticketing/release-pnr`,
        {
          method: "POST",
          headers: authHeaders(token),
          body: JSON.stringify({
            bookingId: booking.id,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(
          data.errorInnerException ||
          data.errorDesc ||
          data.error ||
          data.message ||
          "Could not release this held booking."
        );
      }

      setReleaseError("");
onRefresh?.();

    } catch (err) {
      setReleaseError(
        err.message || "Could not release this held booking."
      );
    } finally {
      setActionBusy(null);
    }
  };
  const extras = safeJsonParse(booking.selected_extras, []);
  const seatDetails = safeJsonParse(booking.seat_details, []);
  const rawSeats = String(booking.selected_seats || "").trim();
  const seatCount =
    seatDetails.length ||
    rawSeats
      .split(",")
      .map((seat) => seat.trim())
      .filter(Boolean).length;

  const extrasCount = extras.reduce(
    (sum, item) => sum + Number(item.qty || 1),
    0
  );
  const postBookingAddonTotal = Number(
    booking.post_booking_addon_total || 0
  );

  const selectedPostSsrPaxId =
    Number(postSsrPassenger || 1);
  const itineraryLegsForDisplay = (() => {
    let legs = booking.itinerary_legs || [];
    if (typeof legs === "string") {
      try {
        legs = JSON.parse(legs);
      } catch {
        legs = [];
      }
    }
    return Array.isArray(legs) ? legs : [];
  })();
  const summarizeStoredFlightLeg = (leg = {}) => {
    const segments = Array.isArray(leg.segments) ? leg.segments : [];
    const first = segments[0] || {};
    const last = segments[segments.length - 1] || first;
    return {
      departCode: first.origin || first.Origin || leg.departCode || leg.depart_code || "",
      arriveCode: last.destination || last.Destination || leg.arriveCode || leg.arrive_code || "",
    };
  };
  const formatFlightAddonLabel = (item = {}, fallback = "Add-on") => {
    const isJourneyWide =
      item.displayJourneyWide === true ||
      item.segmentWise === false;
    const legIndex = Number(item.itineraryLegIndex ?? 0);
    const leg = itineraryLegsForDisplay[legIndex];
    const legSummary = summarizeStoredFlightLeg(leg);
    const outerRoute = [
      legSummary.departCode,
      legSummary.arriveCode,
    ].filter(Boolean).join(" → ");

    return [
      fallback,
      isJourneyWide ? [outerRoute, "Entire journey"].filter(Boolean).join(" · ") : item.route || null,
      isJourneyWide ? null : item.flightNo || item.flightNumbers || null,
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const postSsrItineraryLegs = safeJsonParse(
    booking.itinerary_legs,
    []
  );

  const selectedPostSsrOuterLeg =
    postSsrItineraryLegs[selectedPostSsrLeg] || null;

  const firstPostSsrValue = (...values) =>
    values.find(
      (value) =>
        value !== null &&
        value !== undefined &&
        String(value) !== ""
    );

  const getPostSsrSegmentIdentity = (segment = {}, segmentIndex = null) => ({
    segmentId: firstPostSsrValue(
      segment.segmentId,
      segment.Segment_Id,
      segment.SegmentId
    ),
    supplierLegIndex: firstPostSsrValue(
      segment.supplierLegIndex,
      segment.legIndex,
      segment.Leg_Index,
      segment.LegIndex,
      segmentIndex
    ),
    flightId: firstPostSsrValue(
      segment.flightId,
      segment.Flight_ID,
      segment.FlightID,
      segment.Flight_Id,
      segment.FlightId
    ),
    route: firstPostSsrValue(
      segment.route,
      segment.Route,
      segment.Sector,
      segment.origin && segment.destination
        ? `${segment.origin}-${segment.destination}`
        : null,
      segment.departCode && segment.arriveCode
        ? `${segment.departCode}-${segment.arriveCode}`
        : null
    ),
  });

  const getPostSsrSegment = (item) => {
    const segments = Array.isArray(selectedPostSsrOuterLeg?.segments)
      ? selectedPostSsrOuterLeg.segments
      : [];

    if (
      item?.segmentId !== null &&
      item?.segmentId !== undefined
    ) {
      const byId = segments.find(
        (segment, segmentIndex) =>
          String(
            getPostSsrSegmentIdentity(segment, segmentIndex).segmentId
          ) === String(item.segmentId)
      );

      if (byId) return byId;
    }

    const itemSupplierLegIndex = firstPostSsrValue(
  item?.supplierLegIndex,
  item?.legIndex
);

if (
  itemSupplierLegIndex !== null &&
  itemSupplierLegIndex !== undefined
) {
  const bySupplierLegIndex = segments.find(
    (segment, segmentIndex) =>
      String(
        getPostSsrSegmentIdentity(segment, segmentIndex).supplierLegIndex
      ) === String(itemSupplierLegIndex)
  );

  if (bySupplierLegIndex) return bySupplierLegIndex;
}

    const itemFlightId = item?.flightId;

    if (
      itemFlightId !== null &&
      itemFlightId !== undefined
    ) {
      const byFlightId = segments.find(
        (segment, segmentIndex) =>
          String(
            getPostSsrSegmentIdentity(segment, segmentIndex).flightId
          ) === String(itemFlightId)
      );

      if (byFlightId) return byFlightId;
    }

    return null;
  };

  const getPostSsrGroupKey = (
    item,
    allItems = []
  ) => {
    const trulySegmentWise =
      isPostSsrItemTrulySegmentWise(
        item,
        allItems
      );

    if (!trulySegmentWise) {
      return [
        selectedPostSsrLeg,
        "journey",
      ].join("::");
    }

    const segment = getPostSsrSegment(item);

    const segmentIdentity = segment
      ? getPostSsrSegmentIdentity(
        segment,
        Number(
          item?.legIndex ??
          item?.supplierLegIndex
        )
      )
      : {};

    return [
      selectedPostSsrLeg,
      "segment",
      item.segmentId ??
      segmentIdentity.segmentId ??
      item.legIndex ??
      item.supplierLegIndex ??
      item.flightId ??
      segmentIdentity.flightId ??
      segmentIdentity.route ??
      "unknown",
    ].join("::");
  };

  const getPostSsrItemKey = (
  item,
  allItems = []
) => {
  const groupKey = getPostSsrGroupKey(
    item,
    allItems
  );

  const typeText = [
    item?.ssrTypeName,
    item?.ssrTypeDesc,
    item?.ssrType,
    item?.type,
    item?.name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return [
    groupKey,
    item?.ssrKey || "unknown",
  ].join("::");
};

  const isPostSsrAlreadyAdded = (item) => {
    const stored =
      postSsrOptions?.alreadyAddedSsr || [];

    return stored.some((existing) => {
      const selectedPassengerId =
        Number(postSsrPassenger);

      const samePax =
        Number(existing.paxId) ===
        selectedPassengerId ||
        Number(existing.passengerIndex) + 1 ===
        selectedPassengerId ||
        (
          existing.paxId == null &&
          Number(existing.supplierPaxId) ===
          selectedPassengerId
        );

      const sameLeg =
        Number(
          existing.itineraryLegIndex ??
          existing.outerLegIndex ??
          existing.legIndex ??
          0
        ) === Number(selectedPostSsrLeg);

        const sameSegment =
  String(
    existing.segmentId ??
    existing.supplierLegIndex ??
    existing.flightId ??
    "journey"
  ) ===
  String(
    item.segmentId ??
    item.supplierLegIndex ??
    item.flightId ??
    "journey"
  );

      const normalizeText = (value) =>
        String(value || "").trim().toLowerCase().replace(/\s+/g, " ");

      const normalizeType = (value) => {
        const text = normalizeText(value);
        if (text === "0" || text.includes("baggage") || text.includes("bag")) return "baggage";
        if (text === "1" || text.includes("meal") || text.includes("food")) return "meal";
        if (text === "10" || text.includes("wheelchair")) return "wheelchair";
        if (text === "4" || text.includes("sport")) return "sports";
        if (text === "6" || text.includes("lounge")) return "lounge";
        if (text === "8" || text.includes("carry more")) return "carry-more";
        if (text === "9" || text.includes("fast forward")) return "fast-forward";
        return text || "extra";
      };

      const selectedType = normalizeType([
        item.type,
        item.ssrTypeName,
        item.ssrType,
        item.name,
        item.ssrTypeDesc,
      ].filter(Boolean).join(" "));

      const existingType = normalizeType([
        existing.type,
        existing.ssrTypeName,
        existing.ssrType,
        existing.name,
        existing.ssrTypeDesc,
      ].filter(Boolean).join(" "));

      const selectedName = normalizeText(
        item.name ||
        item.ssrTypeDesc ||
        item.ssrTypeName
      );

      const existingName = normalizeText(
        existing.name ||
        existing.ssrTypeDesc ||
        existing.ssrTypeName
      );

const sameSsr =
  (
    selectedType &&
    existingType &&
    selectedType === existingType &&
    selectedName &&
    existingName &&
    selectedName === existingName
  ) ||
  String(existing.ssrKey || "") ===
    String(item.ssrKey || "");

      return (
        samePax &&
        sameLeg &&
        sameSegment &&
        sameSsr
      );
    });
  };

  const getPostSsrGroupLabel = (
    item,
    allItems = []
  ) => {
    const trulySegmentWise =
      isPostSsrItemTrulySegmentWise(
        item,
        allItems
      );

    if (!trulySegmentWise) {
      const from =
        selectedPostSsrOuterLeg?.departCode ||
        selectedPostSsrOuterLeg?.depart_code;

      const to =
        selectedPostSsrOuterLeg?.arriveCode ||
        selectedPostSsrOuterLeg?.arrive_code;

      return from && to
        ? `${from} → ${to} · Entire journey`
        : "Entire journey";
    }

    const segment = getPostSsrSegment(item);

    if (!segment) {
      return `Segment ${Number(
        item?.legIndex ??
        item?.supplierLegIndex ??
        0
      ) + 1
        }`;
    }

    const origin =
      segment.origin ||
      segment.departCode ||
      segment.from;

    const destination =
      segment.destination ||
      segment.arriveCode ||
      segment.to;

    const flightNo =
      segment.flightNo ||
      segment.flightNumbers ||
      segment.flightNumber;

    return [
      origin && destination
        ? `${origin} → ${destination}`
        : null,
      flightNo,
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const isPostSsrItemTrulySegmentWise = (
  item,
  allItems = []
) => {
  const typeText = [
    item?.ssrTypeName,
    item?.ssrTypeDesc,
    item?.ssrType,
    item?.type,
    item?.name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  // Wheelchair is a journey-level service in our UI.
  // Ignore Codemagen Segment_Wise=true for wheelchair SSRs.
  if (typeText.includes("wheelchair")) {
    return false;
  }

  if (!item?.segmentWise) {
    return false;
  }

  const itinerarySegments = Array.isArray(
  selectedPostSsrOuterLeg?.segments
)
  ? selectedPostSsrOuterLeg.segments
  : [];

if (itinerarySegments.length <= 1) {
  return false;
}

  const ssrKey = String(item?.ssrKey || "").trim();

  if (!ssrKey) {
    return Boolean(item.segmentWise);
  }

  const matchingItems = (allItems || []).filter(
    (candidate) =>
      String(candidate?.ssrKey || "").trim() === ssrKey
  );

  const segmentIdentities = new Set(
    matchingItems.map((candidate) =>
      String(
        candidate?.segmentId ??
        candidate?.supplierLegIndex ??
        candidate?.legIndex ??
        candidate?.flightId ??
        "journey"
      )
    )
  );

  if (
    matchingItems.length > 1 &&
    segmentIdentities.size > 1
  ) {
    return false;
  }

  return true;
};

  const groupPostSsrItems = (items = []) => {
    const groups = new Map();

    items.forEach((item) => {
      const key = getPostSsrGroupKey(
        item,
        items
      );

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          label: getPostSsrGroupLabel(
            item,
            items
          ),
          items: [],
          seen: new Set(),
        });
      }

      const group = groups.get(key);

      const typeText = [
  item?.ssrTypeName,
  item?.ssrTypeDesc,
  item?.ssrType,
  item?.type,
  item?.name,
]
  .filter(Boolean)
  .join(" ")
  .toLowerCase();

const isWheelchair = typeText.includes("wheelchair");

const duplicateKey = isWheelchair
  ? [
      key,
      "wheelchair",
      String(
        item?.ssrTypeDesc ||
        item?.ssrTypeName ||
        item?.name ||
        ""
      )
        .trim()
        .toLowerCase(),
    ].join("::")
  : [
      key,
      item?.ssrKey || "",
      item?.ssrTypeDesc || item?.ssrTypeName || "",
      Number(
        item?.totalAmount ??
        item?.amount ??
        0
      ),
    ].join("::");

      if (!group.seen.has(duplicateKey)) {
        group.seen.add(duplicateKey);
        group.items.push(item);
      }
    });

    return Array.from(groups.values()).map(
      ({ seen, ...group }) => group
    );
  };

  const classifyPostSsrItem = (typeName, item = {}) => {
    const text = [
      typeName,
      item.ssrTypeName,
      item.ssrTypeDesc,
      item.code,
      item.ssrCode,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    if (/\bmeal\b|food|snack|breakfast|lunch|dinner/.test(text)) {
      return "meal";
    }

    if (/baggage|bag|kgs?\b|kilogram/.test(text)) {
      return "baggage";
    }

    if (/\bseat\b/.test(text)) {
      return "seat";
    }

    return "extra";
  };

  const getPostSsrExtraOptions = () =>
    Object.entries(postSsrOptions?.ssrByType || {})
      .filter(([typeName]) =>
        !["meal", "meals", "baggage", "bag", "seat", "seats"].includes(
          String(typeName || "").trim().toLowerCase()
        )
      )
      .flatMap(([typeName, items]) =>
        (Array.isArray(items) ? items : [])
          .filter((item) => classifyPostSsrItem(typeName, item) === "extra")
          .map((item) => ({
            ...item,
            ssrTypeName: item.ssrTypeName || typeName,
          }))
      );

  const enrichPostSsrItem = (item) => {
    const segment = getPostSsrSegment(item);

    return {
      ...item,

      itineraryLegIndex: selectedPostSsrLeg,

      supplierLegIndex:
        item?.supplierLegIndex ??
        item?.legIndex ??
        null,

      route: segment
        ? `${segment.origin || segment.departCode} → ${segment.destination || segment.arriveCode
        }`
        : null,

      origin:
        segment?.origin ||
        segment?.departCode ||
        null,

      destination:
        segment?.destination ||
        segment?.arriveCode ||
        null,

      flightNo:
        segment?.flightNo ||
        segment?.flightNumbers ||
        segment?.flightNumber ||
        null,
    };
  };

  const postSsrSeatMap = postSsrOptions
    ? normalizeSeatMap({ seatMaps: postSsrOptions.seatMaps }) || []
    : [];

  const groupPostSsrSeatRowsBySegment = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return [];

  const outerSegments = Array.isArray(
    selectedPostSsrOuterLeg?.segments
  )
    ? selectedPostSsrOuterLeg.segments
    : [];

  const isSingleSegmentJourney = outerSegments.length <= 1;

  const groups = [];

  rows.forEach((row) => {
    const firstSeat = row.seats?.[0] || {};

    const segmentKey = isSingleSegmentJourney
      ? "journey"
      : (
          row.segmentKey ||
          firstSeat.segmentKey ||
          [
            row.segmentId ?? firstSeat.segmentId,
            row.supplierLegIndex ?? firstSeat.supplierLegIndex,
            row.flightId ?? firstSeat.flightId,
            row.route ?? firstSeat.route,
          ]
            .filter(
              (value) =>
                value !== null &&
                value !== undefined &&
                String(value) !== ""
            )
            .join("::") ||
          "journey"
        );

    const meta = {
      segmentKey,
      segmentId:
        row.segmentId ??
        firstSeat.segmentId ??
        null,
      supplierLegIndex:
        row.supplierLegIndex ??
        firstSeat.supplierLegIndex ??
        null,
      flightId:
        row.flightId ??
        firstSeat.flightId ??
        null,
      origin:
        row.origin ||
        firstSeat.origin ||
        outerSegments[0]?.origin ||
        outerSegments[0]?.departCode ||
        null,
      destination:
        row.destination ||
        firstSeat.destination ||
        outerSegments[0]?.destination ||
        outerSegments[0]?.arriveCode ||
        null,
      route:
        row.route ||
        firstSeat.route ||
        (
          outerSegments[0]?.origin &&
          outerSegments[0]?.destination
            ? `${outerSegments[0].origin} → ${outerSegments[0].destination}`
            : null
        ),
      flightNo:
        row.flightNo ||
        firstSeat.flightNo ||
        outerSegments[0]?.flightNo ||
        outerSegments[0]?.flightNumber ||
        null,
    };

    const existing = groups.find(
      (group) => group.segmentKey === segmentKey
    );

    if (existing) {
      existing.rows.push(row);
    } else {
      groups.push({
        ...meta,
        rows: [row],
      });
    }
  });

  return groups;
};
  const postSsrSeatSegmentGroups =
    groupPostSsrSeatRowsBySegment(
      postSsrSeatMap
    );

  const normalizeSeatIdentityValue = (value) => {
    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ""
    ) {
      return null;
    }

    return String(value)
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "");
  };

  const getSavedSeatPaxId = (seat = {}) =>
    Number(
      seat.paxId ??
      seat.Pax_Id ??
      seat.passengerId ??
      0
    );

  const getSavedSeatOuterLegIndex = (seat = {}) =>
    Number(
      seat.itineraryLegIndex ??
      seat.itinerary_leg_index ??
      seat.outerLegIndex ??
      seat.legIndex ??
      0
    );

  const isSavedSeatForCurrentPassengerAndLeg = (
    seat
  ) => {
    return (
      getSavedSeatPaxId(seat) ===
      selectedPostSsrPaxId &&
      getSavedSeatOuterLegIndex(seat) ===
      Number(selectedPostSsrLeg)
    );
  };

  const doesSavedSeatMatchSeatSegment = (
    savedSeat,
    segment,
    segmentIndex
  ) => {
    if (
      !isSavedSeatForCurrentPassengerAndLeg(
        savedSeat
      )
    ) {
      return false;
    }

    /*
     * 1. Best match:
     * supplier Segment_Id
     */
    const savedSegmentId =
      normalizeSeatIdentityValue(
        savedSeat.segmentId ??
        savedSeat.Segment_Id ??
        savedSeat.segment_id
      );

    const currentSegmentId =
      normalizeSeatIdentityValue(
        segment?.segmentId
      );

    if (
      savedSegmentId &&
      currentSegmentId
    ) {
      return (
        savedSegmentId === currentSegmentId
      );
    }

    /*
     * 2. Supplier inner-leg/segment index
     */
    const savedSupplierLegIndex =
      savedSeat.supplierLegIndex ??
      savedSeat.supplier_leg_index ??
      savedSeat.segmentIndex ??
      savedSeat.segment_index;

    const currentSupplierLegIndex =
      segment?.supplierLegIndex ??
      segmentIndex;

    if (
      savedSupplierLegIndex !== null &&
      savedSupplierLegIndex !== undefined &&
      currentSupplierLegIndex !== null &&
      currentSupplierLegIndex !== undefined
    ) {
      return (
        Number(savedSupplierLegIndex) ===
        Number(currentSupplierLegIndex)
      );
    }

    /*
     * 3. Flight identity fallback
     */
    const savedFlightId =
      normalizeSeatIdentityValue(
        savedSeat.flightId ??
        savedSeat.Flight_ID ??
        savedSeat.flight_id
      );

    const currentFlightId =
      normalizeSeatIdentityValue(
        segment?.flightId
      );

    if (
      savedFlightId &&
      currentFlightId
    ) {
      return (
        savedFlightId === currentFlightId
      );
    }

    /*
     * 4. Route + flight number fallback
     */
    const savedRoute =
      normalizeSeatIdentityValue(
        savedSeat.route
      );

    const currentRoute =
      normalizeSeatIdentityValue(
        segment?.route ||
        (
          segment?.origin &&
            segment?.destination
            ? `${segment.origin}-${segment.destination}`
            : null
        )
      );

    const savedFlightNo =
      normalizeSeatIdentityValue(
        savedSeat.flightNo ??
        savedSeat.flightNumber
      );

    const currentFlightNo =
      normalizeSeatIdentityValue(
        segment?.flightNo
      );

    if (
      savedRoute &&
      currentRoute &&
      savedRoute === currentRoute
    ) {
      if (
        savedFlightNo &&
        currentFlightNo
      ) {
        return (
          savedFlightNo === currentFlightNo
        );
      }

      return true;
    }

    return false;
  };

  const isSeatSegmentAlreadyBooked = (
    segment,
    segmentIndex
  ) =>
    seatDetails.some((savedSeat) =>
      doesSavedSeatMatchSeatSegment(
        savedSeat,
        segment,
        segmentIndex
      )
    );

  const availablePostSsrSeatSegments =
    postSsrSeatSegmentGroups
      .map((segment, segmentIndex) => ({
        segment,
        segmentIndex,
      }))
      .filter(
        ({ segment, segmentIndex }) =>
          !isSeatSegmentAlreadyBooked(
            segment,
            segmentIndex
          )
      );

  const hasAvailablePostSsrSeatSegment =
    availablePostSsrSeatSegments.length > 0;

  const getPostSsrSeatGroupKey = (segment, index) => {
  const outerSegments = Array.isArray(
    selectedPostSsrOuterLeg?.segments
  )
    ? selectedPostSsrOuterLeg.segments
    : [];

  const isSingleSegmentJourney = outerSegments.length <= 1;

  return [
    selectedPostSsrLeg,
    "seat",
    isSingleSegmentJourney
      ? "journey"
      : (
          segment?.segmentKey ||
          segment?.segmentId ||
          segment?.supplierLegIndex ||
          segment?.flightId ||
          `idx-${index}`
        ),
  ].join("::");
};

  const getPostSsrSeatGroupLabel = (
    segment,
    index
  ) => {
    const outerSegments =
      Array.isArray(
        selectedPostSsrOuterLeg?.segments
      )
        ? selectedPostSsrOuterLeg.segments
        : [];

    const fallbackSegment =
      outerSegments[index] || null;

    const origin =
      segment?.origin ||
      fallbackSegment?.origin ||
      fallbackSegment?.departCode ||
      null;

    const destination =
      segment?.destination ||
      fallbackSegment?.destination ||
      fallbackSegment?.arriveCode ||
      null;

    const flightNo =
      segment?.flightNo ||
      fallbackSegment?.flightNo ||
      fallbackSegment?.flightNumber ||
      null;

    const route =
      segment?.route ||
      (
        origin && destination
          ? `${origin} → ${destination}`
          : null
      );

    return [
      route || `Segment ${index + 1}`,
      flightNo,
    ]
      .filter(Boolean)
      .join(" · ");
  };
  const hasPostSsrSelection =
    pendingPostSsrCount > 0;

  // ── Ticket action handlers ────────────────────────────────────────────
  const openBlobInNewTab = async (endpoint, { print = false } = {}) => {
    const win = window.open("", "_blank");
    if (!win) throw new Error("Pop-up blocked. Please allow pop-ups for this site and try again.");

    try {
      const res = await fetch(`${API_BASE}${endpoint}`, { headers: authHeaders(token) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Could not load the ticket.");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      win.location.href = url;
      if (print) {
        win.addEventListener("load", () => { try { win.print(); } catch (_) { } });
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      win.close();
      throw err;
    }
  };

  const handleView = async () => {
    setActionBusy("view");
    try { await openBlobInNewTab(`/flight-bookings/${booking.id}/eticket`); }
    catch (err) { alert(err.message); }
    finally { setActionBusy(null); }
  };

  const handlePrint = async () => {
    setActionBusy("print");
    try { await openBlobInNewTab(`/flight-bookings/${booking.id}/eticket`, { print: true }); }
    catch (err) { alert(err.message); }
    finally { setActionBusy(null); }
  };

  const handleDownloadInvoice = async () => {
    setActionBusy("invoice");
    try { await downloadFile(`${API_BASE}/flight-bookings/${booking.id}/invoice`, token, `invoice-${booking.booking_reference || booking.id}.pdf`); }
    catch (err) { alert(err.message); }
    finally { setActionBusy(null); }
  };

  const handleEmailTicket = () => {
    const subject = `Flight Ticket - ${booking.booking_reference || booking.airline_pnr || "Booking"}`;
    const lines = [
      "Flight Ticket",
      "",
      `Reference: ${booking.booking_reference || "-"}`,
      booking.airline_pnr ? `PNR: ${booking.airline_pnr}` : null,
      `Route: ${cityLabel(booking.depart_code, airportCache)} to ${cityLabel(booking.arrive_code, airportCache)}`,
      `Flight: ${booking.airline || ""} ${booking.flight_numbers || ""}`.trim(),
      `Date: ${formatDate(booking.travel_date)}`,
      passengers.length ? `Passengers: ${passengers.map(formatPassengerName).join(", ")}` : null,
      `Total: ₹${formatPrice(booking.total_price)}`,
    ].filter(Boolean).join("\n");

    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines)}`;
    window.location.href = mailtoUrl;
  };

  const handleRequestReschedule = async (event) => {
    event?.preventDefault?.();
    setActionBusy("reschedule");
    try {
      const res = await fetch(`${API_BASE}/flight-bookings/${booking.id}/request-reschedule`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify(rescheduleForm),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || "Could not submit the reschedule request.");
      setRescheduleOpen(false);
      setRescheduleForm({ requestedDate: "", requestedTime: "", remarks: "" });
      alert(data.message || "Reschedule request submitted.");
    } catch (err) { alert(err.message); }
    finally { setActionBusy(null); }
  };

  const handleWhatsappTicket = () => {
    const whatsappExtras =
      extras.length > 0
        ? extras
          .map((e) =>
            [
              formatFlightAddonLabel(e, e.name || "Add-on"),
              null,
              null,
              e.qty > 1 ? `x${e.qty}` : null,
            ]
              .filter(Boolean)
              .join(" · ")
          )
          .join("\n")
        : null;

    const lines = [
      "*Flight Ticket*",
      "",
      `*Reference:* ${booking.booking_reference || "-"}`,

      booking.airline_pnr
        ? `*PNR:* ${booking.airline_pnr}`
        : null,

      `*Route:* ${cityLabel(
        booking.depart_code,
        airportCache
      )} → ${cityLabel(
        booking.arrive_code,
        airportCache
      )}`,

      `*Flight:* ${booking.airline || ""} ${booking.flight_numbers || ""
        }`.trim(),

      `*Date:* ${formatDate(booking.travel_date)}`,

      passengers.length
        ? `*Passengers:* ${passengers
          .map(formatPassengerName)
          .join(", ")}`
        : null,

      whatsappExtras
        ? `*Extras:*\n${whatsappExtras}`
        : null,

      `*Total:* ₹${formatPrice(booking.total_price)}`,
    ]
      .filter(Boolean)
      .join("\n");

    window.open(
      `https://wa.me/?text=${encodeURIComponent(lines)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const loadPostSsrForLeg = async (legIndex) => {
    setPostSsrLoading(true);
    setPostSsrError("");
    setPostSsrOptions(null);

    try {
      const res = await fetch(`${API_BASE}/flight-ticketing/post-ssr`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({
          bookingId: booking.id,
          legIndex,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(
          data.error ||
          data.errorDesc ||
          "Could not load available add-ons."
        );
      }

      setPostSsrOptions(data);
    } catch (err) {
      setPostSsrError(err.message);
    } finally {
      setPostSsrLoading(false);
    }
  };

  const handleOpenPostSsr = async () => {
  console.log("========== MANAGE ADDONS CLICKED ==========", {
    bookingId: booking?.id,
  });

  setPostSsrOpen(true);
    setPostSsrPanel(null);

    setPostSsrError("");
    setPostSsrSuccess("");

    setPostSsrSelectionsByContext({});

    setSelectedPostSsrLeg(0);

    const firstSelectableIndex = passengers.findIndex(
      (passenger) => !isInfantPassenger(passenger)
    );

    if (firstSelectableIndex >= 0) {
      setPostSsrPassenger(String(firstSelectableIndex + 1));
    } else {
      setPostSsrPassenger("");
    }

    await loadPostSsrForLeg(0);
  };

  const handlePostSsrPayment = async () => {
    console.log("[POSTSSR UI CLICK]", {
      bookingId: booking.id,
      itemCount: getPostSsrCartRows().length,
    });

    if (postSsrPaymentLockRef.current || actionBusy === "postSsr") return;
    postSsrPaymentLockRef.current = true;
    setPostSsrError("");

    const cartRows = getPostSsrCartRows();

const ssrDetails = cartRows.map(
      ({ item, type, paxId, itineraryLegIndex }) => {
        const passenger = passengers[Number(paxId) - 1] || null;

        return {
          // Passenger ownership
          paxId: Number(paxId),
          passengerIndex: Number(paxId) - 1,

          passengerName: passenger
            ? formatPassengerName(passenger)
            : `Passenger ${paxId}`,

          passengerType:
            passenger?.paxType ??
            passenger?.pax_type ??
            passenger?.Pax_type ??
            passenger?.type ??
            passenger?.passengerType ??
            passenger?.passenger_type ??
            null,

          supplierPaxId:
            item.supplierPaxId ??
            item.Pax_Id ??
            item.PaxId ??
            null,

          // Supplier SSR
          ssrKey: item.ssrKey,
          type,

          name:
            type === "seat"
              ? `Seat ${item.seatCode || ""}`.trim()
              : item.ssrTypeDesc ||
              item.ssrTypeName ||
              (type === "baggage"
                ? "Extra Baggage"
                : type === "meal"
                  ? "Meal"
                  : "Extra"),

          seatCode: item.seatCode || null,

          amount: Number(
            item.totalAmount ??
            item.amount ??
            0
          ),

          // Outer itinerary
          itineraryLegIndex: Number(itineraryLegIndex),

          // Segment ownership
          segmentWise:
  String(
    item?.ssrTypeName ||
    item?.ssrTypeDesc ||
    item?.ssrType ||
    item?.type ||
    item?.name ||
    ""
  )
    .toLowerCase()
    .includes("wheelchair")
    ? false
    : Boolean(item.segmentWise),

          segmentId:
            item.segmentId ??
            null,

          supplierLegIndex:
            item.supplierLegIndex ??
            item.legIndex ??
            null,

          flightId:
            item.flightId ??
            null,

          route:
            item.route ||
            null,

          origin:
            item.origin ||
            null,

          destination:
            item.destination ||
            null,

          flightNo:
            item.flightNo ||
            null,
        };
      }
    );

    const payableAmount = cartRows.reduce(
      (sum, row) => sum + Number(row.amount || 0),
      0
    );

    if (!Number.isFinite(payableAmount) || payableAmount < 0) {
      alert("Selected add-ons have no valid payable amount.");
      postSsrPaymentLockRef.current = false;
      return;
    }

    const payerEmail =
      booking.passenger_email || "";

    const rawPhone =
      booking.passenger_phone || "";

    const payerPhone =
      String(rawPhone).replace(/\D/g, "").slice(-10) || "9999999999";

    if (!payerEmail) {
      alert("Email is required before payment.");
      postSsrPaymentLockRef.current = false;
      return;
    }

    setActionBusy("postSsr");

    let checkoutOpened = false;
    const cartLegIndexes = [
  ...new Set(
    ssrDetails.map((item) =>
      Number(item.itineraryLegIndex ?? item.legIndex ?? 0)
    )
  ),
].sort((a, b) => a - b);
    console.log("[POSTSSR FRONTEND INIT]", {
      bookingId: booking.id,
      legIndexes: cartLegIndexes,
      itemCount: ssrDetails.length,
    });

    try {

      
const confirmPostSsrAfterPayment = async (paymentResponse = {}) => {
  console.log("[USERPROFILE-19OCT-DIAGNOSTIC]", {
    marker: "LATEST USERPROFILE.JSX EXECUTED",
    bookingId: booking?.id,
    timestamp: new Date().toISOString(),
  });

  const paymentId =
    paymentResponse.easepayid ||
    paymentResponse.txnid ||
    (payableAmount === 0
      ? `FREE-POSTSSR-${booking.id}-${Date.now()}`
      : "");

  if (payableAmount > 0 && !paymentId) {
    throw new Error("Easebuzz payment reference is missing.");
  }

  const itineraryLegs = safeJsonParse(booking.itinerary_legs, []);
  const normalizedTripType = String(
  booking?.trip_type || booking?.tripType || ""
)
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, "_");

const itineraryLegsForTripType = Array.isArray(itineraryLegs)
  ? itineraryLegs
  : [];

const isSpecialRoundTrip =
  normalizedTripType === "special_round_trip" ||
  normalizedTripType === "special_roundtrip" ||
  itineraryLegsForTripType.some(
    (leg) =>
      String(leg?.supplierBookingMode || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, "-") === "special-round-trip"
  );

const isMultiCity =
  normalizedTripType === "multi_city" ||
  normalizedTripType === "multicity";

const isExplicitNormalRoundTrip = [
  "round_trip",
  "roundtrip",
  "return",
  "normal_round_trip",
].includes(normalizedTripType);

// Fallback for older bookings whose DB trip_type was incorrectly
// saved as one_way, but whose two itinerary legs are reverse routes.
const firstLeg = itineraryLegsForTripType[0];
const secondLeg = itineraryLegsForTripType[1];

const firstDepartCode = String(
  firstLeg?.departCode || ""
).trim().toUpperCase();

const firstArriveCode = String(
  firstLeg?.arriveCode || ""
).trim().toUpperCase();

const secondDepartCode = String(
  secondLeg?.departCode || ""
).trim().toUpperCase();

const secondArriveCode = String(
  secondLeg?.arriveCode || ""
).trim().toUpperCase();

const hasReverseTwoLegItinerary =
  itineraryLegsForTripType.length === 2 &&
  firstDepartCode !== "" &&
  firstArriveCode !== "" &&
  secondDepartCode !== "" &&
  secondArriveCode !== "" &&
  firstDepartCode === secondArriveCode &&
  firstArriveCode === secondDepartCode;

const isNormalRoundTrip =
  !isSpecialRoundTrip &&
  !isMultiCity &&
  (
    isExplicitNormalRoundTrip ||
    (
      ["", "one_way"].includes(normalizedTripType) &&
      hasReverseTwoLegItinerary
    )
  );

  console.log("[POSTSSR-19OCT-FLOW-MODE]", {
  bookingId: booking?.id,
  rawTripType: booking?.trip_type,
  rawTripTypeCamel: booking?.tripType,
  normalizedTripType,
  isNormalRoundTrip,
  cartLegIndexes,
  ssrLegIndexes: [
    ...new Set(
      ssrDetails.map((item) =>
        Number(item.itineraryLegIndex ?? item.legIndex ?? 0)
      )
    ),
  ],
  timestamp: new Date().toISOString(),
});

  // Normal Round Trip: finish one PNR completely before starting the next.
  if (isNormalRoundTrip) {
    const orderedLegIndexes = [...cartLegIndexes].sort(
      (a, b) => a - b
    );

    for (const legIndex of orderedLegIndexes) {
      const legSSRDetails = ssrDetails.filter(
        (item) =>
          Number(item.itineraryLegIndex ?? item.legIndex ?? 0) ===
          Number(legIndex)
      );

      if (!legSSRDetails.length) continue;

      console.log("[POSTSSR NORMAL RT LEG START]", {
        bookingId: booking.id,
        legIndex,
        itemCount: legSSRDetails.length,
      });

      

      // 2. Apply supplier payment for this PNR.
console.log("[POSTSSR-19OCT] PAYMENT START", {
  bookingId: booking.id,
  legIndex,
  legIndexes: [legIndex],
  timestamp: new Date().toISOString(),
});

const addPayment = await apiFetch(
  `${API_BASE}/flight-ticketing/add-payment`,
  {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({
      bookingId: booking.id,
      legIndex,
      legIndexes: [legIndex],
      transactionType: 1,
      clientRefNo: paymentId,
      gatewayResponse: paymentResponse,
    }),
  }
);

console.log("[POSTSSR-19OCT] PAYMENT RESPONSE", {
  bookingId: booking.id,
  legIndex,
  success: addPayment?.success,
  response: addPayment,
  timestamp: new Date().toISOString(),
});

      if (!addPayment.success) {
        throw new Error(
          addPayment.errorInnerException ||
            addPayment.errorDesc ||
            addPayment.error ||
            `Supplier payment failed for leg ${legIndex + 1}.`
        );
      }

      // 3. Confirm this PNR before moving to the next.
console.log("[POSTSSR-19OCT] CONFIRM START", {
  bookingId: booking.id,
  legIndex,
  legIndexes: [legIndex],
  timestamp: new Date().toISOString(),
});

const confirmed = await apiFetch(
  `${API_BASE}/flight-ticketing/confirm-post-ssr`,
  {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({
      bookingId: booking.id,
      legIndex,
      legIndexes: [legIndex],
      paidAmount: legSSRDetails
        .reduce(
          (sum, item) => sum + Number(item.amount || 0),
          0
        )
        .toFixed(2),
    }),
  }
);

console.log("[POSTSSR-19OCT] CONFIRM RESPONSE", {
  bookingId: booking.id,
  legIndex,
  success: confirmed?.success,
  response: confirmed,
  timestamp: new Date().toISOString(),
});

      if (!confirmed.success) {
        throw new Error(
          confirmed.errorInnerException ||
            confirmed.errorDesc ||
            confirmed.error ||
            `Supplier confirmation failed for leg ${legIndex + 1}.`
        );
      }

      console.log("[POSTSSR NORMAL RT LEG COMPLETE]", {
        bookingId: booking.id,
        legIndex,
      });
    }
  
} else {
  

  // Keep the existing add-payment request below this line.
  const addPayment = await apiFetch(
    `${API_BASE}/flight-ticketing/add-payment`,

      {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({
          bookingId: booking.id,
          legIndexes: cartLegIndexes,
          transactionType: 1,
          clientRefNo: paymentId,
          gatewayResponse: paymentResponse,
        }),
      }
    );

    if (!addPayment.success) {
      throw new Error(
        addPayment.errorInnerException ||
          addPayment.errorDesc ||
          addPayment.error ||
          "Airline add-on payment confirmation failed."
      );
    }

    const confirmed = await apiFetch(
      `${API_BASE}/flight-ticketing/confirm-post-ssr`,
      {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({
          bookingId: booking.id,
          legIndexes: cartLegIndexes,
          paidAmount: payableAmount.toFixed(2),
        }),
      }
    );

    if (!confirmed.success) {
      throw new Error(
        confirmed.errorInnerException ||
          confirmed.errorDesc ||
          confirmed.error ||
          "Could not confirm selected add-ons."
      );
    }
  }

  setPostSsrPanel(null);
  setPostSsrOpen(false);
  setPostSsrOptions(null);
  setPostSsrSelectionsByContext({});

  await onRefresh?.();
  setPostSsrSuccess("Add-ons added successfully");
};


      // 1. Tell supplier which SSRs are being purchased
      console.log(
        "[POSTSSR FINAL CART]",
        ssrDetails.map((item) => ({
          paxId: item.paxId,
          passengerName: item.passengerName,
          type: item.type,
          name: item.name,
          leg: item.itineraryLegIndex,
          segmentId: item.segmentId,
          route: item.route,
          flightNo: item.flightNo,
          seatCode: item.seatCode,
          amount: item.amount,
        }))
      );

      const itineraryLegs = safeJsonParse(booking.itinerary_legs, []);

const normalizedTripType = String(
  booking?.trip_type || booking?.tripType || ""
)
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, "_");

const itineraryLegsForTripType = Array.isArray(itineraryLegs)
  ? itineraryLegs
  : [];

const isSpecialRoundTrip =
  normalizedTripType === "special_round_trip" ||
  normalizedTripType === "special_roundtrip" ||
  itineraryLegsForTripType.some(
    (leg) =>
      String(leg?.supplierBookingMode || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, "-") === "special-round-trip"
  );

const isMultiCity =
  normalizedTripType === "multi_city" ||
  normalizedTripType === "multicity";

const isExplicitNormalRoundTrip = [
  "round_trip",
  "roundtrip",
  "return",
  "normal_round_trip",
].includes(normalizedTripType);

// Fallback for older bookings whose DB trip_type was incorrectly
// saved as one_way, but whose two itinerary legs are reverse routes.
const firstLeg = itineraryLegsForTripType[0];
const secondLeg = itineraryLegsForTripType[1];

const firstDepartCode = String(
  firstLeg?.departCode || ""
).trim().toUpperCase();

const firstArriveCode = String(
  firstLeg?.arriveCode || ""
).trim().toUpperCase();

const secondDepartCode = String(
  secondLeg?.departCode || ""
).trim().toUpperCase();

const secondArriveCode = String(
  secondLeg?.arriveCode || ""
).trim().toUpperCase();

const hasReverseTwoLegItinerary =
  itineraryLegsForTripType.length === 2 &&
  firstDepartCode !== "" &&
  firstArriveCode !== "" &&
  secondDepartCode !== "" &&
  secondArriveCode !== "" &&
  firstDepartCode === secondArriveCode &&
  firstArriveCode === secondDepartCode;

const isNormalRoundTrip =
  !isSpecialRoundTrip &&
  !isMultiCity &&
  (
    isExplicitNormalRoundTrip ||
    (
      ["", "one_way"].includes(normalizedTripType) &&
      hasReverseTwoLegItinerary
    )
  );


/*
 * Initiate selected SSRs before opening Easebuzz.
 * Special Round Trip: submit selections from both itinerary legs
 * together because they share the supplier booking/PNR.
 * Normal Round Trip: preserve the existing per-leg payment and
 * confirmation sequence in confirmPostSsrAfterPayment.
 * Do not change One-Way or Multi-City behavior.
 */
console.log("[POSTSSR INITIATE BEFORE PAYMENT]", {
  bookingId: booking.id,
  legIndexes: cartLegIndexes,
  itemCount: ssrDetails.length,
  legs: [
    ...new Set(
      ssrDetails.map((item) =>
        Number(item.itineraryLegIndex ?? item.legIndex ?? 0)
      )
    ),
  ],
});

const initiated = await apiFetch(
  `${API_BASE}/flight-ticketing/initiate-post-ssr`,
  {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({
      bookingId: booking.id,
      legIndexes: cartLegIndexes,
      ssrDetails,
    }),
  }
);

if (!initiated.success) {
  throw new Error(
    initiated.errorInnerException ||
      initiated.errorDesc ||
      initiated.error ||
      "Could not initiate add-ons before payment."
  );
}


setPostSsrError("");
      if (payableAmount === 0) {
        await confirmPostSsrAfterPayment({ status: "success", mode: "free_ssr" });
        return;
      }

      // 2. Open Easebuzz for ONLY the selected add-on amount
      await loadEasebuzzCheckoutScript();

      const payload = await apiFetch(
        `${API_BASE}/payment/easebuzz/initiate`,
        {
          method: "POST",
          headers: authHeaders(token),
          body: JSON.stringify({
            amount: payableAmount.toFixed(2),
            productinfo: `Flight Add-ons ${booking.booking_reference || ""}`.trim(),
            firstname:
              passengers[0]
                ? formatPassengerName(passengers[0])
                : booking.passenger_name || "Traveller",
            email: payerEmail,
            phone: payerPhone,
            bookingReference:
              booking.booking_reference ||
              booking.supplier_booking_ref,
            udf1: "POST_SSR",
            udf2: booking.airline || "",
            udf3: booking.airline_pnr || "",
          }),
        }
      );

      const merchantKey = String(payload.key || "").trim();
      const accessKey = String(payload.access_key || "").trim();

      if (!merchantKey || !accessKey || accessKey.length < 20) {
        throw new Error(
          payload.error || "Payment gateway could not be initialized."
        );
      }

      const easebuzzCheckout = new window.EasebuzzCheckout(
        merchantKey,
        payload.env || "test"
      );

      easebuzzCheckout.initiatePayment({
        access_key: accessKey,

        onResponse: async (response) => {
          if (response.status !== "success") {
            setPostSsrError(
              response.error_Message ||
              response.error ||
              "Payment was not completed."
            );
            postSsrPaymentLockRef.current = false;
            setActionBusy(null);
            return;
          }

          try {
            setActionBusy("postSsr");

            await confirmPostSsrAfterPayment(response);
          } catch (err) {
            console.error("POST SSR confirmation failed:", err);
            setPostSsrError(err.message || "Could not confirm add-ons after payment.");
          } finally {
            postSsrPaymentLockRef.current = false;
            setActionBusy(null);
          }
        },
      });

      checkoutOpened = true;
      setActionBusy(null);
    } catch (err) {
      console.error("POST SSR payment error:", err);
      setPostSsrError(err.message || "Could not process add-on payment.");
    } finally {
      if (!checkoutOpened) {
        postSsrPaymentLockRef.current = false;
        setActionBusy(null);
      }
    }
  };

  return (
    <div className="mbt-booking-card">
      <div className={bookingBarClass("flight")} />
      <div className="mbt-booking-inner" style={{ flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ width: 58, height: 58, borderRadius: 12, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
          {logoUrl
            ? <img src={logoUrl} alt={booking.airline} style={{ width: 34, height: 34, objectFit: "contain" }} onError={(e) => { e.target.style.display = "none"; }} />
            : <Plane size={22} />}
        </div>

        <div className="mbt-booking-info" style={{ minWidth: 220 }}>
          <div className="mbt-booking-title">
            {(() => {
              const legs = safeJsonParse(booking.itinerary_legs, []);

              if (Array.isArray(legs) && legs.length > 1) {
                return legs
                  .map(
                    (leg) =>
                      `${cityLabel(
                        leg.departCode || leg.depart_code,
                        airportCache
                      )} → ${cityLabel(
                        leg.arriveCode || leg.arrive_code,
                        airportCache
                      )}`
                  )
                  .join(" · ");
              }

              return `${cityLabel(
                booking.depart_code,
                airportCache
              )} → ${cityLabel(
                booking.arrive_code,
                airportCache
              )}`;
            })()}
          </div>
          <div className="mbt-booking-meta">
            <span>
              <CalendarDays size={12} />
              {formatDate(booking.travel_date)}
            </span>

            <span>
              <Plane size={12} />
              {booking.airline} · {booking.flight_numbers}
            </span>

            {booking.fare_name && (
              <span>
                {booking.fare_name}
                {booking.class_type ? ` · ${booking.class_type}` : ""}
              </span>
            )}

            {booking.fare_type &&
              String(booking.fare_type).toLowerCase() !== "regular" && (
                <span>
                  <Ticket size={12} />
                  {booking.fare_type} Fare
                </span>
              )}
          </div>
          {booking.booking_reference && (
            <div className="mbt-booking-ref">
              REF&nbsp;<strong>{booking.booking_reference}</strong>

              {(() => {
                let legs = booking.itinerary_legs || [];

                if (typeof legs === "string") {
                  try {
                    legs = JSON.parse(legs);
                  } catch {
                    legs = [];
                  }
                }

                const legPnrs = Array.isArray(legs)
                  ? legs
                    .map((leg) => leg?.airlinePnr)
                    .filter(Boolean)
                  : [];

                const pnrs = [
                  ...new Set([
                    ...legPnrs,
                    booking.airline_pnr,
                  ].filter(Boolean)),
                ];

                return pnrs.length > 0 ? (
                  <>
                    {" "}· PNR <strong>{pnrs.join(" / ")}</strong>
                  </>
                ) : null;
              })()}
            </div>
          )}

          <div className="mbt-flight-summary-chips">
            <span>
              <Users size={13} />
              {passengers.length} Traveller{passengers.length !== 1 ? "s" : ""}
            </span>

            {hasPartialCancellation &&
  legPassengerCounts.map(
    (leg) => (
      <span
        key={
          `flight-active-${leg.legIndex}`
        }
      >
        <Plane size={13} />

        {leg.legIndex === 0
          ? "Outbound"
          : leg.legIndex === 1
            ? "Return"
            : `Flight ${leg.legIndex + 1}`}

        {" · "}

        {leg.active} active

        {leg.cancelled > 0
          ? ` · ${leg.cancelled} cancellation requested`
          : ""}
      </span>
    )
  )}

            {seatCount > 0 && (
              <span>
                <Armchair size={13} />
                {seatCount} Seat Selection{seatCount !== 1 ? "s" : ""}
              </span>
            )}

            {extrasCount > 0 && (
              <span>
                <Luggage size={13} />
                {extrasCount} Add-on{extrasCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* ── Ticket actions ── */}
          {isTicketed ? (
            <div className="mbt-ticket-actions-row">
              <button type="button" className="mbt-ticket-action-btn" disabled={actionBusy === "view"} onClick={handleView}>
                <Eye size={12} />{actionBusy === "view" ? "Opening…" : "View"}
              </button>
              <button type="button" className="mbt-ticket-action-btn" disabled={isDownloading} onClick={() => onDownloadReceipt(booking)}>
                <Download size={12} />{isDownloading ? "Downloading…" : "Download"}
              </button>
              <button type="button" className="mbt-ticket-action-btn" disabled={actionBusy === "print"} onClick={handlePrint}>
                <Printer size={12} />{actionBusy === "print" ? "Preparing…" : "Print"}
              </button>
              <button type="button" className="mbt-ticket-action-btn" onClick={handleEmailTicket}>
                <Mail size={12} />Email
              </button>
              <button type="button" className="mbt-ticket-action-btn mbt-ticket-action-btn--whatsapp" onClick={handleWhatsappTicket}>
                <MessageCircle size={12} />WhatsApp
              </button>
              <button type="button" className="mbt-ticket-action-btn" disabled={actionBusy === "invoice"} onClick={handleDownloadInvoice}>
                <FileText size={12} />{actionBusy === "invoice" ? "Fetching…" : "Invoice"}
              </button>
              {canRequestReschedule && (
                <button type="button" className="mbt-ticket-action-btn mbt-ticket-action-btn--reschedule" onClick={() => setRescheduleOpen(true)}>
                  <CalendarDays size={12} /> Reschedule
                </button>
              )}
              <button
                type="button"
                className="mbt-ticket-action-btn"
                onClick={handleOpenPostSsr}
              >
                Manage Add-ons
              </button>
            </div>
          ) : (
  <div className="mbt-ticket-pending-note">
    <Clock size={12} />
    {isReleased
      ? "Booking released. Ticket actions are unavailable."
      : "Ticket actions unlock once the airline issues your e-ticket."}
  </div>
)}
          {rescheduleStatus === "requested" && (
            <div className="mbt-ticket-pending-note"><Clock size={12} /> Reschedule request submitted. Our team will confirm airline availability and charges.</div>
          )}
        </div>

        <div className="mbt-booking-right">
          <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
            <MbtBadge
  status={
    hasPartialCancellation
      ? "partially_cancelled"
      : booking.status
  }
/>
            {canCancel && (
              <div className="mbt-menu-wrap" ref={menuRef}>
                <button className="mbt-menu-trigger" onClick={() => setMenuOpen(p => !p)}>⋯</button>
                {menuOpen && (
                  <div className="mbt-context-menu">
                    <button className="mbt-menu-item" style={{ color: "#d97706" }}
                      onClick={() => {
                        setMenuOpen(false);
                        if (isUnpaidHeldFlight) {
                          handleReleaseHeldBooking();
                        } else {
                          setCancelOpen(true);
                        }
                      }}
                      disabled={actionBusy === "release"}>
                      <X size={13} /> {isUnpaidHeldFlight ? (actionBusy === "release" ? "Releasing..." : "Release Booking") : "Cancel Booking"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="mbt-booking-price">₹{formatPrice(booking.total_price)}</div>
          {postBookingAddonTotal > 0 && (
            <div className="mbt-booking-addon-price">
              Add-ons +₹{formatPrice(postBookingAddonTotal)}
            </div>
          )}
          {canPayFlight && (
            <div className="mbt-booking-actions">
              <button type="button" className="mbt-booking-pay-now" disabled={isPaying} onClick={() => onPayFlight(booking)}>
                <CreditCard size={12} />{isPaying ? "Processing..." : "Continue Payment"}
              </button>
            </div>
          )}
          {releaseError && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                lineHeight: 1.4,
                color: "#b91c1c",
                maxWidth: 280,
                textAlign: "right",
              }}
            >
              {releaseError}
            </div>
          )}
        </div>

        {postSsrSuccess && (
          <div
            className="mbt-flight-success-note"
            role="status"
            aria-live="polite"
          >
            <CheckCircle2 size={18} />

            <div>
              <strong>Add-ons added successfully</strong>
              <span>
                Your booking and e-ticket have been updated.
              </span>
            </div>

            <button
              type="button"
              className="mbt-flight-success-close"
              onClick={() => setPostSsrSuccess("")}
              aria-label="Dismiss success message"
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>

      {rescheduleOpen && portalRoot && createPortal(
        <div className="mbt-reschedule-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setRescheduleOpen(false)}>
          <form className="mbt-reschedule-modal" onSubmit={handleRequestReschedule}>
            <button type="button" className="mbt-reschedule-close" onClick={() => setRescheduleOpen(false)} aria-label="Close reschedule form">
              <X size={16} />
            </button>
            <div className="mbt-reschedule-head">
              <CalendarDays size={18} />
              <div>
                <h3>Reschedule flight</h3>
                <p>{booking.depart_code} to {booking.arrive_code} · {booking.airline} {booking.flight_numbers}</p>
              </div>
            </div>

            <div className="mbt-reschedule-rules">
              <div className="mbt-reschedule-rules-title">Supplier reschedule charges</div>
              {rescheduleRules.slice(0, 4).map((rule, index) => (
                <div className="mbt-reschedule-rule" key={`${rule.PassengerType ?? "pax"}-${index}`}>
                  <strong>{formatChargeValue(rule)}</strong>
                  <span>{passengerTypeLabel(rule.PassengerType ?? rule.passengerType)} · {chargeWindowLabel(rule)}</span>
                  <small>{applicabilityLabel(rule)} · {returnFlightLabel(rule)}</small>
                  <small>Online fee: {formatServiceFee(rule.OnlineServiceFee ?? rule.onlineServiceFee)} · Offline fee: {formatServiceFee(rule.OfflineServiceFee ?? rule.offlineServiceFee)}</small>
                  {rule.Remarks && <small>{rule.Remarks}</small>}
                </div>
              ))}
            </div>

            <div className="mbt-reschedule-grid">
              <label>
                New travel date
                <input
                  type="date"
                  value={rescheduleForm.requestedDate}
                  min={new Date().toISOString().slice(0, 10)}
                  required
                  onChange={(event) => setRescheduleForm((prev) => ({ ...prev, requestedDate: event.target.value }))}
                />
              </label>
              <label>
                Preferred time
                <input
                  type="time"
                  value={rescheduleForm.requestedTime}
                  onChange={(event) => setRescheduleForm((prev) => ({ ...prev, requestedTime: event.target.value }))}
                />
              </label>
            </div>
            <label className="mbt-reschedule-field">
              Remarks
              <textarea
                rows={3}
                value={rescheduleForm.remarks}
                placeholder="Share alternate flight/time preferences if any"
                onChange={(event) => setRescheduleForm((prev) => ({ ...prev, remarks: event.target.value }))}
              />
            </label>
            <div className="mbt-reschedule-note">
              Final fare difference and airline approval are confirmed by support before the change is completed.
            </div>
            <div className="mbt-reschedule-actions">
              <button type="button" onClick={() => setRescheduleOpen(false)}>Cancel</button>
              <button type="submit" disabled={actionBusy === "reschedule"}>
                {actionBusy === "reschedule" ? "Submitting..." : "Submit request"}
              </button>
            </div>
          </form>
        </div>,
        portalRoot
      )}

      {postSsrOpen && postSsrPanel === null && portalRoot && createPortal(
        <div
          className="mbt-reschedule-overlay"
          role="presentation"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setPostSsrOpen(false)
          }
        >
          <div className="mbt-reschedule-modal">
            <button
              type="button"
              className="mbt-reschedule-close"
              onClick={() => setPostSsrOpen(false)}
              aria-label="Close add-ons"
            >
              <X size={16} />
            </button>

            <div className="mbt-reschedule-head">
              <Plane size={18} />
              <div>
                <h3>Manage Add-ons</h3>
                <p>
                  {booking.depart_code} to {booking.arrive_code}
                  {" · "}
                  {booking.airline} {booking.flight_numbers}
                </p>
              </div>
            </div>

            {postSsrLoading && (
              <div className="mbt-reschedule-note">
                Loading available meals, seats and baggage...
              </div>
            )}

            {postSsrError && (
              <div className="mbt-reschedule-note">
                {postSsrError}
              </div>
            )}

            {!postSsrLoading && postSsrOptions && (
              <>
                {safeJsonParse(booking.itinerary_legs, []).length > 1 && (
                  <div className="mbt-reschedule-field mbt-custom-select-field">
                    <span>Flight leg</span>

                    <div className="mbt-custom-select">
                      <button
                        type="button"
                        className="mbt-custom-select-trigger"
                        onClick={() => {
                          setFlightLegDropdownOpen((prev) => !prev);
                          setPassengerDropdownOpen(false);
                        }}
                      >
                        <span>
                          {(() => {
                            const legs = safeJsonParse(booking.itinerary_legs, []);
                            const leg = legs[selectedPostSsrLeg];

                            if (!leg) {
                              return `Leg ${selectedPostSsrLeg + 1}`;
                            }
                            const legSummary = summarizeStoredFlightLeg(leg);

                            return `${legSummary.departCode || `Leg ${selectedPostSsrLeg + 1}`} → ${legSummary.arriveCode || ""
                              }`;
                          })()}
                        </span>

                        <ChevronDown
                          size={15}
                          className={flightLegDropdownOpen ? "is-open" : ""}
                        />
                      </button>

                      {flightLegDropdownOpen && (
                        <div className="mbt-custom-select-menu">
                          {safeJsonParse(booking.itinerary_legs, []).map((leg, index) => {
                            const selected = selectedPostSsrLeg === index;
                            const legSummary = summarizeStoredFlightLeg(leg);

                            return (
                              <button
                                key={index}
                                type="button"
                                className={`mbt-custom-select-option ${selected ? "is-selected" : ""
                                  }`}
                                onClick={async () => {
                                  setSelectedPostSsrLeg(index);

                                  setFlightLegDropdownOpen(false);

                                  await loadPostSsrForLeg(index);
                                }}
                              >
                                {legSummary.departCode || `Leg ${index + 1}`} →{" "}
                                {legSummary.arriveCode || ""}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="mbt-reschedule-field mbt-custom-select-field">
                  <span>Passenger</span>

                  <div className="mbt-custom-select">
                    <button
                      type="button"
                      className="mbt-custom-select-trigger"
                      disabled={postSsrSelectablePassengers.length === 0}
                      onClick={() => {
                        setPassengerDropdownOpen((prev) => !prev);
                        setFlightLegDropdownOpen(false);
                      }}
                    >
                      <span>
                        {postSsrSelectablePassengers.length === 0
                          ? "No eligible passengers"
                          : formatPassengerName(
                            passengers[Number(postSsrPassenger || 1) - 1]
                          )}
                      </span>

                      <ChevronDown
                        size={15}
                        className={passengerDropdownOpen ? "is-open" : ""}
                      />
                    </button>

                    {passengerDropdownOpen && (
                      <div className="mbt-custom-select-menu">
                        {passengers.map((passenger, index) => ({ passenger, index }))
                          .filter(({ passenger }) => !isInfantPassenger(passenger))
                          .map(({ passenger, index }) => {
                            const value = String(index + 1);
                            const selected = postSsrPassenger === value;

                            return (
                              <button
                                type="button"
                                key={index}
                                className={`mbt-custom-select-option ${selected ? "is-selected" : ""
                                  }`}
                                onClick={() => {
                                  setPostSsrPassenger(value);
                                  setPassengerDropdownOpen(false);
                                }}
                              >
                                {formatPassengerName(passenger)}
                              </button>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mbt-post-ssr-services">

                  <button
                    type="button"
                    className="mbt-post-ssr-service-card"
                    onClick={() => setPostSsrPanel("meal")}
                  >


                    <div className="mbt-post-ssr-service-copy">
                      <strong>Add Meal</strong>

                      <span>
                        {postSsrSelections.Meals.length > 0
                          ? `${postSsrSelections.Meals.length} meal${postSsrSelections.Meals.length > 1 ? "s" : ""
                          } selected`
                          : "Choose available meals"}
                      </span>
                    </div>

                    <ChevronRight size={18} />
                  </button>


                  <button
                    type="button"
                    className="mbt-post-ssr-service-card"
                    onClick={() => setPostSsrPanel("baggage")}
                  >


                    <div className="mbt-post-ssr-service-copy">
                      <strong>Add Baggage</strong>

                      <span>
                        {postSsrSelections.Baggage.length > 0
                          ? `${postSsrSelections.Baggage.length} baggage option${postSsrSelections.Baggage.length > 1 ? "s" : ""
                          } selected`
                          : "Choose additional baggage"}
                      </span>
                    </div>

                    <ChevronRight size={18} />
                  </button>

                  {getPostSsrExtraOptions().length > 0 && (
                    <button
                      type="button"
                      className="mbt-post-ssr-service-card"
                      onClick={() => setPostSsrPanel("extras")}
                    >
                      <div className="mbt-post-ssr-service-copy">
                        <strong>Add Extras</strong>

                        <span>
                          {postSsrSelections.Extras.length > 0
                            ? `${postSsrSelections.Extras.length} extra${postSsrSelections.Extras.length > 1 ? "s" : ""
                            } selected`
                            : "Choose available extras"}
                        </span>
                      </div>

                      <ChevronRight size={18} />
                    </button>
                  )}

                  <button
                    type="button"
                    className="mbt-post-ssr-service-card"
                    disabled={!hasAvailablePostSsrSeatSegment}
                    onClick={() => {
                      if (hasAvailablePostSsrSeatSegment) {
                        setPostSsrPanel("seat");
                      }
                    }}
                  >
                    <div className="mbt-post-ssr-service-copy">
                      <strong>
                        {hasAvailablePostSsrSeatSegment
                          ? "Select Seat"
                          : "Seat Already Selected"}
                      </strong>

                      <span>
                        {!hasAvailablePostSsrSeatSegment
                          ? "Seat assigned for all flight segments"
                          : postSsrSelections.Seats.length > 0
                            ? `${postSsrSelections.Seats.length} seat${postSsrSelections.Seats.length > 1 ? "s" : ""
                            } selected`
                            : availablePostSsrSeatSegments.length <
                              postSsrSeatSegmentGroups.length
                              ? "Choose seats for remaining flight segments"
                              : "Choose an available seat"}
                      </span>
                    </div>
                  </button>

                </div>

                {pendingPostSsrCount > 0 && (
                  <div className="mbt-post-ssr-summary">

                    <strong>Selected Add-ons ({pendingPostSsrCount})</strong>

                    {postSsrCartRows.map(({ item, type, paxId, itineraryLegIndex, amount, key }) => {
                      const passenger = passengers[paxId - 1];
                      const leg = postSsrItineraryLegs[itineraryLegIndex];
                      const label =
                        type === "seat"
                          ? `Seat ${item.seatCode || ""}`.trim()
                          : item.ssrTypeDesc || item.ssrTypeName || (type === "baggage" ? "Extra Baggage" : type === "meal" ? "Meal" : "Extra");
                      const context = [
                        passenger ? formatPassengerName(passenger) : `Passenger ${paxId}`,
                        leg ? `${leg.origin || leg.from || ""} → ${leg.destination || leg.to || ""}` : `Leg ${itineraryLegIndex + 1}`,
                        item.route || null,
                      ].filter(Boolean).join(" · ");

                      return (
                        <div key={key}>
                          <span>
                            {label}
                            {context ? ` · ${context}` : ""}
                          </span>
                          <b>₹{formatPrice(amount)}</b>
                        </div>
                      );
                    })}

                    <div className="mbt-post-ssr-summary-total">
                      <span>Total</span>

                      <b>
                        ₹{formatPrice(pendingPostSsrTotal)}
                      </b>
                    </div>

                  </div>
                )}

                <div className="mbt-reschedule-actions">
                  <button
                    type="button"
                    onClick={() => setPostSsrOpen(false)}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={
                      actionBusy === "postSsr" ||
                      !postSsrPassenger ||
                      pendingPostSsrCount === 0
                    }
                    onClick={handlePostSsrPayment}
                  >
                    {actionBusy === "postSsr"
                      ? "Processing..."
                      : "Continue to Payment"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        portalRoot
      )}

      {postSsrOpen && postSsrPanel === "meal" && portalRoot && createPortal(
        <div
          className="mbt-reschedule-overlay mbt-post-ssr-panel-overlay"
          role="presentation"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setPostSsrPanel(null)
          }
        >
          <div className="mbt-post-ssr-large-modal">

            <button
              type="button"
              className="mbt-reschedule-close"
              onClick={() => setPostSsrPanel(null)}
              aria-label="Close meal selection"
            >
              <X size={16} />
            </button>

            <div className="mbt-reschedule-head">
              <span style={{ fontSize: 20 }}>🍽️</span>

              <div>
                <h3>Select Meal</h3>
                <p>
                  {formatPassengerName(
                    passengers[Number(postSsrPassenger || 1) - 1]
                  )}
                </p>
              </div>
            </div>

            {(
              postSsrOptions?.ssrByType?.Meal ||
              postSsrOptions?.ssrByType?.Meals ||
              []
            ).length === 0 ? (
              <div className="mbt-reschedule-note">
                No meals available for this flight.
              </div>
            ) : (
              <div className="mbt-post-ssr-option-list">
                {groupPostSsrItems(
                  postSsrOptions?.ssrByType?.Meal ||
                  postSsrOptions?.ssrByType?.Meals ||
                  []
                ).map((group) => (
                  <div
                    key={group.key}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: "#64748b",
                        marginTop: 4,
                      }}
                    >
                      {group.label}
                    </div>

                    {group.items.map((item) => {
                      const itemKey = getPostSsrItemKey(
                        item,
                        postSsrOptions?.ssrByType?.Meal ||
                        postSsrOptions?.ssrByType?.Meals ||
                        []
                      );

                      const selected =
                        postSsrSelections.Meals.some(
                          (meal) =>
                            meal.__selectionKey === itemKey
                        );

                      const alreadyAdded =
                        isPostSsrAlreadyAdded(item);
                      return (
                        <button
                          key={itemKey}
                          type="button"
                          className={`mbt-post-ssr-option-card ${selected ? "is-selected" : ""
                            } ${alreadyAdded ? "is-already-added" : ""
                            }`}
                          disabled={alreadyAdded}
                          onClick={() => {
                            if (alreadyAdded) return;
                            setPostSsrSelections((prev) => {
                              const alreadySelected =
                                prev.Meals.some(
                                  (meal) =>
                                    meal.__selectionKey === itemKey
                                );

                              return {
                                ...prev,

                                Meals: alreadySelected
                                  ? prev.Meals.filter(
                                    (meal) =>
                                      meal.__selectionKey !== itemKey
                                  )
                                  : [
                                    ...prev.Meals,
                                    {
                                      ...enrichPostSsrItem(item),
                                      __selectionKey: itemKey,
                                    },
                                  ],
                              };
                            });
                          }}
                        >
                          <div>
                            <strong>
                              {item.ssrTypeDesc || "Meal"}
                            </strong>

                            <span>Airline meal</span>
                          </div>

                          <div className="mbt-post-ssr-option-right">
                            <b>
                              {Number(item.totalAmount || 0) > 0
                                ? `₹${formatPrice(item.totalAmount)}`
                                : "Free"}
                            </b>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            <div className="mbt-reschedule-actions">
              <button
                type="button"
                onClick={() => setPostSsrPanel(null)}
              >
                Done
              </button>
            </div>

          </div>
        </div>,
        portalRoot
      )}

      {postSsrOpen && postSsrPanel === "baggage" && portalRoot && createPortal(
        <div
          className="mbt-reschedule-overlay mbt-post-ssr-panel-overlay"
          role="presentation"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setPostSsrPanel(null)
          }
        >
          <div className="mbt-post-ssr-large-modal">

            <button
              type="button"
              className="mbt-reschedule-close"
              onClick={() => setPostSsrPanel(null)}
              aria-label="Close baggage selection"
            >
              <X size={16} />
            </button>

            <div className="mbt-reschedule-head">
              <Luggage size={18} />

              <div>
                <h3>Add Baggage</h3>
                <p>
                  {formatPassengerName(
                    passengers[Number(postSsrPassenger || 1) - 1]
                  )}
                </p>
              </div>
            </div>

            {(
              postSsrOptions?.ssrByType?.BAGGAGE ||
              postSsrOptions?.ssrByType?.Baggage ||
              []
            ).length === 0 ? (
              <div className="mbt-reschedule-note">
                No additional baggage available for this flight.
              </div>
            ) : (
              <div className="mbt-post-ssr-option-list">
                {groupPostSsrItems(
                  postSsrOptions?.ssrByType?.BAGGAGE ||
                  postSsrOptions?.ssrByType?.Baggage ||
                  []
                ).map((group) => (
                  <div
                    key={group.key}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: "#64748b",
                        marginTop: 4,
                      }}
                    >
                      {group.label}
                    </div>

                    {group.items.map((item) => {
                      const itemKey = getPostSsrItemKey(
                        item,
                        postSsrOptions?.ssrByType?.BAGGAGE ||
                        postSsrOptions?.ssrByType?.Baggage ||
                        []
                      );

                      const selected =
                        postSsrSelections.Baggage.some(
                          (baggage) =>
                            baggage.__selectionKey === itemKey
                        );

                      const alreadyAdded =
                        isPostSsrAlreadyAdded(item);

                      return (
                        <button
                          key={itemKey}
                          type="button"
                          className={`mbt-post-ssr-option-card ${selected ? "is-selected" : ""
                            } ${alreadyAdded ? "is-already-added" : ""}`}
                          disabled={alreadyAdded}
                          onClick={() => {
                            if (alreadyAdded) return;
                            setPostSsrSelections((prev) => {
                              return {
                                ...prev,

                                Baggage: selected
                                  ? prev.Baggage.filter(
                                    (baggage) =>
                                      baggage.__selectionKey !== itemKey
                                  )
                                  : [
                                    ...prev.Baggage,
                                    {
                                      ...enrichPostSsrItem(item),
                                      __selectionKey: itemKey,
                                      __groupKey: getPostSsrGroupKey(
                                        item,
                                        postSsrOptions?.ssrByType?.BAGGAGE ||
                                        postSsrOptions?.ssrByType?.Baggage ||
                                        []
                                      ),
                                    },
                                  ],
                              };
                            });
                          }}
                        >
                          <div>
                            <strong>
                              {item.ssrTypeDesc ||
                                "Extra baggage"}
                            </strong>

                            <span>Additional baggage</span>
                          </div>

                          <div className="mbt-post-ssr-option-right">
                            <b>
                              ₹{formatPrice(item.totalAmount)}
                            </b>

                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            <div className="mbt-reschedule-actions">
              <button
                type="button"
                onClick={() => setPostSsrPanel(null)}
              >
                Done
              </button>
            </div>

          </div>
        </div>,
        portalRoot
      )}

      {postSsrOpen && postSsrPanel === "extras" && portalRoot && createPortal(
        <div
          className="mbt-reschedule-overlay mbt-post-ssr-panel-overlay"
          role="presentation"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setPostSsrPanel(null)
          }
        >
          <div className="mbt-post-ssr-large-modal">

            <button
              type="button"
              className="mbt-reschedule-close"
              onClick={() => setPostSsrPanel(null)}
              aria-label="Close extras selection"
            >
              <X size={16} />
            </button>

            <div className="mbt-reschedule-head">
              <Luggage size={18} />

              <div>
                <h3>Add Extras</h3>
                <p>
                  {formatPassengerName(
                    passengers[Number(postSsrPassenger || 1) - 1]
                  )}
                </p>
              </div>
            </div>

            {getPostSsrExtraOptions().length === 0 ? (
              <div className="mbt-reschedule-note">
                No extras available for this flight.
              </div>
            ) : (
              <div className="mbt-post-ssr-option-list">
                {groupPostSsrItems(getPostSsrExtraOptions()).map((group) => (
                  <div
                    key={group.key}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: "#64748b",
                        marginTop: 4,
                      }}
                    >
                      {group.label}
                    </div>

                    {group.items.map((item) => {
                      const itemKey = getPostSsrItemKey(
                        item,
                        getPostSsrExtraOptions()
                      );

                      const selected =
                        postSsrSelections.Extras.some(
                          (extra) =>
                            extra.__selectionKey === itemKey
                        );

                      const alreadyAdded =
  isPostSsrAlreadyAdded(item);

const isPriorityExtra =
  String(item?.ssrTypeName || "").toUpperCase() === "FASTFORWARD" ||
  String(item?.ssrCode || "").toUpperCase() === "PCBF" ||
  [
    item?.ssrTypeName,
    item?.ssrTypeDesc,
    item?.ssrType,
    item?.type,
    item?.name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes("priority check-in");

const groupKey = isPriorityExtra
  ? getPostSsrGroupKey(
      item,
      getPostSsrExtraOptions()
    )
  : null;

const anotherPrioritySelected =
  isPriorityExtra &&
  !selected &&
  postSsrSelections.Extras.some(
    (extra) =>
      extra.__isPriorityExtra === true &&
      extra.__groupKey === groupKey &&
      extra.__selectionKey !== itemKey
  );

return (
                        <button
                          key={itemKey}
                          type="button"
                          className={`mbt-post-ssr-option-card ${selected ? "is-selected" : ""
                            } ${alreadyAdded ? "is-already-added" : ""}`}
                          disabled={alreadyAdded || anotherPrioritySelected}
                          onClick={() => {
  if (alreadyAdded || anotherPrioritySelected) return;

  setPostSsrSelections((prev) => {
    const extrasWithoutCurrent = prev.Extras.filter(
      (extra) => extra.__selectionKey !== itemKey
    );

    if (selected) {
      return {
        ...prev,
        Extras: extrasWithoutCurrent,
      };
    }

    return {
      ...prev,
      Extras: [
        ...extrasWithoutCurrent,
        {
  ...enrichPostSsrItem(item),
  __selectionKey: itemKey,
  __groupKey: getPostSsrGroupKey(
    item,
    getPostSsrExtraOptions()
  ),
  __isPriorityExtra: isPriorityExtra,
}
      ],
    };
  });
}}
                        >
                          <div>
                            <strong>
                              {item.ssrTypeDesc ||
                                item.ssrTypeName ||
                                "Extra"}
                            </strong>

                            <span>Airline extra</span>
                          </div>

                          <div className="mbt-post-ssr-option-right">
                            <b>
                              {Number(item.totalAmount || 0) > 0
                                ? `₹${formatPrice(item.totalAmount)}`
                                : "Free"}
                            </b>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            <div className="mbt-reschedule-actions">
              <button
                type="button"
                onClick={() => setPostSsrPanel(null)}
              >
                Done
              </button>
            </div>

          </div>
        </div>,
        portalRoot
      )}

      {postSsrOpen && postSsrPanel === "seat" && portalRoot && createPortal(
        <div
          className="mbt-reschedule-overlay mbt-post-ssr-panel-overlay"
          role="presentation"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setPostSsrPanel(null)
          }
        >
          <div className="mbt-post-ssr-large-modal mbt-post-ssr-seat-modal">

            <button
              type="button"
              className="mbt-reschedule-close"
              onClick={() => setPostSsrPanel(null)}
              aria-label="Close seat selection"
            >
              <X size={16} />
            </button>

            <div className="mbt-reschedule-head">
              <Armchair size={18} />

              <div>
                <h3>Select Seat</h3>

                <p>
                  {formatPassengerName(
                    passengers[Number(postSsrPassenger || 1) - 1]
                  )}
                </p>
              </div>
            </div>

            {availablePostSsrSeatSegments.length === 0 ? (
              <div className="mbt-reschedule-note">
                Seats have already been selected for all segments of this flight.
              </div>
            ) : (
              <div className="mbt-post-ssr-seat-segments">
                {availablePostSsrSeatSegments.map(
                  ({ segment, segmentIndex }) => {
                    const groupKey =
                      getPostSsrSeatGroupKey(
                        segment,
                        segmentIndex
                      );

                    const selectedSeat =
                      postSsrSelections.Seats.find(
                        (seat) =>
                          seat.__groupKey === groupKey
                      );

                    const selectedForSegment =
                      selectedSeat?.__seatId
                        ? [selectedSeat.__seatId]
                        : [];

                    return (
                      <div
                        key={groupKey}
                        className="mbt-post-ssr-seat-segment"
                      >
                        <div className="mbt-post-ssr-seat-segment-head">
  {selectedPostSsrOuterLeg?.segments?.length <= 1
    ? `${segment.origin || ""} → ${segment.destination || ""}`
    : getPostSsrSeatGroupLabel(segment, segmentIndex)}
</div>

                        <SeatMap
                          seatMap={segment.rows}
                          selectedSeats={
                            selectedForSegment
                          }
                          requiredSeats={1}
                          airlineCode={airlineCode}
                          loading={false}
                          onSeatToggle={(seat) => {
  if (!seat?.id) return;

  console.log("[POST SSR SEAT CLICK]", {
    groupKey,
    seatId: seat.id,
    seatCode: seat.seatCode || seat.id,
    ssrKey: seat.ssrKey,
    segmentIndex,
    segmentId: segment.segmentId,
    supplierLegIndex: segment.supplierLegIndex,
  });

  const alreadySelected =
    selectedSeat?.__seatId === seat.id;

                            setPostSsrSelections(
                              (prev) => {
                                const withoutGroup =
                                  prev.Seats.filter(
                                    (item) =>
                                      item.__groupKey !==
                                      groupKey
                                  );

                                if (alreadySelected) {
                                  return {
                                    ...prev,
                                    Seats: withoutGroup,
                                  };
                                }

                                const outerSegments =
                                  Array.isArray(
                                    selectedPostSsrOuterLeg
                                      ?.segments
                                  )
                                    ? selectedPostSsrOuterLeg
                                      .segments
                                    : [];

                                const fallbackSegment =
                                  outerSegments[
                                  segmentIndex
                                  ] || null;

                                const origin =
                                  segment.origin ||
                                  fallbackSegment
                                    ?.origin ||
                                  fallbackSegment
                                    ?.departCode ||
                                  seat.origin ||
                                  null;

                                const destination =
                                  segment.destination ||
                                  fallbackSegment
                                    ?.destination ||
                                  fallbackSegment
                                    ?.arriveCode ||
                                  seat.destination ||
                                  null;

                                const route =
                                  segment.route ||
                                  seat.route ||
                                  (
                                    origin &&
                                      destination
                                      ? `${origin} → ${destination}`
                                      : null
                                  );

                                const flightNo =
                                  segment.flightNo ||
                                  fallbackSegment
                                    ?.flightNo ||
                                  fallbackSegment
                                    ?.flightNumber ||
                                  seat.flightNo ||
                                  null;

                                const nextSeat = {
                                  ...seat.raw,

                                  ssrKey:
                                    seat.ssrKey,

                                  amount:
                                    seat.extraCost,

                                  seatCode:
                                    seat.seatCode ||
                                    seat.id,

                                  itineraryLegIndex:
                                    selectedPostSsrLeg,

                                  segmentWise:
                                    postSsrSeatSegmentGroups
                                      .length > 1,

                                  route,

                                  origin,

                                  destination,

                                  flightNo,

                                  segmentId:
                                    segment.segmentId ??
                                    seat.segmentId ??
                                    null,

                                  supplierLegIndex:
                                    segment.supplierLegIndex ??
                                    seat.supplierLegIndex ??
                                    null,

                                  flightId:
                                    segment.flightId ??
                                    seat.flightId ??
                                    null,

                                  __groupKey:
                                    groupKey,

                                  __seatId:
                                    seat.id,

                                  __selectionKey:
                                    `${groupKey}::${seat.ssrKey ||
                                    seat.id
                                    }`,
                                };

                                return {
                                  ...prev,

                                  Seats: [
                                    ...withoutGroup,
                                    nextSeat,
                                  ],
                                };
                              }
                            );
                          }}
                        />
                      </div>
                    );
                  }
                )}
              </div>
            )}

            <div className="mbt-reschedule-actions">
              <button
                type="button"
                onClick={() => setPostSsrPanel(null)}
              >
                Done
              </button>
            </div>

          </div>
        </div>,
        portalRoot
      )}

      {cancelOpen && portalRoot && createPortal(

        <div className="mbt-reschedule-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setCancelOpen(false)}>
          <div className="mbt-reschedule-modal">
            <button type="button" className="mbt-reschedule-close" onClick={() => setCancelOpen(false)} aria-label="Close cancellation form">
              <X size={16} />
            </button>
            <div className="mbt-reschedule-head">
              <AlertTriangle size={18} />
              <div>
                <h3>Cancel flight booking</h3>
                <p>{booking.depart_code} to {booking.arrive_code} · {booking.airline} {booking.flight_numbers}</p>
              </div>
            </div>

            <div className="mbt-reschedule-rules">
              <div className="mbt-reschedule-rules-title">Supplier cancellation charges</div>
              {cancellationRules.slice(0, 4).map((rule, index) => (
                <div className="mbt-reschedule-rule" key={`${rule.PassengerType ?? "pax"}-${index}`}>
                  <strong>{formatChargeValue(rule)}</strong>
                  <span>{passengerTypeLabel(rule.PassengerType ?? rule.passengerType)} · {chargeWindowLabel(rule)}</span>
                  <small>{applicabilityLabel(rule)} · {returnFlightLabel(rule)}</small>
                  <small>Online fee: {formatServiceFee(rule.OnlineServiceFee ?? rule.onlineServiceFee)} · Offline fee: {formatServiceFee(rule.OfflineServiceFee ?? rule.offlineServiceFee)}</small>
                  {rule.Remarks && <small>{rule.Remarks}</small>}
                </div>
              ))}
            </div>

            <div className="mbt-reschedule-note">
              The final refund amount is calculated by the airline based on the above charges at the time of cancellation.
            </div>
            <div className="mbt-reschedule-actions">
              <button type="button" onClick={() => setCancelOpen(false)}>Go Back</button>
              <button
                type="button"
                onClick={() => { setCancelOpen(false); navigate(`/cancel-booking/flight/${booking.id}`); }}
              >
                Proceed to Cancel
              </button>
            </div>
          </div>
        </div>,
        portalRoot
      )}
    </div>
  );
}

function FlightCancellationCard({
  booking,
  airportCache,
}) {
  const passengers =
    Array.isArray(
      booking.passenger_details
    )
      ? booking.passenger_details
      : safeJsonParse(
          booking.passenger_details,
          []
        );

  const leg =
    booking.cancellationLeg ||
    safeJsonParse(
      booking.itinerary_legs,
      []
    )[0] ||
    {};

  const departCode =
    leg.departCode ||
    leg.depart_code ||
    booking.depart_code ||
    "—";

  const arriveCode =
    leg.arriveCode ||
    leg.arrive_code ||
    booking.arrive_code ||
    "—";

  const pnr =
    leg.airlinePnr ||
    leg.airline_pnr ||
    booking.airline_pnr ||
    "—";

  const legLabel =
    Number(
      booking.cancellationLegIndex
    ) === 0
      ? "Outbound"
      : Number(
            booking.cancellationLegIndex
          ) === 1
        ? "Return"
        : `Flight ${
            Number(
              booking.cancellationLegIndex
            ) + 1
          }`;

  return (
    <div className="mbt-booking-card">
      <div
        className={
          bookingBarClass("flight")
        }
      />

      <div
        className="mbt-booking-inner"
        style={{
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            width: 58,
            height: 58,
            borderRadius: 12,
            background: "#fff7ed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Plane size={22} />
        </div>

        <div
          className="mbt-booking-info"
          style={{ minWidth: 220 }}
        >
          <div className="mbt-booking-title">
            {legLabel} ·{" "}
            {cityLabel(
              departCode,
              airportCache
            )}{" "}
            →{" "}
            {cityLabel(
              arriveCode,
              airportCache
            )}
          </div>

          <div className="mbt-booking-meta">
            <span>
              <CalendarDays size={12} />
              {formatDate(
                booking.departure_date
              )}
            </span>

            <span>
              <Plane size={12} />
              PNR {pnr}
            </span>
          </div>

          {booking.booking_reference && (
            <div className="mbt-booking-ref">
              REF&nbsp;
              <strong>
                {
                  booking.booking_reference
                }
              </strong>
            </div>
          )}

          <div
            className="mbt-flight-summary-chips"
            style={{ marginTop: 8 }}
          >
            <span>
              <Users size={13} />
              {passengers.length} Passenger
              {passengers.length !== 1
                ? "s"
                : ""}
            </span>
          </div>

          <div
            style={{
              marginTop: 10,
              fontSize: 12,
              lineHeight: 1.6,
              color: "#475569",
            }}
          >
            <strong>
              Cancellation passengers:
            </strong>{" "}

            {passengers
              .map(
                formatPassengerName
              )
              .join(", ")}
          </div>
        </div>

        <div className="mbt-booking-right">
          <MbtBadge
            status={
              booking
                .cancellationDisplayStatus ||
              "cancel_initiated"
            }
          />
        </div>
      </div>
    </div>
  );
}

function BusBookingCard({ booking, onDownloadReceipt, downloadingId }) {
  const { token } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const isDownloading = downloadingId === booking.id;
  const portalRoot = typeof document !== "undefined"
    ? document.getElementById("modal-root")
    : null;

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    if (menuOpen) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menuOpen]);

  const cancellationRules = normalizeChargeRules(booking.cancellation_charges);
  const canCancel = canCancelBooking(booking);
  const bookingJson = safeJsonParse(booking.booking_json, {});
  const passengers = bookingJson.travellers || [];
  const seatNumbers = String(booking.seat_numbers || "").split(",").map(s => s.trim()).filter(Boolean);

  const openBlobInNewTab = async (endpoint, { print = false } = {}) => {
    const win = window.open("", "_blank");
    if (!win) throw new Error("Pop-up blocked. Please allow pop-ups for this site and try again.");
    try {
      const res = await fetch(`${API_BASE}${endpoint}`, { headers: authHeaders(token) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Could not load the ticket.");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      win.location.href = url;
      if (print) win.addEventListener("load", () => { try { win.print(); } catch (_) { } });
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      win.close();
      throw err;
    }
  };

  const handleView = async () => {
    setActionBusy("view");
    try { await openBlobInNewTab(`/bus-bookings/${booking.id}/eticket`); }
    catch (err) { alert(err.message); }
    finally { setActionBusy(null); }
  };

  const handlePrint = async () => {
    setActionBusy("print");
    try { await openBlobInNewTab(`/bus-bookings/${booking.id}/eticket`, { print: true }); }
    catch (err) { alert(err.message); }
    finally { setActionBusy(null); }
  };

  const handleDownloadInvoice = async () => {
    setActionBusy("invoice");
    try { await downloadFile(`${API_BASE}/bus-bookings/${booking.id}/invoice`, token, `invoice-${booking.booking_reference || booking.id}.pdf`); }
    catch (err) { alert(err.message); }
    finally { setActionBusy(null); }
  };

  const handleEmailTicket = () => {
    const subject = `Bus Ticket - ${booking.booking_reference || "Booking"}`;
    const lines = [
      "Bus Ticket", "",
      `Reference: ${booking.booking_reference || "-"}`,
      `Route: ${booking.from_city || ""} to ${booking.to_city || ""}`,
      `Operator: ${booking.operator || ""} ${booking.bus_type ? `(${booking.bus_type})` : ""}`.trim(),
      `Date: ${formatDate(booking.travel_date)} ${booking.departure_time || ""}`,
      passengers.length ? `Passengers: ${passengers.map(p => p.name || `${p.firstName || ""} ${p.lastName || ""}`.trim()).join(", ")}` : null,
      seatNumbers.length ? `Seats: ${seatNumbers.join(", ")}` : null,
      `Total: ₹${formatPrice(booking.total_price)}`,
    ].filter(Boolean).join("\n");
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines)}`;
  };

  const handleWhatsappTicket = () => {
    const lines = [
      "*Bus Ticket*",
      "",
      `*Reference:* ${booking.booking_reference || "-"}`,
      `*Route:* ${booking.from_city || ""} → ${booking.to_city || ""}`,
      `*Operator:* ${booking.operator || ""}`,
      `*Date:* ${formatDate(booking.travel_date)} ${booking.departure_time || ""}`,
      seatNumbers.length
        ? `*Seats:* ${seatNumbers.join(", ")}`
        : null,
      `*Total:* ₹${formatPrice(booking.total_price)}`,
    ]
      .filter(Boolean)
      .join("\n");

    window.open(
      `https://wa.me/?text=${encodeURIComponent(lines)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className="mbt-booking-card">
      <div className={bookingBarClass("bus")} />
      <div className="mbt-booking-inner" style={{ flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ width: 58, height: 58, borderRadius: 12, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Armchair size={22} />
        </div>

        <div className="mbt-booking-info" style={{ minWidth: 220 }}>
          <div className="mbt-booking-title">{booking.from_city} → {booking.to_city}</div>
          <div className="mbt-booking-meta">
            <span><CalendarDays size={12} />{formatDate(booking.travel_date)} {booking.departure_time || ""}</span>
            <span>{booking.operator} · {booking.bus_type}</span>
          </div>
          {booking.booking_reference && (
            <div className="mbt-booking-ref">REF&nbsp;<strong>{booking.booking_reference}</strong></div>
          )}
          {passengers.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#475569" }}>
              <strong>Passengers:</strong> {passengers.map(p => p.name || `${p.firstName || ""} ${p.lastName || ""}`.trim()).join(", ")}
            </div>
          )}
          {seatNumbers.length > 0 && (
            <div style={{ marginTop: 4, fontSize: 12, color: "#475569" }}>
              <strong>Booked Seat Numbers:</strong> {seatNumbers.join(", ")}
            </div>
          )}

          <div className="mbt-ticket-actions-row">
            <button type="button" className="mbt-ticket-action-btn" disabled={actionBusy === "view"} onClick={handleView}>
              <Eye size={12} />{actionBusy === "view" ? "Opening…" : "View"}
            </button>
            <button type="button" className="mbt-ticket-action-btn" disabled={isDownloading} onClick={() => onDownloadReceipt(booking)}>
              <Download size={12} />{isDownloading ? "Downloading…" : "Download"}
            </button>
            <button type="button" className="mbt-ticket-action-btn" disabled={actionBusy === "print"} onClick={handlePrint}>
              <Printer size={12} />{actionBusy === "print" ? "Preparing…" : "Print"}
            </button>
            <button type="button" className="mbt-ticket-action-btn" onClick={handleEmailTicket}>
              <Mail size={12} />Email
            </button>
            <button type="button" className="mbt-ticket-action-btn mbt-ticket-action-btn--whatsapp" onClick={handleWhatsappTicket}>
              <MessageCircle size={12} />WhatsApp
            </button>
            <button type="button" className="mbt-ticket-action-btn" disabled={actionBusy === "invoice"} onClick={handleDownloadInvoice}>
              <FileText size={12} />{actionBusy === "invoice" ? "Fetching…" : "Invoice"}
            </button>
          </div>
        </div>

        <div className="mbt-booking-right">
          <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
            <MbtBadge status={booking.status} />
            {canCancel && (
              <div className="mbt-menu-wrap" ref={menuRef}>
                <button className="mbt-menu-trigger" onClick={() => setMenuOpen(p => !p)}>⋯</button>
                {menuOpen && (
                  <div className="mbt-context-menu">
                    <button className="mbt-menu-item" style={{ color: "#d97706" }}
                      onClick={() => { setCancelOpen(true); setMenuOpen(false); }}>
                      <X size={13} /> Cancel Booking
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="mbt-booking-price">₹{formatPrice(booking.total_price)}</div>
        </div>
      </div>

      {cancelOpen && portalRoot && createPortal(
        <div className="mbt-reschedule-overlay" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setCancelOpen(false)}>
          <div className="mbt-reschedule-modal">
            <button type="button" className="mbt-reschedule-close" onClick={() => setCancelOpen(false)} aria-label="Close">
              <X size={16} />
            </button>
            <div className="mbt-reschedule-head">
              <AlertTriangle size={18} />
              <div>
                <h3>Cancel bus booking</h3>
                <p>{booking.from_city} to {booking.to_city} · {booking.operator}</p>
              </div>
            </div>
            <div className="mbt-reschedule-note">
              Cancellation charges apply based on operator policy and time remaining before departure.
            </div>
            <div className="mbt-reschedule-actions">
              <button type="button" onClick={() => setCancelOpen(false)}>Go Back</button>
              <button type="button" onClick={() => { setCancelOpen(false); navigate(`/cancel-booking/bus/${booking.id}`); }}>
                Proceed to Cancel
              </button>
            </div>
          </div>
        </div>,
        portalRoot
      )}
    </div>
  );
}

function BusCancellationCard({ booking }) {
  const passengers =
    Array.isArray(booking.passenger_details)
      ? booking.passenger_details
      : safeJsonParse(
        booking.passenger_details,
        []
      );

  const seats = String(
    booking.seat_numbers || ""
  )
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const getPassengerName = (p) =>
    p?.name ||
    [
      p?.title,
      p?.firstName || p?.first_name,
      p?.lastName || p?.last_name,
    ]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim() ||
    "Traveller";

  return (
    <div className="mbt-booking-card">
      <div className={bookingBarClass("bus")} />

      <div
        className="mbt-booking-inner"
        style={{
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            width: 58,
            height: 58,
            borderRadius: 12,
            background: "#eff6ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Armchair size={22} />
        </div>

        <div
          className="mbt-booking-info"
          style={{ minWidth: 220 }}
        >
          <div className="mbt-booking-title">
            {booking.from_city} → {booking.to_city}
          </div>

          <div className="mbt-booking-meta">
            <span>
              <CalendarDays size={12} />
              {formatDate(booking.travel_date)}{" "}
              {booking.departure_time || ""}
            </span>

            <span>
              {booking.operator}
              {booking.bus_type
                ? ` · ${booking.bus_type}`
                : ""}
            </span>
          </div>

          {booking.booking_reference && (
            <div className="mbt-booking-ref">
              REF&nbsp;
              <strong>
                {booking.booking_reference}
              </strong>
            </div>
          )}

          {passengers.length > 0 && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: "#475569",
              }}
            >
              <strong>
                Cancelled Passengers:
              </strong>{" "}
              {passengers
                .map(getPassengerName)
                .join(", ")}
            </div>
          )}

          {seats.length > 0 && (
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: "#475569",
              }}
            >
              <strong>
                Cancelled Seat Numbers:
              </strong>{" "}
              {seats.join(", ")}
            </div>
          )}
        </div>

        <div className="mbt-booking-right">
          <MbtBadge status="cancelled" />

          <div className="mbt-booking-price">
            ₹{formatPrice(
              booking.refund_amount
            )}
          </div>

          <div
            style={{
              marginTop: 2,
              fontSize: 10,
              color: "#6B6558",
              textAlign: "right",
            }}
          >
            Refund
          </div>
        </div>
      </div>
    </div>
  );
}

function HotelBookingCard({ booking, onDownloadReceipt, downloadingId, onPayHold, onReleaseHold, payingId, actionId }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [holdRemaining, setHoldRemaining] = useState(() =>
    formatHoldRemaining(parseHoldExpiryTime(booking))
  );
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const isDownloading = downloadingId === booking.id;
  const isHeld = String(booking.status || "").toLowerCase() === "on_hold";
  const holdExpiryTime = parseHoldExpiryTime(booking);
  const holdExpired = isHeld && Boolean(holdExpiryTime) && holdExpiryTime <= Date.now();
  const isPaying = payingId === booking.id;
  const isActing = actionId === booking.id;
  const canCancel = !isHeld && canCancelBooking(booking);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    if (menuOpen) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menuOpen]);

  useEffect(() => {
    if (!isHeld) return;
    const update = () => setHoldRemaining(formatHoldRemaining(holdExpiryTime));
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [isHeld, holdExpiryTime]);

  const guestName = `${booking.guest_first_name || ""} ${booking.guest_last_name || ""}`.trim() || "Guest";
  const nights = booking.nights || 1;
  const rooms = Number(booking.rooms || 1);
  const adults = Number(booking.adults || 1);
  const children = Number(booking.children || 0);
  const childAges = parseHotelJson(booking.occupancies).flatMap(room =>
    Array.isArray(room.childAges)
      ? room.childAges
      : []
  );

  return (
    <div className="mbt-booking-card">
      <div className={bookingBarClass("hotel")} />
      <div className="mbt-booking-inner" style={{ flexWrap: "wrap", alignItems: "flex-start" }}>
        {booking.hotel_image
          ? <img src={booking.hotel_image} alt={booking.hotel_name} className="mbt-booking-thumb" />
          : (
            <div style={{ width: 58, height: 58, borderRadius: 12, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Luggage size={22} />
            </div>
          )}

        <div className="mbt-booking-info" style={{ minWidth: 220 }}>
          <div className="mbt-booking-title">{booking.hotel_name || "Hotel"}</div>
          <div className="mbt-booking-meta">
            <span><CalendarDays size={12} />{formatDate(booking.check_in)} → {formatDate(booking.check_out)}</span>
            <span>
              <Users size={12} />

              {rooms} Room{rooms !== 1 ? "s" : ""} - {adults} Adult{adults !== 1 ? "s" : ""}

              {children > 0 && (
                <>
                  , {children} Child{children !== 1 ? "ren" : ""}
                </>
              )}

              {" · "}
              {nights} Night{nights !== 1 ? "s" : ""}
            </span>
            {booking.hotel_location && <span><MapPinned size={12} />{booking.hotel_location}</span>}
          </div>
          {booking.booking_reference && (
            <div className="mbt-booking-ref">REF&nbsp;<strong>{booking.booking_reference}</strong></div>
          )}

          <div className="mbt-ticket-actions-row">
            {isHeld ? (
              <>
                <button
                  type="button"
                  className="mbt-ticket-action-btn"
                  disabled={holdExpired || isPaying}
                  onClick={() => onPayHold?.(booking)}
                >
                  <CreditCard size={12} />{isPaying ? "Processing..." : "Continue Payment"}
                </button>
                <button
                  type="button"
                  className="mbt-ticket-action-btn"
                  disabled={isActing}
                  onClick={() => onReleaseHold?.(booking)}
                >
                  <X size={12} />{isActing ? "Releasing..." : "Release Hold"}
                </button>
              </>
            ) : (
              <>
                <button type="button" className="mbt-ticket-action-btn" onClick={() => setDetailsOpen(true)}>
                  <Eye size={12} /> View Details
                </button>
                <button
                  type="button"
                  className="mbt-ticket-action-btn"
                  disabled={isDownloading}
                  onClick={() => onDownloadReceipt(booking)}
                >
                  <Download size={12} />{isDownloading ? "Downloading…" : "Download Receipt"}
                </button>
              </>
            )}
          </div>
          {isHeld && (
            <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: holdExpired ? "#b91c1c" : "#b45309" }}>
              {holdRemaining || "Hold expiry unavailable"}
            </div>
          )}
        </div>

        <div className="mbt-booking-right">
          <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
            <MbtBadge status={booking.status} />
            {canCancel && (
              <div className="mbt-menu-wrap" ref={menuRef}>
                <button className="mbt-menu-trigger" onClick={() => setMenuOpen(p => !p)}>⋯</button>
                {menuOpen && (
                  <div className="mbt-context-menu">
                    <button
                      className="mbt-menu-item"
                      style={{ color: "#d97706" }}
                      onClick={() => { setMenuOpen(false); navigate(`/cancel-booking/hotel/${booking.id}`); }}
                    >
                      <X size={13} /> Cancel Booking
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="mbt-booking-price">₹{formatPrice(booking.grand_total)}</div>
        </div>
      </div>

      {detailsOpen && (
        <HotelDetailsModal booking={booking} guestName={guestName} childAges={childAges} onClose={() => setDetailsOpen(false)} />
      )}
    </div>
  );
}
function HotelDetailsModal({ booking, guestName, childAges = [], onClose }) {
  const nights = booking.nights || 1;
  const pricingSnapshot = safeJsonParse(
  booking.pricing_snapshot ||
    booking.pricingSnapshot ||
    booking.pricing,
  {}
);

const pricingBreakdown = Array.isArray(pricingSnapshot?.breakdown)
  ? pricingSnapshot.breakdown
  : Array.isArray(pricingSnapshot?.adminPricing?.breakdown)
    ? pricingSnapshot.adminPricing.breakdown
    : Array.isArray(booking.adminPricing?.breakdown)
      ? booking.adminPricing.breakdown
      : Array.isArray(booking.admin_pricing?.breakdown)
        ? booking.admin_pricing.breakdown
        : [];

const calculatedFees = pricingBreakdown
  .filter((item) => item.isTax !== true)
  .reduce((sum, item) => sum + Number(item.amount || 0), 0);

const calculatedTaxes = pricingBreakdown
  .filter((item) => item.isTax === true)
  .reduce((sum, item) => sum + Number(item.amount || 0), 0);

const roomTotalAmount = Number(
  pricingSnapshot?.supplier?.roomTotal ??
    booking.room_total ??
    0
);

const grandTotalAmount = Number(
  pricingSnapshot?.payableTotal ??
    booking.grand_total ??
    0
);

const storedFeeAmount = Number(
  pricingSnapshot?.feeTotal ??
    pricingSnapshot?.adminPricing?.feeTotal ??
    booking.adminPricing?.feeTotal ??
    booking.admin_pricing?.feeTotal ??
    booking.fee_total ??
    calculatedFees ??
    0
);

const taxAmount = Number(
  pricingSnapshot?.taxTotal ??
    pricingSnapshot?.adminPricing?.taxTotal ??
    booking.adminPricing?.taxTotal ??
    booking.admin_pricing?.taxTotal ??
    booking.admin_tax_total ??
    booking.tax_total ??
    calculatedTaxes
);

const feeAmount =
  storedFeeAmount > 0
    ? storedFeeAmount
    : Math.max(
        0,
        Number(
          (
            grandTotalAmount -
            roomTotalAmount -
            taxAmount
          ).toFixed(2)
        )
      );

const hotelStars = Number(
  booking.hotel_stars ??
    booking.stars ??
    booking.star_rating ??
    booking.hotelStars ??
    0
);
  const stayType = booking.travel_type === null || booking.travel_type === undefined
    ? ""
    : Number(booking.travel_type) === 0
      ? "Domestic"
      : "International";

    const specialRequests = parseHotelJson(booking.special_requests);

  const gstRequest = specialRequests.find(
    (request) =>
      request?.category === "gst_details" ||
      request?.code === "customer_gst"
  );

  const gstDetails = gstRequest?.gstDetails;

  const normalSpecialRequests = specialRequests.filter(
    (request) =>
      request?.category !== "gst_details" &&
      request?.code !== "customer_gst"
  );    

  return createPortal(
    <div
      className="mbt-reschedule-overlay"
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="mbt-reschedule-modal" style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <button type="button" className="mbt-reschedule-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>

        <div className="mbt-reschedule-head">
          <Luggage size={18} />
          <div>
            <h3>{booking.hotel_name || "Hotel Booking"}</h3>
            <p>{booking.hotel_location || "—"}</p>
          </div>
        </div>

        {/* Stay details */}
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#0f766e", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
            Stay Details
          </div>
          <div className="mbt-hotel-details-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px", fontSize: 13 }}>
            <div><span style={{ color: "#64748b" }}>Check-in:</span> <strong>{formatDate(booking.check_in)}</strong></div>
            <div><span style={{ color: "#64748b" }}>Check-out:</span> <strong>{formatDate(booking.check_out)}</strong></div>
            <div><span style={{ color: "#64748b" }}>Nights:</span> <strong>{nights}</strong></div>
            <div><span style={{ color: "#64748b" }}>Rooms:</span> <strong>{booking.rooms || 1}</strong></div>
            <div><span style={{ color: "#64748b" }}>Adults:</span> <strong>{booking.adults || 1}</strong></div>
            <div><span style={{ color: "#64748b" }}>Children:</span> <strong>{booking.children || 0}</strong></div>
            {childAges.length > 0 && (
              <div><span style={{ color: "#64748b" }}>Child Ages:</span> <strong>{childAges.join(", ")}</strong></div>
            )}
            <div><span style={{ color: "#64748b" }}>Room Type:</span> <strong>{booking.room_type || "—"}</strong></div>
            {stayType && (
              <div><span style={{ color: "#64748b" }}>Stay Type:</span> <strong>{stayType}</strong></div>
            )}
            {booking.booking_reference && (
              <div><span style={{ color: "#64748b" }}>Booking Ref:</span> <strong>{booking.booking_reference}</strong></div>
            )}
            {booking.confirmation_number && (
              <div><span style={{ color: "#64748b" }}>Confirmation No:</span> <strong>{booking.confirmation_number}</strong></div>
            )}
            <div><span style={{ color: "#64748b" }}>Status:</span> <strong>{booking.status || "â€”"}</strong></div>
            {hotelStars > 0 && (
  <div>
    <span style={{ color: "#64748b" }}>Star Rating:</span>{" "}
    <strong>{hotelStars} ★</strong>
  </div>
)}
          </div>
        </div>

        {/* Guest details */}
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#0f766e", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
            Guest Details
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.8 }}>
            <div><span style={{ color: "#64748b" }}>Name:</span> <strong>{guestName}</strong></div>
            <div><span style={{ color: "#64748b" }}>Email:</span> <strong>{booking.guest_email || "—"}</strong></div>
            <div><span style={{ color: "#64748b" }}>Phone:</span> <strong>{booking.guest_phone || "—"}</strong></div>
            {booking.guest_address && (
              <div><span style={{ color: "#64748b" }}>Address:</span> <strong>{booking.guest_address}</strong></div>
            )}
          </div>
        </div>

                            {/* GST Details */}
          {gstDetails?.enabled && (
            <div style={{ marginTop: 18 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#0f766e",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 8,
                }}
              >
                GST Details
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "stretch",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  background: "#f8fafc",
                  overflow: "hidden",
                }}
              >
                <div style={{ flex: 1, padding: "10px 12px" }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#64748b",
                      marginBottom: 3,
                    }}
                  >
                    GSTIN
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    {gstDetails.gstin || "—"}
                  </div>
                </div>

                <div
                  style={{
                    width: 1,
                    background: "#e2e8f0",
                  }}
                />

                <div style={{ flex: 1, padding: "10px 12px" }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#64748b",
                      marginBottom: 3,
                    }}
                  >
                    Customer / Company
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    {gstDetails.companyName || "—"}
                  </div>
                </div>

                <div
                  style={{
                    width: 1,
                    background: "#e2e8f0",
                  }}
                />

                <div style={{ flex: 1.5, padding: "10px 12px" }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#64748b",
                      marginBottom: 3,
                    }}
                  >
                    Billing Address
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    {gstDetails.address || "—"}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Special Requests */}
          {normalSpecialRequests.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#0f766e", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                Special Requests
              </div>

              <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                {normalSpecialRequests.map((request, index) => (
                  <div key={index}>
                    <strong>
                      {request?.label || request?.request || String(request)}
                    </strong>
                  </div>
                ))}
              </div>
            </div>
          )}


        {/* Payment breakdown */}
        {/* Payment breakdown */}
<div style={{ marginTop: 18 }}>
  <div
    style={{
      fontSize: 11,
      fontWeight: 700,
      color: "#0f766e",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      marginBottom: 8,
    }}
  >
    Payment Summary
  </div>

  <div style={{ fontSize: 13, lineHeight: 1.9 }}>
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span style={{ color: "#64748b" }}>Room Total</span>
      <strong>
        ₹{formatMoneyAmount(
          pricingSnapshot?.supplier?.roomTotal ?? booking.room_total
        )}
      </strong>
    </div>

    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span style={{ color: "#64748b" }}>Fees</span>
      <strong>₹{formatMoneyAmount(feeAmount)}</strong>
    </div>

    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span style={{ color: "#64748b" }}>Taxes</span>
      <strong>₹{formatMoneyAmount(taxAmount)}</strong>
    </div>

    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        borderTop: "1px solid #e2e8f0",
        paddingTop: 6,
        marginTop: 4,
      }}
    >
      <span style={{ fontWeight: 700 }}>Grand Total</span>
      <strong>
        ₹{formatMoneyAmount(
          pricingSnapshot?.payableTotal ?? booking.grand_total
        )}
      </strong>
    </div>

    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: 4,
      }}
    >
      <span style={{ color: "#64748b" }}>Payment Method</span>
      <strong style={{ textTransform: "capitalize" }}>
        {booking.payment_method || "—"}
      </strong>
    </div>
  </div>
</div>

        {/* Terms & Conditions */}
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#0f766e", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
            Terms &amp; Conditions
          </div>
          <ul style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.8, paddingLeft: 18, margin: 0 }}>
            <li>Check-in time is generally from 12:00 PM / 2:00 PM and check-out by 11:00 AM, subject to hotel policy.</li>
            <li>A valid government-issued photo ID is required for all guests at check-in.</li>
            <li>Cancellation and rescheduling charges apply as per the hotel's fare rules at the time of booking.</li>
            <li>Early check-in or late check-out may incur additional charges and is subject to availability.</li>
            <li>Room rates are inclusive of applicable taxes unless stated otherwise.</li>
            <li>The hotel reserves the right to deny check-in for incomplete or invalid identification.</li>
            <li>Any damages to hotel property will be charged separately by the hotel directly to the guest.</li>
            <li>For cancellations, please use "Cancel Booking" from this page — refund amount is subject to the hotel's cancellation policy at time of cancellation.</li>
          </ul>
        </div>

        <div className="mbt-reschedule-actions" style={{ marginTop: 20 }}>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}


